import MaturixStrengthUnit from '../../enums/MaturixStrengthUnit'
import MaturixTemperatureUnit from '../../enums/MaturixTemperatureUnit'
import { TagType } from '../../enums/TagType'
import { getBandTitleByTagType } from '../../utils/TagHelper'
import { brandName, brandShortName } from '../brand'
import countries from './countries'
import timezones from './timezones'

const ending = (x: number, single: string = '', plural: string = 's') =>
  x === 1 ? single : plural

const deliveries = (x: number) => `Deliver${ending(x, 'y', 'ies')}`
const plans = (x: number) => `Plan${ending(x, '', 's')}`
const workflows = (x: number) => `Workflow${ending(x, '', 's')}`
const users = (x: number) => `User${ending(x, '', 's')}`
const activities = (x: number) => `Activit${ending(x, 'y', 'ies')}`
const sensors = (x: number) => `Sensor${ending(x, '', 's')}`
const struxTagsCount = (x: number) => `StruxTag${ending(x, '', 's')}`
const equipment = (x: number) => `equipment item${ending(x)}`
const materials = (x: number) => `material${ending(x)}`

const xHours = (x: number) => `${x} hour${ending(x)}`
const xUsers = (x: number) => `${x} user${ending(x)}`
const xFiles = (x: number) => `${x} file${ending(x)}`
const xDates = (x: number) => `${x} Date${ending(x)}`
const xForms = (x: number) => `${x} Form${ending(x)}`
const xFields = (x: number) => `${x} field${ending(x)}`
const xFieldsAdded = (x: number) => `${x} Field${ending(x)} added`
const xProjects = (x: number) => `${x} project${ending(x)}`
const xProperties = (x: number) => `${x} Propert${ending(x, 'y', 'ies')}`

const EnglishTree = {
  _brandName: brandName,
  _brandShortName: brandShortName,

  label: 'Label',
  avatar: 'Avatar',
  ok: 'OK',
  alert: 'Alert',
  warning: 'Warning',
  cancel: 'Cancel',
  bulkAddOptions: 'Bulk add options',
  dropdownOptions: 'Dropdown Options',
  optionLabel: 'Option Label',
  selectAllOptions: 'Select all options',
  hierarchyOptionsIntro:
    'Create a list of options that can be selected by users. Create hierarchical lists by nesting options under parents.',
  cantChangeNestingChecking:
    "Can't change nesting yet — still checking which options are used by records. Try again in a moment.",
  optionUsedCannotNest:
    "This option is used by existing records and can't be turned into a parent. Only options with no records can have nested options.",
  cantRestoreExceedsDepth:
    "Can't restore — nesting these options together would exceed the maximum depth.",
  restoredDeletedOptions: (labels: string, count: number) =>
    `Restored previously deleted ${
      count === 1 ? 'option' : 'options'
    }: ${labels}`,
  savesAs: 'Saves as',
  alreadyUsedBy: (label: string) => `Already used by ‘${label}’`,
  useValue: (value: string) => `Use ${value}`,
  matchesDeletedOptionWillRestore: (label: string) =>
    `Matches deleted option ‘${label}’ — will restore it`,
  restoreAndApply: 'Restore & apply',
  collapseOption: 'Collapse option',
  expandOption: 'Expand option',
  pickedUpOption: (label: string) => `Picked up option ${label}.`,
  optionIsOver: (active: string, over: string) =>
    `Option ${active} is over ${over}.`,
  optionDroppedOver: (active: string, over: string) =>
    `Option ${active} was dropped over ${over}.`,
  optionDropped: (label: string) => `Option ${label} was dropped.`,
  reorderingOptionCancelled: (label: string) =>
    `Reordering option ${label} was cancelled.`,
  optionsSelected: (count: number) =>
    `${count} ${count === 1 ? 'option' : 'options'} selected`,
  indent: 'Indent',
  outdent: 'Outdent',
  reorderOption: (label: string) => `Reorder ${label}`,
  selectOption: (label: string) => `Select ${label}`,
  bulkAddOptionEnterEachLine: 'Enter each option on its own line.',
  bulkAddOptionIndentToNest: 'Indent option to nest it under a parent.',
  bulkAddOptionCopyFromExcel: 'Copy and paste entire datasets from Excel',
  seeTips: 'see tips',
  addOptions: 'Add options',
  copyingAndPastingFromExcel: 'Copying and pasting data from Excel',
  bulkImportGuideSubtitle:
    'Three ways to format options when pasting from a spreadsheet.',
  flatDatasets: 'Flat datasets',
  flatDatasetsHint:
    'Place each option on a different row before copying and pasting.',
  hierarchicalDatasetMethod1: 'Hierarchical dataset (Method 1)',
  hierarchicalDatasetMethod1Hint:
    'Place each level in a different column and each option on a different row before copying.',
  hierarchicalDatasetMethod2: 'Hierarchical dataset (Method 2)',
  hierarchicalDatasetMethod2Hint:
    'Same as Method 1, but parents can optionally be duplicated on each row.',
  back: 'Back',
  addOptionBelow: (label: string) => `Add option below ${label}`,
  optionCount: (count: number) =>
    `${count} ${count === 1 ? 'option' : 'options'}`,
  optionMatchCount: (matched: number, total: number) =>
    `${matched} of ${total}`,
  tipXOfY: (current: number, total: number) => `Tip ${current} of ${total}`,
  placeHere: 'Place here',
  failedToSavePlacement: 'Could not save placement. Please try again.',
  placedAtMapCenterDragToAdjust: 'Placed at map center – drag to adjust',
  notPlacedOnMap: 'Not placed on the map',
  placeOnMap: 'Place on map',
  placedOnLocationDragToAdjust: (name: string) =>
    `Placed on ${name} – drag to adjust`,
  placingMultipleItems: (count: number) =>
    `Placing ${count} items – drag each to adjust, then confirm`,
  addedToPlacementQueue: (remaining: number) =>
    `Added to placement queue – ${remaining} remaining`,
  open: 'Open',
  openWorkflowForm: 'Open Workflow',
  minimize: 'Minimize',
  opened: 'Opened',
  close: 'Close',
  closed: 'Closed',
  contacts: 'Contacts',
  loading: 'Loading',
  language: 'Language',
  notSpecified: 'Not specified',
  default: 'Default',
  done: 'Done',
  save: 'Save',
  apply: 'Apply',
  edit_verb: 'Edit',
  merge_verb: 'Merge',
  link_verb: 'Link',
  re_link_verb: 'Re-link',
  unlink_verb: 'Unlink',
  unlinking: 'Unlinking',
  linkToX: (x: string) => `Link to ${x}`,
  linkedToX: (x: string) => `Linked to ${x}`,
  savingEllipsis: 'Saving...',
  details: 'Details',
  delete: 'Delete',
  rename: 'Rename',
  reorder: 'Reorder',
  renameMapView: 'Rename map view',
  mapViewName: 'Map view name',
  deletedField: 'Deleted field',
  duplicate: 'Duplicate',
  duplicatedName: (name: string) => `${name} - Copy`,
  initials: 'Initials',
  invite: 'Invite',
  search: 'Search',
  selectAll_people: 'Select All',
  selectAll_items: 'Select All',
  selectNone_people: 'Select None',
  selectNone_items: 'Select None',
  clearAll_items: 'Clear All',
  clear: 'Clear',
  uploading: 'Uploading',
  noResults: 'No results',
  add: 'Add',
  update_verb: 'Update',
  updating: 'Updating',
  submitting: 'Submitting',
  error: 'Error',
  goBack: 'Go back',
  confirm: 'Confirm',
  select: 'Select',
  selectAll: 'Select all',
  selected: 'Selected',
  start_verb: 'Start',
  finish_verb: 'Finish',
  followers: 'Followers',
  view_verb: 'View',
  download: 'Download',
  export: 'Export',
  downloadingStatus: {
    starting: 'Starting',
    fetching: 'Fetching',
    compiling: 'Compiling',
    generating: 'Generating',
    downloading: 'Downloading',
  },
  generatingLink: 'Generating link...',
  searchCompany: 'Search company',
  thereIsNoCompanies: 'There is no companies',
  addCompany: 'Add Company',
  locationClosure: 'Location closure',
  ifDeliveryHasTheFollowingTags: 'If delivery has the following tags',
  ifEntityHasTheFollowingTags: (entityName: string) =>
    `If ${entityName} has the following tags`,
  thenDoAction: 'Then do action',
  toUserTeamRoleOrCompany: 'To user, team, role or company',
  addRule: 'Add rule',
  tag: 'Tag',
  shouldDeleteRule: 'Should delete rule',
  searchForOrSelectTag: 'Search for or select tag',
  addTag: 'Add tag',
  addUserTeamCompany: 'Add User, Team, Company',
  tagCreation: 'Tag Creation',
  deliveryStatus: 'Delivery status',
  sendNotification: 'Send notification',
  toExternalCalendar: 'To External Calendar',
  allFormsSharingCalendarEnabled: 'Enabled all Workflow sharing to calendar',
  allDeliveriesSharingCalendarEnabled:
    'Enabled all deliveries sharing to calendar',
  allFormsSharingNotificationEnabled:
    'Enabled all Workflow sharing to notifications',
  allDeliveriesSharingNotificationEnabled:
    'Enabled all deliveries sharing to notifications',
  sharingAllFormsToCalendarAndNotificationsEnabled:
    'Enabled sharing all Workflows to calendar and notifications',
  sharingAllDeliveriesToCalendarAndNotificationsEnabled:
    'Enabled sharing all deliveries to calendar and notifications',
  fromRules: 'From rules',
  calendarLinkInputText:
    'You can provide this link to 3rd party calendar apps as a Google Calendar, TeamUp, Outlook etc',
  enableIcal: 'Enable iCalendar ',
  generate: 'Generate',
  copy: 'Copy',
  notificationRules: 'Notification rules',
  mention: 'Mention',
  gotToDirectory: 'Go to directory',
  goToProjectHome: 'Go to Project Home',

  daily: 'Daily',
  monSat: 'Mon-Sat',
  d_daysShort: 'd',
  xSecondsAgoShort: (x: number) => `${x}s ago`,
  xMinutesAgoShort: (x: number) => `${x} min ago`,
  xHoursAgoShort: (x: number) => `${x} h ago`,
  xDaysAgoShort: (x: number) => `${x} d ago`,
  yesterday: 'Yesterday',
  today: 'Today',
  tomorrow: 'Tomorrow',

  loggingIn: 'Logging in',
  loggingOut: 'Logging out',
  loginFailed: 'Login failed',
  backToLogin: 'Back to Login',
  processingLogin: 'Processing login...',
  login: 'Login',
  logo: 'Logo',
  procoreLogin: 'Sign in with Procore',
  loginWithMicrosoft: 'Sign in with Microsoft',
  loginWithGoogle: 'Sign in with Google',
  tryAgain: 'Try Again',
  contactAdminForAccess:
    'If you believe you should have access, please contact your administrator.',
  ssoSessionExpired: 'Your login session has expired. Please try again.',
  ssoProviderError:
    'Could not complete authentication with the provider. Please try again.',
  ssoInvalidUser:
    'Could not retrieve your account information. Please try again.',
  ssoUserNotFound: 'This account is not added to StruxHub.',
  ssoCodeExpired: 'Your login link has expired. Please try logging in again.',
  ssoInternalError: 'An unexpected error occurred. Please try again later.',
  ssoProviderNotAllowed:
    'This login method is not available for your organization.',
  ssoUseAllowedMethod:
    'Your organization requires one of the following login methods:',
  ssoPersonalAccountsNotAllowed:
    'Personal accounts cannot be used to sign in. Please use your work account.',
  ssoInitiationFailed:
    'Could not start sign-in with this provider. Please try again, or use another sign-in method.',
  ssoDomainRequiresMethods: (domain: string) =>
    `Users with @${domain} must use one of the following login methods:`,
  passwordLoginNotAvailableForDomain: (providers: string) =>
    `Email & password login is not available for your domain. Please use: ${providers}`,
  emailAndPassword: 'Email & Password',
  noColumnsDefined: 'No columns defined',
  changePassword: 'Change Password',
  setPassword: 'Set Password',
  passwordWillBeUsedForAllSites:
    'This password will be used for all StruxHub sites',
  setPasswordToSignBackIn:
    'Your invite link is temporary. A password lets you sign back in. You can also set it later in your profile.',
  forgotPassword: 'Forgot password?',
  reset: 'Reset',
  resetPassword: 'Reset Password',
  logOut: 'Log Out',

  hideAll: 'Hide all',
  hideGroup: 'Hide group',
  hideObject: 'Hide object',

  showAll: 'Show all',
  showGroup: 'Show group',
  showObject: 'Show object',

  contactHelpAtMail: (email: string) => 'Contact help at ' + email,
  contactHelp: 'Contact Help',

  userNotFound: 'User not found',
  noUserMatchingYourSearch: 'No user matching your search',
  allUsersAlreadyAssigned: 'All users already assigned',
  noTagMatchingYourSearch: 'No tag matching your search',

  enterYourEmailBelowAndStruxHubWillSendAResetLink: `Enter your email below and ${brandName} will send a reset link`,
  pleaseEnterYourPasswordForTheExistingAccountToLinkDifferentProviders:
    'Please enter your password for the existing account to link different providers:',
  pleaseOpenTheLinkFromTheLetterAgain:
    'Please open the link from the letter again',
  passwordResetLinkWillBeSentIfAccountExists:
    'If an account exists for this email, you will receive a password reset link',
  checkYourEmail: 'Check your email',
  passwordResetLinkSentIfAccountExists: (email: string) =>
    `If an account exists for ${email}, we've sent a link to reset your password. It may take a few minutes.`,
  didNotGetItTryAnotherEmail: "Didn't get it? Try another email",
  passwordResetLinkInvalid:
    'This password reset link is invalid or has expired. Request a new one.',
  invalidEmailError: 'The email address is badly formatted.',
  userNotFoundError:
    'There is no user record corresponding to this identifier. The user may have been deleted.',
  enterANewPasswordBelow: 'Enter a new password below',
  setYourPassword: 'Set your password',
  tooManyUnsuccessfulLoginAttempts: 'Too many unsuccessful login attempts.',
  incorrectEmailOrPassword:
    'Incorrect email or password. Use "Forgot password?" to reset it.',
  passwordErrors: {
    weakPassword:
      'Passwords must be at least 8 characters, Upper and lower case' +
      ', a number, and a special character (!@#$%&)',
    doNotMatch: 'Passwords do not match',
    empty: 'Password cannot be empty',
    invalid: 'The password is invalid or the user does not have a password',
  },
  authFieldErrors: {
    enterYourEmail: 'Enter your email address',
    enterValidEmail: 'Enter a valid email address',
    enterYourPassword: 'Enter your password',
    enterNewPassword: 'Enter a new password',
    enterNewPasswordAgain: 'Re-enter your password',
    enterYourPhoneNumber: 'Enter your phone number',
    enterValidPhoneNumber: 'Enter a valid phone number',
    enterYourFirstName: 'Enter your first name',
    enterYourLastName: 'Enter your last name',
    enterAtLeastXCharacters: (x: number) => `Enter at least ${x} characters`,
  },

  chat: 'Chat',
  activities: 'Activities',
  deliveries: 'Deliveries',
  closures: 'Closures',
  noActiveClosures: 'No active closures',
  activeClosuresHeading: 'Active closures:',
  logistics: 'Logistics',
  logisticObject: 'Logistic Object',
  site: 'Site',
  siteNews: 'Site news',
  home: 'Home',
  hub: 'Hub',
  siteLogistics: 'Site Logistics',
  materials: 'Materials',
  materialsLegacy: 'Materials (legacy)',
  photos: 'Photos',
  analytics: 'Analytics',
  analyticsSetting: 'Analytics setting',
  showInReports: 'Show in Reports',
  dashboards: 'Dashboards',
  widgetDescriptions: {
    workflowsCount:
      'Shows the total number of active workflows in the selected time period. This includes all workflows regardless of their status.',
    companiesCount:
      'Displays the count of unique companies involved in workflows during the selected period. Each company is counted once regardless of how many workflows they participate in.',
    usersCount:
      'Shows the number of unique users associated with workflows in the selected time range.',
    averageDuration:
      'Calculates the average number of days from workflow start date to completion date. Only includes workflows that have both start and end dates.',
    advancedPlanning:
      'Measures the average number of days between when a workflow was requested and when it actually started. Higher values indicate better advance planning.',
    equipmentBookingsWorkflows:
      'Counts how many workflows have equipment bookings attached to them during the selected period.',
    equipmentBookingsHours:
      'Shows the total hours of equipment bookings across all workflows in the selected time range.',
    locationsWorkflows:
      'Shows the distribution of workflows across different locations. Helps identify which locations have the most activity.',
    locationsByType:
      'Displays the breakdown of workflows by location type. Shows the percentage distribution across different workflow types.',
    workflowsByStatus:
      'Visualizes the distribution of workflows across different statuses (Requested, Submitted, Active, Changed, Ended). Helps understand the workflow pipeline.',
    activeVsCompleted:
      'Compares in-progress workflows (Requested, Submitted, Changed, Accepted, Reviewed, Active, OnSite, ToInspect) versus closed workflows (Done, Ended, Passed, Failed, Denied) in the selected period. Draft and deleted workflows are excluded.',
    workflowsByCategory:
      'Shows how workflows are distributed across different categories. Helps identify which workflow categories are most common.',
    workflowsTimeline:
      'Shows the number of workflows started over time. Useful for identifying trends and patterns in workflow creation.',
    recentWorkflows:
      'Table listing the most recent workflows with their status and dates. Provides quick access to the latest workflow activity.',
  },
  dashboard: {
    settings: 'Dashboard Settings',
    settingsDescription:
      'Toggle widgets on or off. Widgets are organized by category for better dashboard management.',
    resetLayout: 'Reset Layout',
    widgetInfo: 'Widget information',
    backToWidget: 'Back to widget',
    downloadChart: 'Download chart',
    downloadAsPng: 'Download as PNG',
    downloadAsJpeg: 'Download as JPEG',
    noChange: 'No change',
    chartType: 'Chart type',
    chartTypes: {
      pie: 'Pie chart',
      bar: 'Bar chart',
      donut: 'Donut chart',
      line: 'Line chart',
    },
    chartTypeNotSupported: 'Chart type not supported',
    noWorkflowsFound: 'No workflows found for this period',
    workflowsCreated: 'Workflows Created',
    retry: 'Retry',
    refresh: 'Refresh',
    lastUpdated: 'Last updated',
    justNow: 'just now',
    minutesAgo: (m: number) => `${m}m ago`,
    hoursAgo: (h: number) => `${h}h ago`,
    widgetTitles: {
      totalWorkflows: 'TOTAL WORKFLOWS',
      companies: 'COMPANIES',
      usersEditors: 'USERS',
      averageDuration: 'AVERAGE DURATION',
      advancedPlanning: 'ADVANCED PLANNING',
      workflowsTimeline: 'WORKFLOWS TIMELINE',
      workflowsByStatus: 'WORKFLOWS BY STATUS',
      activeVsCompleted: 'ACTIVE VS COMPLETED',
      workflowsByCategory: 'WORKFLOWS BY CATEGORY',
      recentWorkflows: 'RECENT WORKFLOWS',
      locationsWorkflows: 'LOCATIONS (WORKFLOWS)',
      locationsByType: 'LOCATIONS (BY TYPE)',
      equipmentBookings: 'EQUIPMENT BOOKINGS',
      equipmentHours: 'EQUIPMENT HOURS',
    },
    widgetLabels: {
      activeWorkflowsInPeriod: 'Workflows in selected period',
      uniqueCompaniesInvolved: 'Unique companies involved',
      uniqueUsersEditing: 'Unique users in workflows',
      daysFromStartToCompletion: 'Days from start to completion',
      timeRequestedBeforeStart: 'Time requested before start date',
      workflowsWithEquipment: 'Workflows with equipment',
      totalEquipmentBookingHours: 'Total equipment booking hours',
    },
    sections: {
      keyMetrics: {
        title: 'Key Metrics',
        description: 'Primary performance indicators',
      },
      planningEfficiency: {
        title: 'Planning Efficiency',
        description: 'Work planning metrics',
      },
      workflowStatus: {
        title: 'Workflow Status',
        description: 'Current state and progress',
      },
      workflowAnalytics: {
        title: 'Workflow Analytics',
        description: 'Distribution and trends',
      },
      recentActivity: {
        title: 'Recent Activity',
        description: 'Latest workflow updates',
      },
      locationAnalytics: {
        title: 'Location Analytics',
        description: 'Where work is happening',
      },
      equipmentResources: {
        title: 'Equipment & Resources',
        description: 'Resource utilization',
      },
    },
  },
  reports: 'Reports',
  filedToGenerateReport: 'Failed to generate report',
  objectProperties: 'Object properties',
  formProperties: 'Form Properties',
  createNewForm: 'Create New Form',
  formNameLabel: 'Form Name',
  untitled: 'Untitled',
  newFormButton: 'New Form',
  selectEquipmentForm: 'Select Equipment Form',
  selectMaterialForm: 'Select Material Form',
  selectLocationForm: 'Select Location Form',
  noFormsAvailable: 'No forms available',
  askAdminToCreateForm:
    'Please ask an administrator to create a form before adding records.',
  propertiesPlural: 'properties',
  createNewEquipment: 'Create New Equipment',
  createNewMaterial: 'Create New Material',
  createNewLocation: 'Create New Location',
  createNewStruxTag: 'Create New StruxTag',
  equipmentForm: 'Equipment Form',
  materialForm: 'Material Form',
  equipmentCreationForm: 'Equipment Creation Form',
  materialCreationForm: 'Material Creation Form',
  anyForm: 'Any Form',
  equipmentPropertiesLabel: 'Equipment Properties',
  materialPropertiesLabel: 'Material Properties',
  notifications: 'Notifications',
  userSettings: 'User Settings',
  projectSettings: 'Project Settings',
  projectSettingsOnDesktop: 'Project Settings on Desktop version',
  adminSettings: 'Admin Settings',
  settings: 'Settings',
  pinSettingsSidebar: 'Pin sidebar to show on all pages',
  unpinSettingsSidebar: 'Unpin sidebar',
  messages: 'Messages',
  themeDark: 'Dark Theme',
  themeLight: 'Light Theme',
  signIn: 'Sign In',
  signInWithSms: 'Sign In with SMS',
  signUp: 'Sign Up',
  signedInAs: 'Signed in as',
  account: 'Account',
  profile: 'Profile',
  editor: 'Editor',
  editProfile: 'Edit profile',
  myPortal: 'My portal',
  portalSidebar: {
    projects: 'Projects',
    navigation: 'Portal navigation',
    accountMenu: 'Account menu',
    editProfile: 'Edit Profile',
    openNavigation: 'Open navigation',
    closeNavigation: 'Close navigation',
    expandSidebar: 'Expand sidebar',
    collapseSidebar: 'Collapse sidebar',
    signOut: 'Sign Out',
    reportABug: 'Report a bug',
  },
  portalProjects: {
    view: 'Project view',
    gridView: 'Grid view',
    mapView: 'Map view',
    welcomeBack: (name: string) => `Welcome back, ${name} 👋`,
    chooseProject: 'Choose a Project to get started',
    openingProject: (name: string) => `Opening ${name}…`,
    couldNotOpenProject: 'Could not open project',
    loadFailedFor: (tenants: string) =>
      `Couldn't load projects for ${tenants}.`,
    someProjectsMissing: 'Some of your projects may be missing from this list.',
    loadingProblem: 'This is a loading problem, not an empty account.',
    loadingProjects: 'Loading Projects…',
    noProjectsMatchSearch: 'No Projects match your search.',
    noProjectsMatchFilters: 'No Projects match your filters.',
    noProjectsLoaded: 'No Projects could be loaded.',
    noProjectsYet: 'You do not have any Projects yet.',
    loadingMap: 'Loading Map…',
    mapUnavailable: 'Map unavailable — no Mapbox configuration on this host.',
    mapDescription:
      "Each pin sits at the Project's real location and carries its logo — tap to open.",
    lastOpened: (when: string) => `Last opened ${when}`,
    daysAgo: (n: number) => `${n} days ago`,
    oneWeekAgo: '1 week ago',
    weeksAgo: (n: number) => `${n} weeks ago`,
    loadingPortal: 'Loading Portal…',
    lastOpenedLabel: 'Last Opened',
    loadFailed:
      'Could not load your projects. Please check your connection and try again.',
  },
  portalProfile: {
    profileSettings: 'Profile settings',
    personalData: 'Personal Data',
    accountData: 'Account Data',
    profilePicture: 'Profile Picture',
    upload: 'Upload',
    uploading: 'Uploading\u2026',
    removing: 'Removing\u2026',
    firstName: 'First Name',
    lastName: 'Last Name',
    jobTitle: 'Job Title',
    phoneNumber: 'Phone Number',
    email: 'Email',
    password: 'Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    enterCurrentPassword: 'Enter current password',
    minimumCharacters: 'Minimum 8 characters',
    reEnterPassword: 'Re-enter password',
    passwordsMismatch: 'Passwords do not match.',
    change: 'Change',
    updatePassword: 'Update password',
    saveChanges: 'Save changes',
    saving: 'Saving\u2026',
    backToPortal: 'Back to User Portal',
    tenant: 'Tenant',
    errorLoadProfile: 'Could not load your profile. Please try again.',
    errorSaveProfile: 'Could not save your changes. Please try again.',
    errorUploadAvatar: 'Could not upload your photo. Please try again.',
    errorRemoveAvatar: 'Could not remove your photo. Please try again.',
    errorChangePassword: 'Password change failed.',
    currentPasswordIncorrect: 'Current password is incorrect.',
    tooManyPasswordAttempts:
      'Too many attempts. Please wait a few minutes and try again.',
    phoneOnlySignInMethod:
      'Your phone number is your only way to sign in, so it can’t be removed.',
    phoneInUse: 'This phone number is already used by another account.',
    invalidPhone: 'Enter a valid phone number.',
    invalidProfile:
      'Some details are invalid. Check your entries and try again.',
    firstNameRequired: 'First name is required.',
    setPassword: 'Set password',
    projectCoverAlt: (name: string) => `${name} cover`,
    struxHubVersion: (v: string) => `StruxHub version: ${v}`,
    fileTooLarge: (maxMB: number) =>
      `File is too large. Maximum size is ${maxMB} MB.`,
    phoneNotVerifiedYet: 'This Phone Number is not verified yet',
    verify: 'Verify',
    remove: 'Remove',
    addPhoneNumber: 'Add phone number',
    changePhoneNumber: 'Change phone number',
    newNumber: 'New number',
    codeSmsNotice:
      'We’ll text a 6-digit code to this number. Message and data rates may apply.',
    sendCode: 'Send code',
    sendingCode: 'Sending\u2026',
    enterTheCode: 'Enter the code',
    verificationCode: 'Verification code',
    codeSentTo: (phone: string) => `Sent to ${phone}.`,
    wrongNumber: 'Wrong number?',
    edit: 'Edit',
    resendCodeIn: (time: string) => `Resend code in ${time}`,
    resendCode: 'Resend code',
    sendNewCode: 'Send new code',
    verifyingCode: 'Verifying\u2026',
    phoneNumberVerified: 'Phone number verified.',
    phoneAlreadyVerified: 'This number is already verified.',
    removePhoneTitle: 'Remove phone number',
    removePhoneQuestion: (phone: string) =>
      `Remove ${phone} from your profile?`,
    invalidCode: (triesLeft: number) =>
      `That code isn’t right. ${triesLeft} ${ending(triesLeft, 'try', 'tries')} left.`,
    codeExpired: 'This code has expired. Send a new one.',
    verificationPhoneInUse:
      'This number is already in use by another account. Contact support if it’s yours.',
    invalidMobilePhone: 'Enter a valid mobile number.',
    tooManyCodesRequested: (minutes: number) =>
      `Too many codes requested. Try again in ${minutes} ${ending(minutes, 'minute', 'minutes')}.`,
    tooManyCodesRequestedHours: (hours: number) =>
      `Too many codes requested. Try again in ${hours} ${ending(hours, 'hour', 'hours')}.`,
    smsUnavailable: 'We couldn’t send a text right now. Try again later.',
    unsupportedDestination: 'Texts to this country aren’t supported yet.',
    errorPhoneVerification:
      'Could not verify your phone number. Please try again.',
    qrCode: 'QR Code',
    qrCodeDescription:
      'Use this code at scanners on any of your StruxHub projects.',
  },
  portalProjectRequest: {
    title: 'New Project Request',
    description:
      'To create a new project, submit a request using the form below',
    firstName: 'First Name',
    lastName: 'Last Name',
    jobTitle: 'Job Title',
    workEmail: 'Work Email',
    phoneNumber: 'Phone Number',
    projectName: 'Project Name',
    additionalDetails: 'Additional details',
    submit: 'Submit',
    submitting: 'Submitting…',
    fieldRequired: 'This field is required.',
    invalidEmail: 'Enter a valid email address.',
    successTitle: 'Request was submitted',
    successDescription:
      'Your project request has been submitted successfully. We’ll review your request and follow up with you soon.',
    errorSubmit: 'Could not submit your request. Please try again.',
    errorInvalid: 'Some details are invalid. Please check the form.',
    errorTooManyRequests:
      'Too many requests. Please wait a while and try again.',
  },
  preferences: 'Preferences',
  administration: 'Administration',
  projectDetails: 'Project details',
  integrations: 'Integrations',
  userDirectory: 'User directory',
  team: 'Team',
  crew: 'Crew',
  teams: 'Teams',
  defaultTeams: 'Default teams',
  presentations: 'Presentations',
  tracker: 'Tracker',
  uploadSchedule: 'Upload Schedule',
  filterBy: 'Filter by',
  filterByMe: "Filter by 'Me'",
  filterByMyCompany: "Filter by 'My Company'",
  filter: 'Filter',
  filters: 'Filters',
  createNewFilter: 'Create New Filter',
  addScheduleData: 'Add schedule data',
  editScheduleData: 'Edit schedule data',
  struxhubObject: `${brandName} object`,
  selectObject: 'Select object',
  selectWorkForm: 'Select work form',
  hierarchies: 'Hierarchies',
  dataToSchedule: 'Data to Schedule',
  searchUserOrGroup: 'Search user or group',
  tool: 'Tool',
  workflow: 'Workflow',
  workflowDetails: 'Workflow details',
  workflows: 'Workflows',
  inactiveWorkflowTypeTooltip:
    'This Workflow Type is not available for creation.',
  workflowConfigurator: 'Workflow Configurator',
  manageItIn: 'Manage it in',
  manageInWorkflowConfiguratorOnDesktop:
    'Manage it in Workflow Configurator on Desktop version',
  fields: 'Fields',
  submissions: 'Submissions',
  fieldsSelected: 'fields selected',
  struxHubFields: 'StruxHub Fields',
  primaryWorkflowFields: 'Primary',
  secondaryWorkflowFields: 'Secondary',
  structureFields: 'Structure',
  contentFields: 'Content',
  primaryWorkflowFieldsDescription:
    'Primary Workflow Fields define the essential, record-defining information used across StruxHub to display, manage, and control each Workflow. This includes core details like Date and Time, Location, Company, Equipment, and Material—along with role-based Workflow Users such as the Responsible User (the primary owner of the Workflow), the Requester, and Additional Contacts. Each Primary Workflow Field can be added once',
  secondaryWorkflowFieldsDescription:
    'Adds supporting context and relationships used across StruxHub to clarify, organize, and complement core Workflow details',
  struxHubFieldsDescription:
    'Fields that are native to StruxHub and appear across the platform',
  structureFieldsDescription: 'Layout elements used to organize a Workflow',
  contentFieldsDescription:
    'Instructional text shown to users. Does not collect data',
  customFieldsDescription:
    'Generic input fields for capturing data not covered by StruxHub Fields',
  goToStep: 'Go to Step',
  goToWorkflow: 'Go to Workflow',
  sitemapSetup: 'Sitemap setup',
  templateUpdating: 'Template Updating',
  selectExistingWorkflow: 'Select existing Workflow',
  createNewWorkflow: 'Create new Workflow',
  enterWorkflowType: 'Enter Workflow type',
  basedOnWorkflow: 'Based on',

  publishTo: 'Publish to',
  geoReferencing: 'Geo Referencing',
  tags: 'Tags',
  permittedCompanies: 'Permitted Companies',
  notPermitted: 'Not permitted',
  hoursOfOperation: 'Hours of Operation',
  inheritedHoursDescription:
    "Inherited from the Project's working hours. Manage it in",
  inheritedClosuresDescription:
    "Inherited from the Project's closures. Manage it in",
  outsideOperatingHours: 'Outside operating hours',
  dateClosures: 'Date Closures',
  showInMap: 'Show in Maps',
  showInMapTooltip:
    'Show in Maps:\nOn: The Resource Object can be shown on Map Views and toggled visible or hidden there.\nOff: The Resource Object is not available in Maps.',
  resourceShownInMaps:
    'The Resource Object can be shown on Map Views and toggled visible or hidden there.',
  resourceHiddenFromMaps: 'The Resource Object is not available in Maps.',
  workflowTypes: 'Workflow Types',
  workflowResource: 'WR',
  workflowResourceTooltip:
    'WR (Workflow Resource):\nOn: The Resource Object can be booked and assigned to a Workflow.\nOff: The Resource Object is not available for booking.',
  workflowResourceBulk: 'Workflow Resource',
  resourceNotAvailableForBooking:
    'The Resource Object is not available for booking.',
  resourceAvailableForBooking:
    'The Resource Object can be booked and assigned to a Workflow.',
  maxBookingDuration: 'Max Booking Duration',
  maxOverlapping: 'Max Overlapping',
  maxFormBookingDurationDays: (duration, unit) =>
    `Max Booking Duration: Max booking duration for this Workflow is ${duration} ${unit}`,
  objects: 'Objects',
  image: 'Image',
  groupBy: 'Group by',
  basemap: 'Basemap',
  newView: 'New view',
  addView: 'Add view',
  savedViews: 'Saved views',
  createNewView: 'Create new view',
  createNewWhiteboard: 'Create new whiteboard',
  mapFileUpload: {
    uploadMapFile: 'Upload Map File',
    uploadWhiteboard: 'Upload whiteboard',
    replaceThisPlan: 'Replace this plan',
    withThisOne: '... with this one.',
    dragMapHere: 'Drag map here',
    dragMapFileHere: 'Drag Map File here',
    acceptedExtensions: (extensions: string) => `(${extensions} accepted)`,
    selectPage: 'Select page',
    planName: 'Plan name',
    whiteboardName: 'Whiteboard name',
    planUploaded: 'Plan uploaded',
    mapFileUploaded: 'Map file uploaded',
    basemapUploaded: 'Basemap uploaded',
    uploadingPlan: 'Uploading plan',
    uploadingBasemap: 'Uploading basemap',
    tilesetLimits: (maxNumber: number, maxSize: string) =>
      `You can upload up to ${maxNumber} tilesets of up to ${maxSize} each.`,
    tooManyTilesets: 'You have uploaded too many tilesets',
    fileTooLarge: 'You have uploaded too large a file',
    tilesetNameTooLong: (maxLength: number) =>
      `Tileset name too long. Max size ${maxLength} symbols`,
    invalidFileName:
      'File name can contain only letters, numbers, and symbols “.”, “-”, and “_”',
  },
  mapExport: {
    exportMap: 'Export map',
    output: 'Output',
    map: 'Map',
    frameTag: 'Exported area · drag map to adjust',
    backToSavedView: 'Restore saved viewport',
    restoreViewportReason: 'Available after you move or zoom the map.',
    framing: 'Framing',
    previewReduced: 'Shown smaller than actual size',
    background: 'Background',
    blank: 'Blank',
    basemapLabels: 'Basemap labels',
    notOnBlank: 'Not on Blank',
    buildings3d: '3D buildings',
    streetOnly: 'Street only',
    angle: 'Angle',
    topDown: 'Top-down',
    tilted: 'Tilted',
    toScale: 'To scale',
    notToScale: 'Not to scale',
    topDownBody: (deg: number) =>
      `Flattened from the saved ${deg}° tilt, so it's to scale.`,
    tiltedBody: 'A picture, not a plan: no scale bar or label key.',
    whatYouLose: 'What you lose',
    tiltTradeOffsTitle: 'A tilted export is a picture, not a plan',
    tiltTradeOffs: {
      noScale: "No scale bar. Don't measure from it.",
      mapLabelsOnly: "The map's own labels, no label key.",
      farSofter: 'Far side is softer and smaller.',
      northCentreSlower:
        'North arrow is exact only at the centre, and it takes longer to export.',
      buildingsHide: '3D buildings can hide pins.',
    },
    format: 'Format',
    png: 'PNG',
    pdf: 'PDF',
    useFor: 'Use for',
    useForChoices: {
      print: 'Print',
      screen: 'Screen',
    },
    paperSize: 'Paper size',
    paperOption: (paper: string, size: string) => `${paper} · ${size}`,
    screenShape: 'Aspect ratio',
    screenShapes: {
      '16x9': '16:9 Widescreen',
      '16x10': '16:10',
      '4x3': '4:3',
    },
    closestToMap: 'Closest to your map',
    tooLargeForDevice: 'Too large for this device',
    dpiReadout: (dpi: number) => `${dpi} dpi`,
    pixelReadout: (width: number, height: number) => `${width} × ${height} px`,
    approxFileSize: (mb: number) => `about ${mb} MB`,
    approxFileSizeRange: (low: number, high: number) =>
      `about ${low}–${high} MB`,
    widerThanPaper: (paper: string) => `Wider than ${paper}`,
    standardQuality: 'Standard',
    highQuality: 'High',
    screenStandardHint: (width: number, height: number) =>
      `${width} × ${height} px: good for email, chat and slides.`,
    screenHighHint: (width: number, height: number) =>
      `${width} × ${height} px: stays sharp when zoomed in.`,
    screenHighExceedsDevice: 'High is too large for this device.',
    paperStandardHint: (dpi: number) => `${dpi} dpi: good for office printers.`,
    paperHighHint: (dpi: number) => `${dpi} dpi: sharp large-format prints.`,
    highQualityExceedsDevice: 'High is too large for this device at this size.',
    screenImageryPointer: 'Satellite is sharper on large paper.',
    useSharpestPaper: (useFor: string, paper: string, quality: string) =>
      `Switch to ${useFor} · ${paper} · ${quality}`,
    letter: 'Letter',
    a4: 'A4',
    tabloid: 'Tabloid',
    a3: 'A3',
    a2: 'A2',
    a1: 'A1',
    archD: 'ARCH D',
    a0: 'A0',
    archA: 'ARCH A',
    archB: 'ARCH B',
    ansiC: 'ANSI C',
    archC: 'ARCH C',
    ansiD: 'ANSI D',
    archE1: 'ARCH E1',
    ansiE: 'ANSI E',
    archE: 'ARCH E',
    paperGroups: {
      imperial: 'ANSI and ARCH',
      metric: 'ISO',
    },
    moreSizes: (family: string) => `More sizes (${family})`,
    withFamiliarName: (standard: string, name: string) =>
      `${standard} (${name})`,
    orientation: 'Orientation',
    landscape: 'Landscape',
    portrait: 'Portrait',
    millimetres: (width: number, height: number) => `${width} × ${height} mm`,
    inches: (width: number, height: number) => `${width} × ${height} in`,
    pageElements: 'Title and legend',
    pageElementsHint: 'Title, date, legend, scale bar and north arrow.',
    pageElementsHintTilted: 'Title, date and north arrow, marked Not to scale.',
    northLetter: 'N',
    clusterLegend: 'Workflows',
    clusterLegendKeyed: 'Workflows · see label key',
    keyTagLegend: 'See label key',
    truePositionLegend: 'True position',
    metres: (length: number) => `${length} m`,
    kilometres: (length: number) => `${length} km`,
    miles: (length: number) => `${length} mi`,
    mapFiles: 'Map files',
    mapFileList: 'Map file list',
    mapFilesOutsideFrame: (count: number) =>
      `${count} more map file${ending(count)} on this view ${ending(count, 'is', 'are')} outside the frame`,
    shapes: 'Shapes',
    markers: 'Icons',
    layerLabels: 'Labels',
    pins: 'Pins',
    markups: 'Markups',
    needsPins: 'Needs Pins',
    needsMarkups: 'Needs Markups',
    readability: 'Readability',
    optimized: 'Optimized',
    asOnMap: 'As on map',
    readabilityHints: {
      optimized: 'Sized for the paper and distance, placed without overlaps.',
      asOnMap: 'Pins, icons and labels keep their sizes from the map.',
      tilted: "Optimized needs Top-down, so the map's own sizes are kept.",
    },
    overallSize: 'Overall size',
    easyToReadFrom: 'Easy to read from',
    readZones: {
      inHand: 'In hand or on a desk',
      upClose: 'Up close',
      acrossRoom: 'Across a room',
    },
    standardFor: (name: string) => `Standard for ${name}`,
    standardAt: (name: string, distance: string) =>
      `Standard for ${name}: ${distance}`,
    aboutDistance: (distance: string) => `about ${distance}`,
    readFromValueText: (distance: string, zone: string) =>
      `${distance}, ${zone}`,
    adjustByType: 'Adjust by type',
    adjustByTypeNote: (isPrint: boolean): string =>
      isPrint ? 'On top of the reading distance.' : 'Relative to Overall size.',
    percent: (value: number) => `${value}%`,
    centimetres: (length: number) => `${length} cm`,
    feet: (length: number) =>
      `${Math.trunc(length) || ''}${length % 1 ? '½' : ''} ft`,
    customized: 'Customized',
    textKinds: {
      workflowPin: 'Workflow labels',
      location: 'Location labels',
      markup: 'Markup labels',
      markupText: 'Markup text',
      key: 'Key and numbers',
    },
    points: (pt: number) => `${pt} pt`,
    textKindValueText: (percent: number, pt: number) =>
      `${percent} percent, about ${pt} points`,
    atFloorHint: 'Already the smallest size that reads at this distance.',
    atFloorScreenHint: 'Already the smallest readable size.',
    atFitLimitHint: 'Largest that fits this paper.',
    atFitLimitScreenHint: 'Largest that fits this export.',
    needsLabelKey: 'Needs Label key',
    symbolKinds: {
      workflowPin: 'Workflow pins',
      location: 'Location icons',
    },
    printedMillimetres: (length: number) => `${length} mm`,
    printedInches: (length: number) => `${length} in`,
    symbolValueText: (percent: number, length: string) =>
      `${percent} percent, about ${length}`,
    key: 'Label key',
    keyExplainer:
      "Labels that don't fit get a number or letter, listed in the key.",
    keyAllFit: 'Every label fits on the map, so no label key is needed.',
    keyOffHint: (count: number) =>
      `Turn on to list the ${count} hidden ${count === 1 ? 'label' : 'labels'} in the file.`,
    keyOnHint: {
      besideMap: () => 'Beside the map in the file',
      besidePage: (paper: string) =>
        `Beside the page, so the PNG is wider than ${paper}. A PDF keeps it on page 2.`,
      pageTwo: () => 'On page 2 of the PDF',
    },
    keyViewList: 'View list',
    keySummaryTitle: (labels: number) =>
      labels === 1 ? 'Where the 1 label goes' : `Where the ${labels} labels go`,
    labelFates: {
      onMap: 'On the map',
      inKey: 'In the label key',
      notListed: 'Not listed, key full',
      asCounts: 'Count only, in black disks',
      notShown: 'Left off',
    },
    markupLeftOff: 'Markup labels left off',
    densityWarning: (isPaper: boolean): string =>
      isPaper
        ? 'Too crowded: zoom in, make text smaller, or pick larger paper.'
        : 'Too crowded: zoom in or make text smaller.',
    keyGroupHeader: (count: number) =>
      `${count} ${count === 1 ? 'Workflow' : 'Workflows'}`,
    keyGroupHeaderTrimmed: (count: number, listed: number) =>
      `${count} Workflows, ${listed} listed`,
    keyOverflowRow: (count: number) => `+${count} more not listed`,
    previewKeyBadge: {
      besideMap: (labels: number) =>
        `Label key beside the map · ${labels} ${labels === 1 ? 'label' : 'labels'}`,
      besidePage: (labels: number) =>
        `Label key beside the page · ${labels} ${labels === 1 ? 'label' : 'labels'}`,
      pageTwo: (labels: number) =>
        `Label key on page 2 · ${labels} ${labels === 1 ? 'label' : 'labels'}`,
    },
    exportProgress: 'Export progress',
    exportCancelled: 'Export cancelled.',
    shareMap: 'Share map',
    createImage: 'Create image',
    createPdf: 'Create PDF',
    mapReady: (details: string) => `Map ready · ${details}`,
    keepScreenOpen: 'Keep this screen open until the file is ready.',
    shareFailed: 'Could not share the file. Try again.',
    desktopExportHint: (tool: string) =>
      `Customize paper, layers and labels in ${tool} on desktop.`,
    phases: {
      preparing: 'Preparing…',
      loadingTiles: 'Loading tiles…',
      placingLabels: 'Placing labels…',
      rendering: 'Rendering…',
      encoding: 'Saving file…',
    },
    errors: {
      tilesTimeout: 'The map took too long to load. Try again.',
      bufferClamped:
        'This device could not render the full size. Pick a smaller size.',
      zoomTooDeep: 'Zoom out to export at this size.',
      exceedsDevice:
        'Too large for this device. Pick a smaller size or Standard quality.',
      exceedsDeviceScreen:
        'Too large for this device. Choose Standard quality.',
      interrupted: 'The window changed size during the export. Try again.',
      pageReloaded: 'The page reloaded during the export. Try again.',
      failed: 'Export failed. Try again.',
    },
    phoneErrors: {
      bufferClamped:
        'This phone ran short of memory for the map. Close other apps and try again.',
      exceedsDevice: 'Too large for this phone.',
      exceedsDeviceScreen: 'Too large for this phone.',
      interrupted:
        'The screen turned or changed size during the export. Try again.',
      pageReloaded:
        'The phone ran out of memory and reloaded the page. Close other apps, then try again.',
    },
    shareMapFailed: 'Share map could not open. Try again.',
    actualSize: 'Actual size',
    fitPage: 'Fit page',
    actualSizeHint: 'Drag to look around',
    previewViewMode: 'Preview size',
    actualSizePanLabel:
      'Actual size · drag or use the arrow keys to look around, Shift for larger steps',
    actualSizeTooltip:
      "Check that labels are readable at real size. Doesn't move the export area.",
    actualSizeTopDownOnly: 'Actual size · Top-down only',
    fitPageTooltip: 'Show the whole page again, framed as before.',
    keepTabVisible: 'Keep this tab visible until the export finishes.',
  },
  newMapView: 'New Map View',
  notPublished: 'Not published',
  applications: 'Applications',
  savedViewsDialog: {
    searchHint: 'Search views',
    columns: {
      viewName: 'View name',
      filterCount: 'No. Filters',
      access: 'Shared to Project',
      creator: 'Created by',
    },
  },
  createNewTag: 'Create New Tag',
  viewBasemap: 'View basemap',
  viewBookings: 'View bookings',
  thereIsNoInstanceInTheSelectedCategory:
    'There is no instance in the selected category',

  directory: 'Directory',
  name: 'Name',
  firstName: 'First Name',
  lastName: 'Last Name',
  phoneNumber: 'Phone',
  globalRole: 'Job Title',
  employeeIDNumber: 'Employee ID Number',
  company: 'Company',
  companyAliasLabel: 'Company as informed by user',
  companyOther: 'Other',
  addXAsNewCompany: (name: string) => `Add "${name}" as a new Company`,
  selectOrAddCompany: 'Select or add a Company',
  companyDirectory: 'Company directory',
  common: 'Common',
  myCompany: 'My company',
  myCo: 'My Co.',
  companyType: 'Company Type',
  classification: 'Classification',
  companyName: 'Company Name',
  user: 'User',
  users: 'Users',
  userTags: 'Tags',
  allUsers: 'All Users',
  role: 'Role',
  projectRole: 'Project Role',
  projectRoles: 'Project Roles',
  projectTrades: 'Project Trades',
  address: 'Address',
  email_noun: 'Email',
  phone: 'Phone',
  phoneVerification: 'Phone verification',
  call_verb: 'Call',
  email_verb: 'Email',
  sendDirectMessageInStruxHub: `Send direct message in ${brandName}`,
  password: 'Password',
  currentPassword: 'Current Password',
  newPassword: 'New Password',
  confirmNewPassword: 'Confirm New Password',
  confirmPassword: 'Confirm Password',
  group: 'Group',
  reportsTo: 'Reports To',
  reportOf: (type: string) => `${type} report`,
  createReport: 'Create Report',
  deliveryReport: 'Delivery Report',
  formReport: 'Workflow Report',
  materialReport: 'Material Report',
  aReportWillBeGeneratedWithTheAboveFilters:
    'A report will be generated with the above filters',
  aReportWillBeSentToYourEmailShortly:
    'A report will be sent to your email shortly',
  reportDetails: 'Report Details',
  workflowReportTitle: 'Workflow Report',
  deliveryReportTitle: 'Delivery Report',
  scanStationReportTitle: 'Scan Station Report',
  resourceDetails: 'Resource Details',
  officeField: 'Office/Field',
  accountType: 'Account Type',
  status: 'Status',
  manufacturer: 'Manufacturer',
  fuel_type: 'Fuel Type',
  bid_package: 'Bid Package',
  assembly: 'Assembly',
  isDockMaster: 'Is Dock Master',
  isInspector: 'Is Inspector',
  invalidUser: 'Invalid User',
  unknownUser: 'Unknown User',
  unknownDeletedObject: 'Unknown (Deleted) Object',
  ruleProblems: {
    incomplete: "Rule is incomplete and can't be saved.",
    unknownCondition:
      'This tag points to something that no longer exists. Remove it or pick it again.',
  },
  inviteStatus: 'Invite Status',
  inviteStatusValues: {
    Not_Invited: 'Not Invited',
    Pending: 'Pending',
    Accepted: 'Accepted',
    // service
    Not_Added: 'Not Added',
    Added: 'Added',
    Requested: 'Requested',
    Not_Requested: 'Not Requested',
  },
  accountPosition: 'Account Position',
  accountPositions: {
    office: 'Office',
    field: 'Field',
    management: 'Management',
  },
  fieldMapping: 'Field Mapping',
  projectAccessTypeValues: {
    admin: 'Admin',
    member: 'Member',
    owner: 'Owner',
  },
  modalInfoSections: {
    personalInformation: 'Personal information',
    companyInformation: 'Company information',
    projectInformation: 'Project information',
    additionalInformation: 'Additional information',
    workflowPermissions: 'Workflow permissions',
    companyContactInfo: 'Company Contact Info',
    legalInfo: 'Legal info',
    companyAddress: 'Company address',
    users: 'Users',
    qrCodeAccessAndBadges: 'QR Code access and badges',
    globalInfo: `Global info (${brandShortName} Level)`,
  },
  projectWorkflows: {
    deliveryBooking: 'Delivery Booking',
    deliveryInspection: 'Delivery Inspection',
    scheduleComment: 'Schedule Comment',
    formsMaster: 'Forms Master',
    scanMaster: 'Scan Master',
    viewSchedule: 'View schedule',
    scheduleUpdating: 'Update schedule',
    scheduleUploading: 'Upload schedule',
    scheduleNotes: 'Notes on schedule',
  },
  permissions: {
    none: 'None',
    readOnly: 'Read-only',
    standard: 'Standard',
    master: 'Master',
  },
  userProfileRoleNames: {
    admin: 'Site Admin',
    owner: 'Owner',
    docMaster: 'Doc Master',
    inspector: 'Inspector',
    presentationUser: 'Presentation User',
    formsMaster: 'Forms Master',
    scheduleViewing: 'View schedule',
    scheduleUpdating: 'Update schedule',
    scheduleUploading: 'Upload schedule',
    scheduleNotes: 'Notes on schedule',
  },
  companyTypes: {
    GeneralContractorPrimary: 'General Contractor (Primary)',
    GeneralContractorSecondary: 'General Contractor (Secondary)',
    Owner: 'Owner',
    SubContractor: 'Subcontractor',
    TieredSubContractor: 'Tiered Subcontractor',
  },
  saveProfile: 'Save profile',
  notificationType: 'Notification Type',
  notificationSettings: 'Notification Settings',
  deliveryNotifications: 'Delivery notifications',
  scheduleNotifications: 'Schedule Notifications',
  trackerNotifications: 'Tracker Notifications',
  logisticsNotifications: 'Logistics Notifications',
  formNotifications: 'Workflow notifications',
  scannerNotifications: 'Scanner notifications',
  iWouldLikeReceiveMy: 'I would like to receive my',
  inApp: 'In-App',
  sms: 'SMS',
  bySms: 'By SMS',
  byEmail: 'By email',
  successfullyUpdated: 'Successfully updated',
  wrongPhoneNumberFormat: 'Wrong phone number format',
  userProfileDescriptions: {
    name: `Your name will be used when you communicate with other team members in ${brandName}.`,
    projectRoles:
      'Your role in project is used to ensure that the notifications and the Workflows you are involved in are pertinent. ' +
      'Further customization is possible in Preferences in Settings.',
    notifications:
      `${brandName} apps will send you notifications for work that you are a team member of. ` +
      'These notifications appear In-App next to the bell icon on the top right of the page, ' +
      'if you have entered your phone number in your profile through SMS messages sent to your phone, ' +
      'and some summaries of notifications may be sent to your email address. ' +
      'You can de-select any of these options below to no longer receive notifications by that method.',
  },
  whitelistedDomains: 'Whitelisted Domains',

  projectAdminSummary: 'Project Admin Summary',
  lastUpdated: 'Last Updated',
  dateByName: (date: string, name: string) => date + ' by ' + name,
  uploadProjectSchedule: 'Upload Project Schedule',
  fileUploaded: 'File Uploaded: ',
  totalActivities: 'Total Activities',
  totalUsers: 'Total Users',
  uploadProcurementPlan: 'Upload Procurement Plan',
  bpAssignedToCompanies: (amount: number, totalAmount: number) =>
    amount + ' of ' + totalAmount + ' bid packages assigned to companies.',
  downloadCurrentValuesAsCsv: (toLink: (aText: string) => any) => [
    toLink('Download'),
    ' current values as a csv file.',
  ],
  uploadTeamList: 'Upload Team List',
  currentTeamMembers: (amount: number) => amount + ' current team members.',
  emptyTagTypes: (tags: TagType[]) =>
    `Some of the required Type Tags (${tags
      .map(tag => getBandTitleByTagType(tag))
      .toString()}) are empty for certain companies. Default values will be set for them.`,
  adminSummaryDescriptions: {
    projectDetails:
      'Define the name and location or your project so that you easily find it in your Project selector',
    projectSchedule: `Upload your updated Master Schedule from P6 as frequently as you want. Don't worry — you won't lose any data in ${brandName}.`,
    procurementPlan:
      'Upload your updated Procurement plan as you bring on more subcontractors to the Project ' +
      'and refine your expected values for each bid package.',
    teamList:
      'Upload your Project Team list as new team members join the Project. ' +
      'Including optional information such as internal Department/Team affiliation facilitates ' +
      'the resource assignments to Activities.',
  },

  project: 'Project',
  xProjects,
  projectSingularPlural: 'Project(s)',
  notificationsRulesSetupDescriptions: {
    helperText1: (entityType: string) =>
      'A rule will send notifications to a set of users for all future ' +
      entityType +
      ' with a set of designated tags',
    helperText2:
      'Tags can be used alone or combined with boolean operators. For example:',
  },

  selectProject: 'Select Project',
  selectProjectToSync: 'Select Project(s) to Sync',
  selectBookingTime: 'Select booking time',
  newProject: 'New Project',
  projectName: 'Project Name',
  projectCode: 'Project Code',
  projectLocation: 'Project Location',
  projectLogo: 'Project Logo',
  projectHeaderImage: 'Project Header',
  streetAddress: 'Street Address',
  city: 'City',
  state_region: 'State',
  zipCode: 'Zip Code',
  country: 'Country',
  projectTimeSettings: 'Project time settings',
  externalWorkflowSharing: 'External Workflow sharing',
  addOtherStxCalendarsToProject: 'Add other StruxHub calendars to project',
  QRCodes: 'QR codes',
  xQRCode: (name: string) => `${name} QR Code`,
  scanStations: 'Scan stations',
  projectAdmins: 'Project Admins',
  setTheTimeZoneForThisProject: 'Set the time zone for this project',
  dateFormat: 'Date format',
  maturixProjectName: 'Maturix Project name',
  maturixTemperatureUnit: 'Maturix Temperature unit',
  maturixStrengthUnit: 'Maturix Strength unit',
  maturixStations: 'Maturix Stations',
  undefinedSection: 'Undefined section',
  setTheDateTimeFormatForThisProject:
    'Set the date time format for this project',
  localTimezone: 'Local timezone',
  us: 'US',
  international: 'International',
  customProjectClosures: 'Custom Project Closures',
  addCustomProjectClosure: 'Add Custom Project Closure',
  forTheFuture: 'For the Future',
  projectType: 'Project Type',
  apps: 'Apps',
  admins: 'Admins',
  theListOfProjectsAdministrators: "The list of project's administrators",
  addNew: 'Add new',
  addNewValue: 'Add new',
  addNewAdmin: 'Add new Admin',
  addNewContact: 'Add new Contact',
  defaultWorkingHours: 'Default working hours',
  timeZone: 'Time zone',
  everyday: 'Everyday',
  holidays: 'Holidays',

  struxHubHome: `${brandName} Home`,
  struxHubForms: `${brandName} Workflows`,
  app: 'App',
  manageApps: 'Manage Apps',
  defaultPage: 'Default page',
  appsVisibilityInfo: 'Show or hide Apps in the navigation.',
  defaultPageInfo:
    'Select which App serves as the landing page when users return to the product.',
  projectDetailsDescriptions: {
    projectNameError:
      'Name should not be empty and should contain only letters, numbers, spaces, _ , -',
    projectCodeError:
      'Project Code cannot be empty and should not contain any special characters. Max length for Project code is 5 characters',
    changeProjectLocationWarning:
      'Your Project Time Zone has been set differently ' +
      'to the time zone at your Project Location',
  },
  integrationsSetupDescriptions: {
    invalidToken: 'Invalid Token',
  },
  procoreDescription: 'Construction management software',
  loginToProcore: 'Login to Procore',
  procoreLoginDescription:
    'To set this integration you need to be logged in to Procore account.',
  procoreNoCompaniesAccess:
    'You do not have access to any Procore companies. Please contact your company administrator.',
  procoreNoAccessibleProjects:
    'You do not have any accessible projects in this company. Please contact your company administrator.',
  procoreConnectionFailed:
    'Could not connect to Procore. Please try again or contact your company administrator.',
  procoreUsersWithoutContact: (names: string[]) =>
    `${names.join(', ')} can't be added: ${names.length === 1 ? 'the user has' : 'the users have'} no email or phone number. Contact your Procore Project Admin to get access to the Directory.`,
  integrationEnabled: 'Integration Enabled',
  integrationDisabled: 'Integration Disabled',
  deliveriesFieldMapping: 'Deliveries Field Mapping',
  workflowsFieldMapping: 'Workflow Field Mapping',
  deleteWorkflowMapping: 'Delete Workflow Mapping',
  synchronizationEnabled: 'Synchronization Enabled',
  synchronizationDisabled: 'Synchronization Disabled',
  connecting: 'Connecting...',
  loggedIn: 'Logged In',
  loginToEnableIntegration: 'Login to Procore to Enable the Integration',
  loginToDisableIntegration: 'Login to Procore to disable the integration',
  procoreLoginSuccess: 'Successfully logged in to Procore account',
  procoreIntegrationEnabled: 'Integration with Procore enabled',

  procoreFieldMappings: {
    struxhubDeliveryLog: 'Struxhub Delivery Log',
    procoreDeliveryLog: 'Procore Delivery Log',
    startTimeOfBookingDate: 'Start time of Booking Date',
    time: 'Time',
    requesterCompany: 'Requester Company',
    deliveryFrom: 'Delivery From',
    bookingId: 'BookingId',
    trackingNumber: 'Tracking Number',
    vendorVendorEmailsMaterialsDeliveryNote:
      'Vendor, Vendor emails, Materials, Delivery note',
    contents: 'Contents',
    notesFromDeliveryLog: 'Notes from Delivery Log',
    comments: 'Comments',
    attachedFilesFromDeliveryLog: 'Attached files from Delivery Log',
    attachments: 'Attachments',
  },

  countryValues: countries,
  timezoneValues: timezones,
  timezoneWithOffsets: (name: string, offsets: string[]) =>
    `${name} (GMT ${offsets.join('/')})`,

  addCustomProjectClosures: 'Add Custom Project Closures',
  closureName: 'Closure Name',
  closureStartDate: 'Closure Start Date',
  closureEndDate: 'Closure End Date',

  loadingTeamMembers: 'Loading Team Members',
  loadingMonitoringData: 'Loading Monitoring Data',
  xTeamMembersSelected: (x: number) => `${x} Team member${ending(x)} selected`,
  xCompaniesSelected: (x: number) =>
    `${x} Compan${ending(x, 'y', 'ies')} selected`,
  importExportTeamMembers: 'Import/Export team members',
  teamMembersDropdownStates: {
    downloadTeamMembers: 'Download users (.csv)',
    uploadTeamMembers: 'Upload users (.csv)',
    viewCompanyDirectory: 'View company directory',
    connectToProcore: 'Connect to Procore',
  },
  selectNotInvited: 'Select Not Invited',
  selectStatusRequested: 'Select STATUS Requested',
  setNotInvited: 'Set Not Invited',
  addNewProjectMember: 'Add new project member',
  uploadYourUpdatedProjectMemberListCsvFile:
    'Upload your updated Project Member list csv file.',
  orDragYourFileHere: 'Or drag your file here.',
  uploadYourScheduleFileInP6XerOrExcelXlsxXlsmFormat:
    'Upload your Schedule file in P6 (.xer) or Excel (.xlsx, .xlsm) format.',
  sendInviteLinkToUser: 'Send Invite Link to User',
  addUser: 'Add User',
  byUsers: 'By Users',
  byWorkflowField: 'By Workflow Field',
  byCompany: 'By Company',
  bicUsers: 'BIC Users',
  editUsers: (x: number) => `Edit User${ending(x)}`,
  userValidationErrors: {
    firstNameRequired: 'First name is required',
    lastNameRequired: 'Last name is required',
    emailRequired: 'Email is required',
    invalidEmail: 'Email is not valid',
    invalidPhone: 'Phone number is not valid',
    emailIsTaken: 'Email is already taken by another project member',
    phoneIsTaken: 'Phone is already taken by another project member',
    memberWithSuchEmailIsAlreadyAdded:
      'A user with such email is already added to the project',
    memberWithSuchPhoneNumberIsAlreadyAdded:
      'A user with such phone number is already added to the project',
    companyRequired: 'Company is required',
  },

  bulkSaveErrors: {
    invalidEmail: 'Invalid email address',
    invalidPhone: 'Invalid phone number',
    duplicateRow: 'Duplicates an earlier row in this batch',
    missingName: 'First and last name are required',
    identifierConflict: 'Email and phone belong to different existing users',
    authDenied: 'Registration was rejected by the authentication service',
    authUnavailable: 'Authentication service is unavailable — try again later',
    rowProcessingFailed:
      'Row failed processing — contact support if it persists',
    rowError: (row: number | string, message: string) =>
      `Row ${row}: ${message}`,
    namedRowError: (name: string, message: string) => `${name}: ${message}`,
    andXMoreRows: (x: number) => `…and ${x} more row${ending(x)}.`,
    xRowsFailedToSave: (x: number) => `${x} row${ending(x)} failed to save`,
    unknownError: 'Unknown error',
    xMembersImported: (x: number) => `${x} member${ending(x)} imported`,
  },

  companyValidationErrors: {
    nameRequired: 'Company name is required',
    emailRequired: 'Business email is required',
    invalidEmail: 'Email is not valid',
    invalidPhone: 'Business phone number is not valid',
    emailIsTaken: 'Email is already taken by another company',
    somethingFilledIncorrectly: 'Something is filled incorrectly',
    companyTypeIsRequired: 'Company type is required',
    codeRequired: 'Code is required',
    codeIncorrectLength: 'Code length should be between 3 and 5 symbols',
    codeIsNotUnique:
      'A company with this code already exists, change the value of the code field to make it unique',
  },

  companyLinkingErrors: {
    anErrorOccurredWhileLinking: 'An error occurred while linking',
    anErrorOccurredWhileUnlinking: 'An error occurred while unlinking',
    anErrorOccurredWhileFetchingGlobalStorage:
      'An error occurred while fetching global storage',
  },

  companyMerging: {
    confirmation: (sourceCompaniesText: string, targetCompanyText: string) =>
      `Do you confirm the merging of ${sourceCompaniesText} into ${targetCompanyText}?`,
  },

  somethingWentWrongDuringAPIInteraction:
    'Something went wrong during API interaction',

  signUpLinkInvalid:
    'This signup link is incomplete. Please use the QR code from your project invitation.',

  confirmDeactivationOfTheFollowingXCompanies: (x: number) =>
    `Confirm deactivation of the following ${x} compan${ending(
      x,
      'y',
      'ies',
    )}?`,
  confirmActivationOfTheFollowingXCompanies: (x: number) =>
    `Confirm activation of the following ${x} compan${ending(x, 'y', 'ies')}?`,
  confirmDeletionOfTheFollowingXProjectMembers: (x: number) =>
    `Confirm deletion of the following ${x} project member${ending(x)}?`,
  confirmInvitationOfTheFollowingXProjectMembers: (x: number) =>
    `Confirm invitation of the following ${x} project member${ending(x)}?`,
  confirmSettingAsNotInvitedOfTheFollowingXProjectMembers: (x: number) =>
    `Confirm setting as not invited of the following ${x} project member${ending(
      x,
    )}?`,
  confirmInvitation: 'Confirm Invitation',
  confirmSettingUsersAsNotInvited: 'Confirm setting users as not invited',
  confirmDeletion: 'Confirm deletion',
  confirmActivation: 'Confirm activation',
  confirmDeactivation: 'Confirm Deactivation',
  confirmDeletionOfTheFollowingXPlannedManpower: (x: number) =>
    `Confirm deletion of the following ${x} planned manpower?`,
  confirmDeletionOfTheFollowingXBidPackages: (x: number) =>
    `Confirm deletion of the following ${x} bid package${ending(x)}?`,
  confirmDeletionOfActivities: (x: number) =>
    `Are you sure you want to delete ${x} activit${ending(x, 'y', 'ies')}?`,
  confirmDeletionOfEquipment: (x: number) =>
    `Are you sure you want to delete the selected equipment?\n\n${x} ${equipment(
      x,
    )} will be deleted.`,
  confirmDeletionOfSensors: (x: number) =>
    `Are you sure you want to delete the selected sensors?\n\n${x} ${sensors(
      x,
    )} will be deleted.`,
  confirmDeletionOfLocations: (x: number) =>
    `Are you sure you want to delete the selected locations?\n\n${x} location${ending(
      x,
    )} will be deleted.`,
  confirmDeletionOfStruxTags: (x: number) =>
    `Are you sure you want to delete the selected StruxTags?\n\n${x} ${struxTagsCount(
      x,
    )} will be deleted.`,
  confirmDeletionOfMaterials: (x: number) =>
    `Are you sure you want to delete the selected materials?\n\n${x} ${materials(
      x,
    )} will be deleted.`,
  confirmDeletionOfResources: (x: number, countLabel: string) =>
    `Are you sure you want to delete the selected ${countLabel}?\n\n${x} ${countLabel} will be deleted.`,
  resourceCountLabel: (resourceTypeId: string, x: number) => {
    switch (resourceTypeId) {
      case 'offloading_equipment':
        return equipment(x)
      case 'sensor':
        return sensors(x)
      case 'material':
        return materials(x)
      default:
        return `item${ending(x)}`
    }
  },
  confirmApprovalOfTheFollowingXProjectMembers: (x: number) =>
    `Confirm approving the following ${x} request${ending(
      x,
    )} into ${brandName}?`,
  confirmApproval: 'Confirm Approval',

  selectProcoreProject: 'Select Procore project',
  selectProcoreCompany: 'Select Procore company',
  noUnit: 'No unit',
  noCompanies: 'No companies',
  noDirectoryAccess: 'No directory access',
  xUsers,
  xUsersShowing: (x: number) => `${x} User${ending(x)} showing`,
  xUsersCount: (x: number) => `${x} User${ending(x)}`,
  xTeams: (x: number) => `${x} team${ending(x)}`,
  addXUsers: (x: number) => {
    if (!x) {
      return 'Add'
    }
    // 'Add 1 user' or 'Add x users'
    return `Add ${x} User${ending(x)}`
  },
  xFiles,
  xFields,
  xFieldsAdded,

  files: 'Files',
  multiLevelScreen: 'Multi-Level Screen',
  levelsViewsWithZoneSummary: 'Levels views with zone summary',
  levelsViewsWithCompanySummary: 'Levels views with company summary',
  gantt: 'Gantt',
  presentationMode: 'Presentation mode',
  play: 'Play',
  stop: 'Stop',
  turnOn: 'Turn On',
  presentationScreens: 'Presentation Screens',
  xMinutes: (x: number) => `${x} minute${ending(x)}`,
  order_noun: 'Order',
  order_number: (x: number) => {
    switch (x) {
      case 1:
        return '1st'
      case 2:
        return '2nd'
      case 3:
        return '3rd'
      default:
        return `${x}th`
    }
  },
  display: 'Display',
  presentationModeDescriptions: {
    multiLevelScreen:
      'Displays activity and manpower performance of actuals vs. planned ' +
      'for all the levels of the building. Scrolls to display all levels',
    levelsViewsWithZoneSummary:
      'Cycles through each level of the building displaying ' +
      'the aggregate activity and manpower performance for each zone. ' +
      'Scrolls to display data for each zone.',
    levelsViewsWithCompanySummary:
      'Cycles through each level of the building displaying ' +
      'the aggregate company performance. Scrolls to display data for each company.',
    gantt:
      'Displays the project gantt view for the current look-ahead. ' +
      'Scrolls to display all activities. ',
    deliveries: 'Displays the weekly delivery calendar for the current week.',
    logistics: 'Displays the logistic map.',
    confirmText:
      'Presentation mode will log you out and set this device to playback. To ' +
      'exit presentation mode go to Settings. You will need re-log in once you ' +
      'exit presentation mode.',
    header:
      'Turning on Presentation Mode will log you out, and log this device on ' +
      'in a presentation only mode.',
    periodOfInactivity:
      'Presentation will automatically run after a period of inactivity of',
    stop:
      'Stopping presentation mode will exit you back to a log in screen ' +
      'where you can log into your projects.',
  },

  include: 'Include',
  saveConfiguration: 'Save configuration',
  preTaskChecklist: 'Pre-task Checklist',
  requireDailyInspections: 'Require daily inspections',
  inspectionFrequency: 'Inspection frequency',
  deadLineForRecordingAnInspection: 'Deadline for recording an inspection',
  checklist: 'Checklist',
  instructions: 'Instructions',
  xOptions: (x: number) => `${x} option${ending(x)}`,
  NA: 'N/A',
  remove: 'Remove',
  typeOfList: 'Type of List',
  addAdditionalItem: 'Add additional item',
  enterInstruction: 'Enter Instruction',
  enterTheXDigitCodeSentTo: (x: number) => `Enter the ${x} digit code sent to`,
  areYouSureWantToDeleteInstructionItem:
    'Are you sure you want to delete the instruction item?',
  instructionListTypes: {
    enumerated: 'Enumerated',
    bulleted: 'Bulleted',
    none: 'None',
  },
  fieldStates: {
    mandatory: 'Mandatory',
    optional: 'Optional',
    hidden: 'Hidden',
  },

  revertingProjectData: 'Reverting Project Data',
  loadingUpdatingProject: 'Loading/Updating Project',
  downloadProjectDataCsv: 'Download project data .csv',
  exploreYourScheduleInStruxHub: `Explore your schedule in ${brandName}`,
  scheduleIsNotUploaded: 'Schedule is not uploaded',
  gettingXerProjects: 'Getting xer projects',
  gettingExcelSheets: 'Getting excel sheets',
  parsingTheScheduleFileStarted: 'Parsing the Schedule file started',
  readingScheduleFile: 'Reading schedule file',
  extractingScheduleData: 'Extracting schedule data',
  xEntriesInTheFile: (x: number) =>
    `${x} entr${ending(x, 'y', 'ies')} in the file`,
  savingScheduleDataCollections: 'Saving schedule data collections',
  xOfYEntriesSaved: (x: number, y: number) =>
    `${x} of ${y} entr${ending(y, 'y', 'ies')} saved`,
  recalculatingStatusUpdates: 'Recalculating status updates',
  savingSchedule: 'Saving schedule',
  uploadingScheduleFailedAt: 'Uploading schedule failed at',
  uploadingScheduleDone: 'Uploading schedule done',
  seeGanttInStruxHub: `See Gantt in ${brandName}`,
  noActivitiesFound: 'No activities found',
  noPropertiesFoundForForm: 'No properties found for this form',
  noPropertiesMatchSearch: 'No properties match your search',
  continueToNextStep: 'Continue to next step',
  thereAreNoProjectsInProvidedFile: 'There are no projects in provided file',
  thereAreNoInstancesToDisplay: (instancesName: string = 'Instances') =>
    `There are no ${instancesName.toLowerCase()} to display`,
  scheduleUpdateSummary: 'Schedule Update Summary',
  current: 'Current',
  upload_noun: 'Upload',
  fileName: 'File Name',
  dateUploaded: 'Date Uploaded',
  uploadScheduleDescriptions: {
    warning: (delta: number) =>
      `The ${delta} ${activities(delta)} ` +
      `that ${ending(delta, 'is', 'are')} not present in the Updated schedule` +
      `will be removed from view. All content associated with ` +
      `th${ending(delta, 'is', 'ese')} ${delta} ` +
      `${activities(delta)} will be deleted ` +
      `from ${brandName} as well.`,
    chooseProject:
      'The current P6 xer file contains data from multiple Projects. ' +
      'Please select the Project to import. The data in this Project should ' +
      'correspond to those data contained in other sources ' +
      '(e.g. Procurement Plan, Team Member list).',
    chooseSheet:
      'The current files contains multiple sheets. ' +
      'Please select the sheet to import.',
    mergeData: 'Update current schedule.',
    mapFields: 'Please select which field will be used for each column',
  },
  activityId: 'Activity ID',
  activityUniqueId: 'Activity Unique ID',
  activityName: 'Activity Name',
  activityCode: 'Activity Code',
  activityStatus: 'Activity Status',
  activityPercentComplete: 'Activity Percent Complete',
  plannedActivityStart: 'Planned Activity Start',
  plannedActivityFinish: 'Planned Activity Finish',
  actualActivityStart: 'Actual Activity Start',
  actualActivityFinish: 'Actual Activity Finish',
  resourceId: 'Resource ID',
  resourceName: 'Resource Name',
  trade: 'Trade',
  assignee: 'Assignee',
  primarySafetyHazard: 'Primary Safety Hazard',
  secondarySafetyHazard: 'Secondary Safety Hazard',
  categoryOfVariance: 'Category Of Variance',
  categoryOfVarianceOpened: 'Category Of Variance Opened',
  activityCodeTypes: 'Activity Code Types',
  resources: 'Resources',
  uploadAnother: 'Upload Another',
  next: 'Next',
  saveAndViewUpdatedGantt: 'Save and View Updated Gantt',
  lastUpload: 'Last upload',
  companies: 'Companies',
  targetCompany: 'Target Company',
  activitiesWithTheFollowingActivityCodesAreExcluded:
    'Activities with the following activity codes are excluded',
  activityCodesSelected: 'Activity Codes selected',
  scheduleRestoringConfirmation: 'Schedule Restoring Confirmation',
  doYouConfirmRestoringFileFromDate: (file: string, wrappedDate) => [
    `Do you confirm restoring ${file} from `,
    wrappedDate,
    '?',
  ],
  errorSendingOneTimeCode:
    'Error sending SMS. Please try again. If the issue persists, please contact an administrator.',
  noNonsenseInOneTimeCodeResponse:
    'Phone number not found. Please enter your phone number complete with area code.',
  redirectingYouTo: (x: string) => `Redirecting you to ${x}...`,
  doYouWantToSaveChanges: 'Do you want to save changes?',
  doYouAlreadyHaveAnAccount: 'Do you already have an account?',
  uploadHistory: 'Upload History',
  restore: 'Restore',
  chooseTenant: 'Choose Tenant',
  chooseAnotherTenant: 'Choose another tenant',
  unableToReachTenant: 'Unable to reach tenant',
  couldNotOpenTenant: (tenant: string) =>
    `We couldn't open ${tenant}. You may not have access, or the request timed out. Choose another tenant or sign out and try again.`,
  theSelectedTenant: 'the selected tenant',
  chooseProject: 'Choose Project',
  chooseSheet: 'Choose Sheet',
  mapFields: 'Map Fields',
  xActivities: (x: number) => `${x} ${activities(x)}`,
  xHours,
  locationsWithDeletedParent: (x: number): string =>
    x === 1
      ? '1 Location lost its LBS Parent'
      : `${x} Locations lost their LBS Parent`,
  locationsWithDeletedParentDescription: (x: number): string =>
    x === 1
      ? "Its LBS Parent was deleted before Oct 2026, so it doesn't appear in the Maps sidebar. Review it to delete or reparent it."
      : "Their LBS Parent was deleted before Oct 2026, so they don't appear in the Maps sidebar. Review them to delete or reparent each one.",
  reviewLocations: (x: number): string => `Review ${x} Location${ending(x)}`,
  shownInTableBelow: 'Shown in the table below',
  xLocations: (x: number) => `${x} Location${ending(x)}`,
  xObjectsHere: (x: number) => `${x} object${ending(x)} here`,
  xObjectsAtThisSpot: (x: number, names: string[]) =>
    `${x} object${ending(x)} at this spot: ${names.join(', ')}`,
  workflowsHereX: (x: number) => `Workflows here (${x})`,
  xAttachments: (x: number) => `${x} Attachment${ending(x)}`,
  xColumns: (x: number) => `${x} column${ending(x)}`,
  cancelUpload: 'Cancel Upload',
  selectProjectMembersLinkedToX: (x: string, isPlural: boolean) =>
    `Select Project Member${isPlural ? 's' : ''} linked to ${x}`,
  selectActivityCodesLinkedToX: (x: string, isPlural: boolean) =>
    `Select Activity Code${isPlural ? 's' : ''} linked to ${x}`,
  selectionOf1ProjectMemberIsPermitted: `Selection of 1 Project Member is permitted.`,
  selectionOf1ActivityCodeIsPermitted: `Selection of 1 Activity Code is permitted.`,
  multipleSelectionsArePermitted: `Multiple selections are permitted.`,
  excludeSectionsOfTheScheduleOptional:
    'Exclude sections of the schedule (optional)',
  activityFilterDialogDescriptions: {
    hiddenCodes:
      'If you wish to hide certain sections of your schedule, ' +
      'check the activity codes that correspond to the activities that you want to hide.',
  },
  only: 'Only',
  noCompany: 'No company',
  noMessage: 'No message',
  submit_verb: 'Submit',
  showAllXActivities: (x: number) => `Show All ${x} ${activities(x)}`,
  showXOfYActivities: (x: number, y: number) =>
    `Show ${x} of ${y} ${activities(x)}`,
  verification: 'Verification',
  xActivitiesAdded: (x: number, wrappedX) => [
    wrappedX,
    ` ${activities(x)} added.`,
  ],
  seeDetails: 'See details',
  xTypeActivityCodesAdded: (x: number, wrappedX, type: string) => [
    wrappedX,
    `${type} Activity Code${ending(x)} added.`,
  ],

  activityFilters: 'Activity filters',
  scheduleFilters: 'Schedule filters',
  customFilter: 'Custom filter',
  filterNameCannotBeEmpty: 'Filter name cannot be empty',
  suchFilterNameIsAlreadyTaken: 'Such filter name is already taken',
  struxHubData: `${brandName} Data`,
  scheduleData: 'Schedule Data',
  members: 'Members',
  newUserCreatedViaSignUp: 'New user sign-up',
  newUserAwaitingApproval: 'Awaiting approval',
  projectMembers: 'Project Members',
  activityCodes: 'Activity Codes',
  projectMembersSelected: 'Project Members selected',
  renameFilterOptional: 'Rename Filter (optional)',
  filterName: 'Filter name',
  xCompaniesIdentified: (x: number) =>
    `${x} Compan${ending(x, 'y', 'ies')} identified`,
  xCompaniesShowing: (x: number) =>
    `${x} Compan${ending(x, 'y', 'ies')} showing`,
  xCompaniesCount: (x: number) => `${x} Compan${ending(x, 'y', 'ies')}`,
  xCompanies: (x: number) => `${x} compan${ending(x, 'y', 'ies')}`,
  activityCodeSelected: 'Activity Code selected',
  responsibleUser: 'Responsible User',
  responsibilityCode: 'Responsibility Code',
  responsibilityDescription: 'Responsibility Description',
  contractN: 'Contract #',
  plannedValueS: 'Planned Value ($)',
  uploadAnUpdatedXlsxFile: 'Upload an updated xlsx file',
  downloadCurrentListAsXlsxFile: 'Download current list as xlsx file',
  addNewEntry: 'Add new entry',
  addNewUser: 'Add new user',
  previous: 'Previous',
  activityFiltersDescriptions: {
    confirmDeletionOfX: (x: string) =>
      `Are you sure you want to delete ${x} filter?`,
    title:
      `${brandName} transforms your activity codes and P6 data into convenient ` +
      'filters for your teams to easily find their activities on web and ' +
      'mobile. You can configure these filters below and update them at any time.',
    selectProjectMembers:
      'Select the Project Member(s) you want to include in this filter. ' +
      'Multiple selections are permitted.',
    selectActivityCodes:
      'Select the Activity Code(s) you want to include in this filter. ' +
      'Multiple selections are permitted.',
    company:
      'This filter allows users to select ' +
      'the Companies that are responsible for Activities in your Project.',
    level:
      'This filter allows users to select ' +
      'the elevation levels of your building.',
    zone:
      'This filter allows users to select ' +
      'the different zones of each level of the building.',
    activityCode:
      'This filter allows users to select ' +
      'additional codes of Activities that you want to track.',
    projectMember:
      'This filter allows users to select ' +
      'project members that you want to track.',
    associateCompanies:
      'Please indicate how you are associating Companies ' +
      'to your Activities in the schedule.',
    resourceCompanies:
      'Company names are in the Resource field in the .xer file. ' +
      'Note: this is the default value if you have a resource-loaded schedule.',
    bpCompanies:
      'Company names are in a separate xlsx file and ' +
      'associated with Activities via an Activity code.',
    bpStep1:
      'Select the Activity code type in your .xer file ' +
      'that is associated with the responsible Companies.',
    bpStep2:
      'Upload an excel file containing at least the following 2 column headers ' +
      'Responsibility Code and Company.',
  },

  trackingHierarchy: 'Tracking Hierarchy',
  defaultHierarchies: 'Default Hierarchies',
  defineBandsEnPermissionsForYourTrackingHierarchy:
    'Define bands & permissions for your Tracking Hierarchy',
  setupDefaultHierarchiesForDifferentViews:
    'Setup default hierarchies for different views',
  selectBands: 'Select bands',
  selectControlBand: 'Select Control Band',
  band: 'Band',
  addBand: 'Add Band',
  activityUpdatesOn: 'Activity updates on',
  activityUpdatesOff: 'Activity updates off',
  view_noun: 'View',
  wbs: 'WBS', // work breakdown structure
  webGantt: 'Web Gantt',
  webSitemap: 'Web Sitemap',
  mobile: 'Mobile',
  table: 'Table',
  tablet: 'Tablet',
  hierarchyPreview: 'Hierarchy Preview',
  unassigned: 'Unassigned',
  photo: 'Photo',
  completed: 'Completed',
  startEarly: 'Start Early',
  PComplete: '% Complete',
  unassignedActivity: 'Unassigned activity',
  trackingHierarchyDescriptions: {
    title:
      'Setup your Tracking Hierarchy below to start tracking your schedule ' +
      'by your Field Teams, by your Subcontractors, or by both.',
  },
  editCompany: 'Edit Company',
  addDataToSchedule: 'Add data to schedule',
  plannedCompanyManpower: 'Planned Company Manpower',
  dataToScheduleDescriptions: {
    title:
      `With ${brandName}, you can quickly add data to ` +
      'your schedule by importing data in excel files',
  },

  formField: 'Form field',
  showField_question: 'Show field?',
  mandatory_question: 'Mandatory?',
  fieldShown: 'Field shown',
  mandatory: 'Mandatory',
  addNewListElement: 'Add new list element',
  workflowPreview: 'Workflow preview',

  setThresholdForDDays: 'Set Threshold for ∆ Days',
  setThresholdForDManpower: 'Set Threshold for ∆ Manpower',
  onTrack: 'On-Track',
  watch: 'Watch',
  behind: 'Behind',
  dDays: '∆ Days',
  days: 'Days',
  percent: 'Percent',
  number: 'Number',
  daysBehind: 'Days Behind',
  daysAhead: 'Days Ahead',
  XBehind: '% Behind',
  XAhead: '% Ahead',
  under: 'Under',
  over: 'Over',
  XUnder: '% Under',
  XOver: '% Over',
  dManpower: '∆ Manpower',
  sitemapsDescriptions: {
    title:
      `With ${brandName}, you can easily link your Schedule with your sitemaps. ` +
      'Below you can configure the threshold values for the ∆ Days and ' +
      '∆ Manpower metrics that are used to visualize progress on the Schedule ' +
      'and current Manpower, respectively.',
    configure: 'Configure Status Thresholds for Schedule & Manpower',
  },

  inbox: 'Inbox',
  unread_2form: 'Unread',
  read_2form: 'Read',
  readStatus: 'Notification Status',
  archived: 'Archived',
  xArchived: (x: number) => `${x} archived`,
  xMarkedAsRead: (x: number) => `${x} marked as read`,
  xMarkedAsUnread: (x: number) => `${x} marked as unread`,
  xUnarchived: (x: number) => `${x} unarchived`,
  requested: 'Requested',
  changed: 'Changed',
  scheduled: 'Scheduled',
  onSite: 'On Site',
  denied: 'Denied',
  passedInspection: 'Passed Inspection',
  failedInspection: 'Failed Inspection',
  done_plural: 'Done',
  flags: 'Flags',
  workflowSubmitted: 'Workflow submitted',
  workflowApproved: 'Workflow approved',
  workflowDone: 'Workflow done',
  workflowChanged: 'Workflow changed',
  workflowDenied: 'Workflow denied',
  workflowReadyToInspect: 'Workflow ready to inspect',
  workflowActivated: 'Workflow activated',
  workflowReviewed: 'Workflow reviewed',
  workflowOnSite: 'Workflow on-site',
  workflowFailed: 'Workflow failed',
  workflowPassed: 'Workflow passed',
  workflowDeleted: 'Workflow deleted',
  workflowEnded: 'Workflow ended',
  workflowFollowed: 'Workflow followed',
  rfis: 'RFIs',
  scheduleComments: 'Schedule comments',
  onTime: 'On-Time',
  late: 'Late',
  performance: 'Performance',
  parameters: 'Parameters',
  onTimeWindowHr: 'On-time Window (hr)',
  showLess: 'Show less',
  showMore: 'Show more',
  all_items: 'All',
  my_documents: 'My Documents',
  my_company: 'My Company',
  maps: 'Maps',
  none: 'None',
  all: 'All',
  important: 'Important',
  importantTabTooltip:
    'Notifications that require your attention or action—includes ball-in-court, site communication, and @-mentions.',
  updates: 'Updates',
  updatesTabTooltip:
    'Workflow updates not marked as Important — informational only.',
  batteryLevel: 'Battery Level',
  struxTagState: 'StruxTag State',
  sensorStateInUseMoving: 'Moving',
  sensorStateInUseIdle: 'Idle',
  sensorStateNoSignal: 'No Signal',
  noStruxTagLinked: 'No StruxTag linked',
  noActivitySessions: 'No activity sessions',
  activitySessionMoving: 'Moving',
  activitySessionIdle: 'Idle',
  activitySessionOpen: 'Open',
  activitySessionClosed: 'Closed',
  unArchive: 'Unarchive',
  archive: 'Archive',
  leaveConversation: 'Leave conversation',
  archiveConversation: 'Archive conversation',
  unarchiveConversation: 'Unarchive conversation',
  conversationIsArchived: 'This conversation is archived',
  photoMessage: '📷 Photo',
  thisWorkflowIsInProject: (projectName: string) =>
    `This workflow is in ${projectName}`,
  leaveConversationConfirmation:
    'Are you sure you want to leave this conversation? You will lose access to it.',
  archiveConversationConfirmation:
    'Are you sure you want to archive this conversation? It will be hidden for all members and become read-only.',
  conversation: 'Conversation',
  reconnecting: 'Reconnecting…',
  noMessagesYet: 'No messages yet',
  noMessagesYetSayHello: 'No messages yet — say hello…',
  couldNotLoadYourInbox: 'Could not load your inbox',
  couldNotLoadMessages: 'Could not load messages',
  couldNotOpenConversation:
    'Could not open the conversation — check your connection',
  noArchivedConversations: 'No archived conversations',
  noConversationsFound: 'No conversations found',
  inboxIsEmpty: 'Inbox is empty',
  xMembersYOnline: (members: number, online: number) =>
    `${members} members, ${online} online`,
  xMembers: (x: number) => `${x} member${ending(x)}`,
  failedToLeave: 'Failed to leave',
  leave_verb: 'Leave',
  message_noun: 'Message',
  youCanAttachUpToXPhotos: (x: number) =>
    `You can attach up to ${x} photos to a message.`,
  xFilesCouldNotBeAttached: (x: number) =>
    `${x} file${ending(x)} could not be attached — images up to 15 MB only.`,
  notSentTapToRetry: 'Not sent — tap to retry',
  newChat: 'New Chat',
  searchUser: 'Search user',
  newGroup: 'New Group',
  noPeopleFound: 'No people found',
  addMembers: 'Add Members',
  groupDetails: 'Group Details',
  userDetails: 'User Details',
  groupName: 'Group name',
  createdByX: (name: string) => `Created by ${name}`,
  you: 'You',
  couldNotCreateGroup: 'Could not create the group — try again',
  couldNotUpdateGroup: 'Could not update the group — try again',
  read: 'Read',
  unread: 'Unread',
  wwpColoring: 'WWP Coloring',
  useCSIMaterialCategories: 'Use CSI Material Categories', // Construction Specifications Institute
  noNotificationsPassedFilters: 'No Notifications passed filters',
  replyToActivityFlag: 'Reply to Activity Flag',
  activityFlag: 'Activity Flag',
  replyToActivityRfi: 'Reply to Activity RFI',
  activityRfi: 'Activity RFI',
  replyToScheduleComment: 'Reply to Schedule comment',
  replyToCategoryOfVariance: 'Reply to Category Of Variance',
  replyToSafetyHazard: 'Reply to Safety Hazard',
  scheduleComment: 'Schedule comment',
  activityUpdate: 'Activity Update',
  scheduleUpdate: 'Schedule Update',
  safetyHazard: 'Safety Hazard',
  safetyHazardOpened: 'Safety Hazard Opened',
  bulk: 'Bulk',
  bulkEdit: 'Bulk Edit',
  bulkUpdate: 'Bulk Update',
  bulkEditConfirmQuestion: (fieldCount: number, recordCount: number) =>
    `Update ${xFields(fieldCount)} for ${recordCount} record${ending(
      recordCount,
    )}?`,
  replyToActivityUpdate: 'Reply to Activity Update',
  activityFollowed: 'Activity Followed',
  deliveryFollowed: 'Delivery Followed',
  replyToScheduleUpdate: 'Reply to Schedule Update',
  replyToBulkUpdate: 'Reply to Bulk Update',
  deliveryRequested: 'Delivery requested',
  deliveryRequestedByLocationClosure: (id: string) =>
    `Delivery (${id}) has been affected by a Location Closure and must be re-scheduled`,
  deliveryScheduled: 'Delivery scheduled',
  deliveryOnSite: 'Delivery on site',
  deliveryPassedInspection: 'Delivery passed inspection',
  deliveryFailedInspection: 'Delivery failed inspection',
  deliveryInspected: 'Delivery Inspected',
  deliveryDone: 'Delivery done',
  deliveryIncompletelyDone: 'Delivery incompletely done',
  deliveryUpdated: 'Delivery updated',
  deliveryDeleted: 'Delivery deleted',
  deliveryDeletedBy: (id: string, user: string, company: string) =>
    `Delivery (${id}) has been deleted by ${user} at ${company}`,
  deliveryDenied: 'Delivery denied',
  deliveryCanceled: 'Delivery Canceled',
  deliveryOnHold: 'Delivery on hold',
  deliveryPaused: 'Delivery paused',
  updateMaterials: 'Update materials',
  materialsLoading: 'Materials are loading',
  xMaterials: (count: number) => `${count} Materials`,
  xFilesShowing: (count: number) => `${count} Files showing`,
  planned: 'Planned',
  actual: 'Actual',
  scannerDeactivated: 'Scanner deactivated',
  scheduleUpdated: 'Schedule updated',
  passed: 'Passed',
  failed: 'Failed',
  scheduling: 'Scheduling',
  schedule: 'Schedule',
  schedule_noun: 'Schedule',
  pass_verb: 'Pass',
  deny_verb: 'Deny',
  change_verb: 'Change',
  accept: 'Accept',
  fail: 'Fail',
  incompletelyDone: 'Incompletely done',
  deleted: 'Deleted',
  deactivated: 'Deactivated',
  activate: 'Activate',
  deactivate: 'Deactivate',
  canceled: 'Canceled',
  siteNotReady: 'Site not ready',
  materialProblem: 'Material problem',
  laborIssue: 'Labor issue',
  qualityIssue: 'Quality issue',
  clarificationNeeded: 'Clarification needed',
  incorrectSpecification: 'Incorrect specification',
  materialQuestion: 'Material question',
  superintendentNeeded: 'Superintendent needed',
  plusXMore: (x: number) => `+ ${x} more`,
  submittalInReview: 'Submittal in review',
  submittalComplete: 'Submittal complete',
  inFabrication: 'In fabrication',
  fabricationComplete: 'Fabrication complete',
  inStorage: 'In storage',
  installed: 'Installed',
  delivery: 'Delivery',
  newRequest: 'New request',
  deleteDelivery: 'Delete Delivery',
  yesDeleteDelivery: 'Yes, delete delivery',
  cancelDelivery: 'Cancel Delivery',
  yesCancelDelivery: 'Yes, cancel delivery',
  changeDate: 'Change Date',
  yesMoveTheDate: 'Yes, move the date',
  requestIsNotValid: 'Request is not Valid',
  datePassedDeadline: 'Date Passed Deadline',
  requestAnyway: 'Request anyway',
  requestAccessTo: 'Request access to',
  thisRequestHasPassedTheDeadline: 'This Request has passed the Deadline',
  noDeadline: 'No Deadline',
  twentyFourHours: '24 hours',
  fortyEightHours: '48 hours',
  seventyTwoHours: '72 hours',
  fourDays: '4 days',
  fiveDays: '5 days',
  oneWeek: '1 week',
  twoWeeks: '2 weeks',
  oneMonth: '1 Month',
  twoMonths: '2 Months',
  threeMonths: '3 Months',
  sixMonths: '6 Months',
  twelveMonths: '12 Months',
  dateAndTime: 'Date & Time',
  startDate: 'Start Date',
  endDate: 'End Date',
  finishDate: 'Finish Date',
  addFinishDate: 'Add finish date',
  removeFinish: 'Remove finish',
  startTime: 'Start time',
  endTime: 'End time',
  nextDay: 'next day',
  selectTime: 'Select time',
  location: 'Location',
  locations: 'Locations',
  locationFrom: 'Location From',
  locationTo: 'Location To',
  formType: 'Workflow Type',
  title: 'Title',
  noAvailableTemplates: 'No available templates',
  editForm: 'Edit Workflow',
  contact: 'Contact',
  offloadingEquipment: 'Offloading Equipment',
  vehicles: 'Vehicles',
  material: 'Material',
  materialDeliveryLocation: 'Material delivery location',
  materialsCategory: 'Materials category',
  inspectionRequired: 'Inspection Required',
  supplierAndDriver: 'Supplier & Driver',
  anySpecialInstructions: 'Any special instructions?',
  deliverySitemap: 'Delivery Sitemap',
  gate: 'Gate',
  area: 'Area',
  fromToDelivery: 'From-To Delivery',
  installationZone: 'Installation Zone',
  deliveryVehicleType: 'Delivery Vehicle Type',
  numberOfVehicles: 'Number of Vehicles',
  numberOfVehicleLicensePlate: 'Vehicle Model & Lic.#',
  equipment: 'Equipment',
  materialResource: 'material resource',
  struxTags: 'StruxTags',
  registerStruxTag: 'Register StruxTag',
  register: 'Register',
  registering: 'Registering...',
  infuseId: 'Infuse ID',
  infuseIdIsRequired: 'Infuse ID is required',
  infuseIdMustNotContainSpaces: 'Infuse ID must not contain spaces',
  failedToRegisterStruxTag: 'Failed to register StruxTag',
  sharedResources: 'Shared Resources',
  units: 'Units',
  inspector: 'Inspector',
  vendorEmails: 'Vendor emails',
  driverMobilePhoneNumbers: 'Driver(s) mobile phone number(s)',
  specialInstructions: 'Special Instructions',
  linkedActivity: 'Linked Activity',
  customActionButton: 'Custom Action Button',
  building: 'Building',
  route: 'Route',
  staging: 'Staging',
  interiorPath: 'Internal Path',
  interiorDoor: 'Internal Door',
  bookingDate: 'Booking Date',
  requesterCompany: 'Requester Company',
  duration: 'Duration',
  onsiteContact: 'On-site Contact',
  onsiteContacts: 'On-site Contacts',
  category: 'Category',
  materialNote: 'Material Note',
  unnamedMaterial: 'Unnamed material',
  unnamedCompany: 'Unnamed company',
  required: 'Required',
  optional: 'Optional',
  multipleSelection: 'Multiple selection',
  adding: 'Adding',
  findOrAddNewEntry: 'Find or Add new entry',
  findEntry: 'Find entry',
  yes: 'Yes',
  no: 'No',
  unscheduledDelivery: 'Unscheduled Delivery',
  uploadASitemapToShowGatesAndAreas: 'Upload a sitemap to show Gates and Areas',
  uploadAViewToShowGatesAndAreas: 'Upload a view to show Gates and Areas',
  uploadASitemapToShowBuilding: 'Upload a sitemap to show Building',
  buildingAccessMap: 'Building Access Map',
  uploadASitemapToShowGates: 'Upload a sitemap to show Gates',
  gateAccessMap: 'Gate Access Map',
  uploadASitemapToShowEquipment: 'Upload a sitemap to show Equipment',
  equipmentAccessMap: 'Equipment Access Map',
  zoneAccessMap: 'Zone Access Map',
  uploadASitemapToShowAreas: 'Upload a sitemap to show Areas',
  routeAccessMap: 'Route Access Map',
  uploadASitemapToShowRoute: 'Upload a sitemap to show Route',
  deliveryWorkflow: 'Delivery Workflow',
  workflowMenu: 'Workflow Menu',
  addANoteToThisDelivery: 'Add a note to this Delivery',
  send: 'Send',
  deliveryDescriptions: {
    confirmDelete:
      'Deleting this delivery will remove the delivery ' +
      'and all associated notes and data from the project.',
    confirmDelete2: 'Do this only if the Delivery Request was a mistake.',
    confirmCancel: `Canceled deliveries will remain on the calendar but be marked as 'Canceled'.`,
    confirmCancel2: 'Would you like to Cancel this delivery?',
    nonWorkTimeDateChange: (days: number) =>
      `Please move this Delivery. Requests must be ${days} working ` +
      `day${ending(days)} ` +
      `in Advance from the date and time you are requesting and on a work date-time`,
    maximumDeliveriesAreScheduled:
      'Maximum number of deliveries are already scheduled for this location',
  },
  loginInfoPageDescriptions: {
    requestSentTitle: 'Request sent',
    requestApprovedTitle: 'Request approved',
    invalidInvitationTitle: 'Invalid invitation link',
    somethingWentWrongTitle: 'Something went wrong',
    accountRequestPendingEmail:
      "The administrator is reviewing your request. You'll receive an invitation email as soon as it's approved. Check your mailbox.",
    accountRequestPendingSms:
      "The administrator is reviewing your request. You'll receive an invitation link by SMS as soon as it's approved.",
    accessRequestPending:
      "The administrator is reviewing your request. You'll receive an invitation as soon as it's approved.",
    accountRequestApproved:
      'An invitation email has been sent to your mailbox. To log in, follow the instructions in it.',
    goToAvailableProject: 'Go to an available Project',
    invalidInvitationDescription:
      'This invitation link is incomplete or damaged. Open it again from your invitation, or ask your administrator to send a new one.',
    somethingWentWrongDescription:
      "We couldn't complete your request. Try again later, or contact support if the problem persists.",
    expiredInvitationTitle: 'Your invitation has expired',
    expiredInvitationDescription: (hours: number) =>
      `For security reasons, invitation links expire. Resend the invitation to get a new link, valid for ${xHours(hours)}.`,
    resendMyInvitationLink: 'Resend my invitation link',
    invitationResentTitle: 'Invitation resent',
    invitationResentDescription: 'Check your email and follow the new link.',
  },

  ssoAdminConsentPage: {
    grantedTitle: 'Admin consent granted',
    grantedDescription: `Microsoft single sign-on is now enabled for your organization. Your team can sign in to ${brandName} with their Microsoft accounts.`,
    notCompletedTitle: `Consent wasn't completed`,
    notCompletedDescription: `Admin consent wasn't granted. You can try the consent link again, or contact ${brandName} support.`,
    continueToApp: `Continue to ${brandName}`,
    backToApp: `Back to ${brandName}`,
    directory: 'Directory',
  },

  assignResponsibilitiesConfirmation: 'Assign Responsibilities Confirmation',
  assignResponsibilities: 'Assign Responsibilities',
  assignToActivities: 'Assign to Activities',
  groupByTeam: 'Group by Team',
  noTeam: '[no team]',
  hashActivities: '# Activities',
  rfi: 'RFI',
  flag: 'Flag',
  reply: 'Reply',
  unspecifiedType: '(Unspecified type)',
  contractsOrRevisions: 'Contracts/Revisions',
  priorWorkNotComplete: 'Prior Work Not Complete',
  informationNotAvailable: 'Information Not Available',
  unclearTaskDescription: 'Unclear Task Description',
  staffNotAvailable: 'Staff Not Available',
  materialsNotAvailable: 'Materials Not Available',
  noInformationAvailableOnThisMonitoring:
    'No information is available on this monitoring',
  conditionsOfSatisfactions: 'Conditions of Satisfactions',
  taskSequenceChange: 'Task Sequence Change',
  incorrectTimeEstimate: 'Incorrect Time Estimate',
  offProjectDemands: 'Off Project Demands',
  unforeseenConditions: 'Unforeseen Conditions',
  weather: 'Weather',
  weatherDescriptions: {
    'clear sky': 'Clear Sky',
    'few clouds': 'Few Clouds',
    'scattered clouds': 'Scattered Clouds',
    'overcast clouds': 'Overcast Clouds',
    'broken clouds': 'Broken Clouds',
    'light rain': 'Light Rain',
    'heavy intensity rain': 'Heavy Rain',
    'light snow': 'Light Snow',
    'heavy snow': 'Heavy Snow',
    'shower sleet': 'Shower Sleet',
    'freezing rain': 'Freezing Rain',
    'very heavy rain': 'Very Heavy Rain',
    thunderstorm: 'Thunderstorm',
    snow: 'Snow',
    mist: 'Mist',
  },
  humidity: 'humidity',
  precipitation: 'precipitation',
  temperatureRange: {
    high: 'High',
    low: 'Low',
    avg: 'Avg.',
  },
  fiveDayForecast: '5-Day Forecast',
  elevatedWork: 'Elevated Work',
  materialHandling: 'Material Handling',
  groundDistribution: 'Ground Distribution',
  craneOrHeavyLift: 'Crane / Heavy Lift',
  confinedSpace: 'Confined Space',
  mobileEquipment: 'Mobile Equipment',
  deliver: 'Deliver',
  loto: 'LOTO',
  hotWork: 'Hot Work',
  hazardousMaterial: 'Hazardous Material',
  highVoltage: 'High Voltage',
  highDecibels: 'High Decibels',
  lifting: 'Lifting',
  closeQuarters: 'Close Quarters (<6ft)',
  rfiOpened: 'RFI Opened',
  flagOpened: 'Flag Opened',
  scheduleCommentOpened: 'Schedule comment opened',
  addANote: 'Add a note',
  addToLog: 'Add to log',
  emojiPicker: 'Emoji picker',
  addPhotoFromLibrary: 'Add Photo From Library',
  addFile: 'Add file',
  atMentionAUser: 'At-mention a user',
  atMentionUserDescription:
    'To notify and add a user as an Additional Contact to the workflow',
  start_noun: 'Start',
  end_noun: 'End',
  forDate: (date: string) => `For ${date}`,
  addANoteToThisStatusUpdate: 'Add a note to this Status Update',
  chooseADeliveryTime: 'Choose a delivery time',
  allZones: 'All Zones',
  allEquipment: 'All Equipment',
  selectDate: 'Select date',
  selectMonth: 'Select month',
  selectDateRange: 'Select date range',
  selectField: 'Select field',
  selectDateAndTime: 'Select date & time',
  jumpToDate: 'Jump to date',
  jumpToMap: 'Jump to map',
  newDelivery: 'New Delivery',
  newForm: 'New Workflow',
  pending: 'Pending',
  confirmChange: 'Confirm Change',
  okToProceed: 'OK to proceed?',
  proceed: 'Proceed',
  actualStart: 'Actual Start',
  updated: 'Updated',
  approved: 'Approved',
  pendingApproval: 'Pending approval',
  missing: 'Missing',
  actualFinish: 'Actual Finish',
  xDaysUpdated: (x: number) => `${x} Days Updated!`,
  copyToMissing: 'Copy to Missing',
  copiedToClipboard: 'Copied to clipboard',
  traceId: 'Trace ID',
  copyTraceId: 'Copy Trace ID',
  copyLinkToDelivery: 'Copy link to delivery',
  copyLink: 'Copy link',
  enabled: 'Enabled',
  disabled: 'Disabled',
  checkInUnscheduledDelivery: 'Check-in unscheduled delivery',
  paste: 'Paste',
  estimateMissing: 'Estimate Missing',
  activityDetails: 'Activity Details',
  assigned: 'Assigned',
  followed: 'Followed',
  following: 'Following',
  notFollowing: 'Not following',
  predecessors: 'Predecessors',
  successors: 'Successors',
  viewTeam: 'View team',
  dates: 'Dates',
  plannedStart: 'Planned Start',
  plannedEnd: 'Planned End',
  actualEnd: 'Actual End',
  activityLog: 'Activity Log',
  when: 'When',
  date: 'Date',
  time: 'Time',
  where: 'Where',
  min_shortMinutes: 'min',
  hr_shortHours: 'hr',
  requester: 'Requester',
  responsible: 'Responsible',
  subscriber: 'Subscriber',
  directAssignments: 'Direct Assignments',
  otherSubmissions: 'Other Submissions',
  onsiteContactPerson: 'On-site Contact Person',
  onsiteContactPersons: 'On-site Contact Persons',
  vehicle: 'Vehicle',
  quantity: 'Quantity',
  inspection: 'Inspection',
  thisDeliveryWasNotScheduledInAdvance:
    'This delivery was not scheduled in advance',
  thisDeliveryWasScheduledForNonWorkTime:
    'This delivery was scheduled for a non-work day or time',
  sitemap: 'Sitemap',
  flagAnIssue: 'Flag an issue',
  openAnRfi: 'Open an RFI',
  requestADelivery: 'Request a Delivery',
  reason: 'Reason',
  addReasonForFlag: 'Add reason for flag',
  addReasonForRfi: 'Add reason for RFI',
  addReasonForCategoryOfVariance: 'Add reason for Category Of Variance',
  addReasonForSafetyHazard: 'Add reason for Safety Hazard',
  addANoteToThisActivityLog: 'Add a note to this Activity log',
  uploadingImage: 'Uploading Image',
  ganttDescriptions: {
    assignXUsersToYActivities: (x: number, y: number) =>
      `Assigning the following ${x} Team member${ending(x)} to ` +
      `${y} selected ${activities(y)}`,
  },
  actualDatesDescriptions: {
    newStartDateAfterOld: (
      newDate: string,
      oldDate: string,
      wrapper: (x: string) => any,
    ) => [
      'Making ',
      wrapper(newDate),
      ' the Actual Start will unmark ',
      wrapper(oldDate),
      ` as the Actual Start and will ignore all updates before ${newDate}.`,
    ],
    newStartDateBeforeOld: (
      newDate: string,
      oldDate: string,
      wrapper: (x: string) => any,
    ) => [
      'Making ',
      wrapper(newDate),
      ' the Actual Start will unmark ',
      wrapper(oldDate),
      ' as the Actual Start and will preserve all associated data.',
    ],
    newEndDateAfterOld: (
      newDate: string,
      oldDate: string,
      wrapper: (x: string) => any,
    ) => [
      'Making ',
      wrapper(newDate),
      ' the Actual Finish will unmark ',
      wrapper(oldDate),
      ' as the Actual Finish and will ignore its % Complete value.',
    ],
    newEndDateBeforeOld: (
      newDate: string,
      oldDate: string,
      wrapper: (x: string) => any,
    ) => [
      'Making ',
      wrapper(newDate),
      ' the Actual Finish will unmark ',
      wrapper(oldDate),
      ` as the Actual Finish and will ignore all updates after ${newDate}.`,
    ],
  },

  offloadingEquipmentMaps: (x: number) =>
    `Offloading equipment map${ending(x)}`,

  filterOptions: 'Filter options',
  selectOptions: 'Select Options',
  filterByLBS: 'Filter by LBS',
  auto: 'auto',

  level: 'Level',
  zone: 'Zone',
  siteLocation: 'Site location',
  verticalObject: 'Vertical object',
  manpower: 'Manpower',
  manPower: 'Manpower',
  description: 'Description',
  code: 'Code',
  codeVerifying: 'Code is being verified',
  codeResending: 'Code is being resent',
  resendCode: 'Resend Code',
  contract: 'Contract',
  plannedValue: 'Planned Value',
  bidStatus: 'Bid Status',

  street: 'Street',
  satellite: 'Satellite',
  layers: 'Layers',
  terrain: 'Terrain',
  transit: 'Transit',
  traffic: 'Traffic',
  more: 'More',
  mapType: 'Map Type',
  mapSettings: 'Map Settings',
  mapDetails: 'Map Details',
  overlays: 'Overlays',
  labelsDisplay: 'Labels Display',

  allowTextAndIconToScale: 'Allow text and icon to scale',
  allowLabelsToOverlap: 'Allow labels to overlap',
  showWorkflowPinLabels: 'Show workflow pin labels',
  workflowMarkups: 'Workflow Markups',
  showForMe: 'Show for me',
  showAssociationLabels: 'Show association labels',
  showWorkflowMarkupsForEveryone:
    'Show for everyone in this project by default',
  showAdjacent: 'Show adjacent',
  markupsShortcutHint: 'toggles on/off',
  clickAgainToEditPoints: 'Click again to edit points',
  longPressToRemovePoint: 'Long-press a point to remove it',
  finishShaping: 'Finish shaping',
  discardShape: 'Discard shape',
  finishLine: 'Finish line',
  discardLine: 'Discard line',
  labels: 'Labels',
  threeDBuildings: '3D Buildings',
  resetViewport: 'Reset Viewport',
  toggleProjection: 'Toggle Projection',
  moreOptions: 'More options',
  showPlan: 'Show Plan',
  showWorkflows: 'Show Workflows',

  version: 'Version',
  revision: 'Revision',
  build: 'Build',

  viewName: 'View name',
  viewImage: 'View image',
  shareToProject: 'Share to Project',
  create: 'Create',
  private: 'Private',
  listSections: {
    shown: 'Shown',
    hidden: 'Hidden',
  },

  deliveryIdNotProvided: 'Delivery ID not provided',
  sendingDeliveryTicket: 'Sending delivery ticket to driver',
  ticketSuccessfullySent: 'Ticket successfully sent',
  failedToSendTicket: 'Failed to send ticket',
  invalidDeliveryTicket: 'Invalid delivery ticket',
  deliveryDetails: 'Delivery Details',
  onSiteLogistics: 'On-site Logistics',

  showAtEntryGate: 'Show at Entry Gate',
  showAtX: (location: string) => `Show at ${location}`,
  deliveryTicket: 'Delivery Ticket',
  hasDelivery: 'has delivery',
  duplicate_verb: 'Duplicate',
  createFromToDelivery:
    'Create From-To delivery. Current delivery should be marked ≥ [Onsite]',
  duplicateCurrentDelivery: 'Duplicate current delivery',
  unsubscribeToDelivery: 'Unsubscribe to delivery',
  subscribeToDelivery: 'Subscribe to delivery',
  sharedDelivery: 'Shared Delivery',

  requiredFieldCannotBeEmpty: 'Required field cannot be empty',
  fieldShouldBeUnique: 'Field should be unique',
  pleaseCorrectErrors: 'Please correct errors',
  pleaseFillAllFields: 'Please fill all fields',

  companiesUpdated: 'Companies updated',
  companyAdded: 'Company added',

  forms: 'Forms', // Candidate for removal
  formTypes: 'Workflow types',

  legacy: 'Legacy',
  deliveryLocations: 'Delivery locations',
  cards: 'Cards',

  columns: 'Columns',
  struxhubLabs: `${brandName} labs`,

  mobileConfiguration: 'Mobile configuration',
  additionalDataUpload: 'Additional data upload',
  onTimeThresholds: 'On-time thresholds',
  qrScannersSetup: 'QR Code scanners setup',

  businessPhone: 'Business phone',
  businessEmail: 'Business email',
  responsibles: 'Responsibles',
  responsibleContact: 'Responsible Contact',
  dba: 'DBA',
  licenseNumber: 'License number',

  deliveryId: 'Delivery ID',
  bookingTime: 'Booking time',
  enterVerificationCodeFromSMS: 'Enter verification code from SMS',
  getDirections: 'Get directions',
  showDirectionsOnMap: 'Show Direction on Map',
  copyGoogleMapsLink: 'Copy Google Maps Link',
  copyLocationCoordinates: 'Copy Location Coordinates',
  moreActions: 'More actions',
  copyFailed: "Couldn't copy",

  userInviteMessages: {
    oneUserWasInvitedToProject: (userName: string) =>
      `${userName} was invited to the project`,
    manyUsersWereInvitedToProject: (count: number) =>
      `${xUsers(count)} were invited to the project`,
  },

  userEmailLinking: {
    linkYourEmail: 'Link your email',
    linkYourEmailAndSetPassword: 'Link your Email and set Password',
    intro: `You can also use your e-mail to log into ${brandName}. This step is optional.`,
    setEmailWithPassword: 'Please create a password.',
    weakPassword: 'Weak password. Check the valid format',
    emailHasBeenLinkedSuccessfully: 'Email has been linked successfully',
  },

  thereIsNoProject:
    'There is no project available for your user account. Please, contact administrator',
  noProjectsAvailableTitle: 'No projects available',

  repeatOn: 'Repeat on',

  operatorValues: {
    isNot: 'IS NOT',
    is: 'IS',
    are: 'ARE',
    and: 'AND',
    or: 'OR',
    for: 'FOR',
    assign: 'ASSIGN',
    if: 'IF',
  },

  any: 'Any',
  selectCompany: 'Select company',
  selectPermittedCompanies: 'Select Permitted Companies',
  selectLocation: 'Select location',
  approval: 'Approval',
  firewatch: 'Firewatch',
  noRules: 'No Rules',
  rule: 'Rule',

  xOfY: (x: number, y: number) => `${x} of ${y}`,
  options: 'Options',
  selectActivityGroups: 'Select Activity Groups',
  selectActionType: 'Select action type',
  numberOfBands: 'Number of Bands',
  lbs: 'LBS', // location breakdown structure
  lbsTag: 'LBS Tag',
  lbsTagTooltip:
    'LBS (Location Breakdown Structure) defines where a Resource is located within the Project. Select associated Resource for the Map View.',
  workBreakdownStructureWbs: 'Work breakdown structure (WBS)',
  locationBreakdownStructureLbs: 'Location breakdown structure (LBS)',
  groupByOptions: 'Group by options',
  showEmptyBandsLevels: 'Show empty bands levels',
  customGroupCreation: 'Custom Group Creation',
  bandX: (x: number) => `Band ${x}`,
  custom: 'Custom',
  groupByX: (x: string) => `Group by: ${x}`,

  bookingId: 'Booking ID',
  responsibleCompany: 'Responsible Company',
  bookingDateEnTimeOffset: (offset: string) =>
    `Booking Date & Time (${offset})`,
  durHmm_durationShort: 'Dur. (h:mm)',
  locationStatus: 'Location Status',
  closeLocations: 'Close Locations',
  closeResources: 'Close Resources',
  openLocations: 'Open Locations',
  openResources: 'Open Resources',
  selectFields: 'Select fields',
  selectProperty: 'Select property',
  addAction: 'Add Action',
  closureStart: 'Closure Start',
  closureFinish: 'Closure Finish',
  permittedCompany: 'Permitted Company',
  closureWorkflow: 'Closure Workflow',
  updateForm: 'Update Workflow',
  formStatus: 'Workflow Status',
  formStart: 'Workflow Start',
  formFinish: 'Workflow Finish',
  materialName: 'Material Name',
  productCategory: 'Product Category',
  deliveryMaterialNotes: 'Delivery Material Notes',
  vendor: 'Vendor',
  quantityOnsite: 'Quantity On-site',
  storageArea: 'Storage Area',
  requiredOnsiteDate: 'Required On-site Date',
  nextDelivery: 'Next Delivery',
  deliveryBookingDate: 'Delivery Booking Date',
  bookedQuantity: 'Booked quantity',
  recordLastUpdated: 'Record Last Updated',
  numObjects: '# Objects',
  showOn: 'Show on',
  noShow: 'No Show',
  moved: 'Moved',
  otherReason: 'Other Reason',
  mostPertinentDefault: 'Most pertinent (default)',
  zones: 'Zones',
  gates: 'Gates',
  routes: 'Routes',
  areas: 'Areas',
  levels: 'Levels',
  totalX: (x: number) => `Total ${x}`,
  deliveriesEnEquipmentNeedsPerHour: 'Deliveries & Equipment Needs per hour',
  resetFilters: 'Reset Filters',
  saved_activities: 'Saved',
  since: 'Since',
  lastP6Update: 'Last P6 update',
  lastWeek: 'Last week',
  lastMonth: 'Last month',
  last6Week: 'Last 6 week',
  dateRange: 'Date range',
  dateRangeForRecentlyUpdatedActivities:
    'Date range for recently updated activities',
  myTasks: 'My Tasks',
  recentlyUpdated_activities: 'Recently Updated',
  activitiesWithSpecificStatus: 'Activities with specific status',
  actualizedFromSchedule_activities: 'Actualized from Schedule',
  activitiesWithOpenScheduleComment: 'Activities with open Schedule Comment',
  notCompleted_activities: 'Not Completed',
  criticalPathActivities: 'Critical path Activities',
  flaggedActivities: 'Flagged Activities',
  lateActivities: 'Late Activities',
  activitiesWithOpenRFI: 'Activities with open RFI',
  activitiesWithDeliveries: 'Activities with Deliveries',
  numberOfActivities: 'Number of Activities',
  seeXActivities: (x: number) => `See ${x} ${activities(x)}`,
  type: 'Type',
  types: 'Types',
  assigners: 'Assigners',
  items: 'Items',
  projectMember: 'Project member',
  disableFilter: 'Disable filter',
  totalDeliveries: 'Total Deliveries',
  totalSitemaps: 'Total Sitemaps',
  totalForms: 'Total Workflows',
  totalAttachments: 'Total Attachments',
  totalReports: 'Total Reports',
  totalClosures: 'Total Closures',
  totalSensors: 'Total Sensors',
  seeXUsers: (x: number) => `See ${xUsers(x)}`,
  totalLogistics: 'Total Workflows',
  totalMaterials: 'Total Materials',
  seeXDeliveries: (x: number) => `See ${x} ${deliveries(x)}`,
  xPlans: (x: number) => `${x} ${plans(x)}`,
  seeXObjects: (x: number) => `See ${x} Object${ending(x)}`,
  seeXSitemaps: (x: number) => `See ${x} Sitemap${ending(x)}`,
  seeXForms: (x: number) => `See ${xForms(x)}`,
  seeXClosures: (x: number) => `See ${x} Closure${ending(x)}`,
  seeXSensors: (x: number) => `See ${x} Sensor${ending(x)}`,
  seeXLogistics: (x: number) => `See ${x} Workflow${ending(x)}`,
  seeXMaterials: (x: number) => `See ${x} Material${ending(x)}`,
  seeXReports: (x: number) => `See ${x} Report${ending(x)}`,
  seeXAttachments: (x: number) => `See ${x} Attachment${ending(x)}`,
  currentLookahead: 'Current lookahead',
  fullProject: 'Full Project',
  numberOfDeliveries: 'Number of Deliveries',
  numberOfWorkflows: 'Number of Workflows',
  savedFilters: 'Saved Filters',
  saved_deliveries: 'Saved',
  xDeliveries: (x: number) => `${x} ${deliveries(x)}`,

  xWorkflows: (x: number) => `${x} ${workflows(x)}`,
  savedActivitySets: 'Saved Activity Sets',
  thereAreNoSavedActivityPresets: 'There are no saved activity presets',
  xForms,
  xFilters: (x: number) => `${x} filter${ending(x)}`,
  thereAreNoSavedFilters: 'There are no saved filters',
  makePublic: 'Make Public',
  workPackageName: 'Work package name',
  selectedFilters: 'Selected filters',
  share: 'Share',
  shareWithLink: 'Share with link',
  saveCurrentFilters: 'Save current filters',
  saveANewCustomFilter: 'Save a new custom filter',
  editACustomFilter: 'Edit a custom filter',

  oneDay: 'One Day',
  oneDayDefaultToday: 'One Day Default Today',
  week: 'Week',
  fixedWeek: 'Fixed Week',
  fixedTwoWeeks: 'Fixed 2 Weeks',
  fixedThreeWeeks: 'Fixed 3 Weeks',
  threeWeeks: '3 Weeks',
  fourWeeks: '4 Weeks',
  fixedFourWeeks: 'Fixed 4 Weeks',
  month: 'Month',
  fixedSixWeeks: 'Fixed 6 Weeks',
  sixWeeks: '6 Weeks',
  threeMonth: '3 Months',
  year: 'Year',
  other: 'Other',

  xDateToYDate: (xDate: string, yDate: string) => `${xDate} to ${yDate}`,
  assignUsersOrCategories: 'Assign users or categories',
  searchUserCompanyTeamOrRole: 'Search User, Company, Team or Role',
  successfullyUpdatedExistingAssignments:
    'Successfully updated existing assignments',
  successfullyDeletedAssignments: 'Successfully deleted assignments',

  approve_verb: 'Approve',
  readyToInspect: 'Ready to inspect',
  toInspect: 'To inspect',
  notInUse: 'Not in use',
  everyXDaysInspection: (x: number) => `Every ${x} day${ending(x)} inspection`,
  everyXWeeksInspection: (x: number) =>
    `Every ${x} week${ending(x)} inspection`,
  everyXMonthsInspection: (x: number) =>
    `Every ${x} month${ending(x)} inspection`,
  addInspection: 'Add Inspection',
  finish_noun: 'Finish',
  legend: 'Legend',
  startFinishState: 'Start/Finish state',
  intermediateState: 'Intermediate state',
  action: 'Action',
  ballInCourtStep: 'Ball-in-court step',
  active: 'Active',
  struxhubActivityX: (code: string) => `${brandName} Activity ${code}`,
  openActivity: 'Open Activity',
  openDelivery: 'Open Delivery',
  loadingPresentationMode: 'Loading Presentation Mode',

  xSelected: (x: number) => `${x} selected`,
  xItemsCount: (x: number) => `${x} Item${ending(x)}`,
  xTypesCount: (x: number) => `${x} Type${ending(x)}`,
  xFormsCount: (x: number) => `${x} Form${ending(x)}`,
  xApplicationsCount: (x: number) => `${x} Application${ending(x)}`,
  xStepsCount: (x: number) => `${x} Step${ending(x)}`,
  xFieldsCount: (x: number) => `${x} Field${ending(x)}`,
  xOfYSelected: (x: number, y: number) => `${x} of ${y} selected`,
  xDeliveriesShowing: (x: number) => `${x} ${deliveries(x)} showing`,
  xEquipment: (x: number) => `${x} Equipment`,
  xSensorsShowing: (x: number) => `${x} ${sensors(x)} showing`,
  xStruxTags: (x: number) => `${x} ${struxTagsCount(x)}`,
  xResourcesShowing: (x: number, countLabel: string) =>
    `${x} ${countLabel} showing`,
  assign_verb: 'Assign',
  subscribe_verb: 'Subscribe',
  new: 'New',
  completedTotal: 'Completed/Total',
  searchByBookingIdOrSubscribers: 'Search by Booking ID or Subscribers',
  me: 'Me',
  full_period: 'Full',
  day: 'Day',
  calendarView: 'Calendar View',
  ganttView: 'Gantt View',
  listView: 'List View',
  mapView: 'Map View',
  dur_duration: 'Dur',
  loc_locations: 'Loc',
  equip_equipment: 'Equip',
  mat_materials: 'Mat',
  vend_vendor: 'Vend',
  a_actualFlag: 'A',
  unspecified: 'Unspecified',

  yesUnsubscribe: 'Yes, unsubscribe',
  deliveriesListDescriptions: {
    subscribeConfirmMassage: (
      dUnsubscribedCount: number,
      dSelectedCount: number,
    ) =>
      `Subscribe to [${dUnsubscribedCount}] unsubscribed ` +
      `${deliveries(dUnsubscribedCount)} ` +
      `of [${dSelectedCount}] selected ` +
      `${deliveries(dSelectedCount)}?`,

    unsubscribeConfirmMessage: (dSelectedCount: number) =>
      `You have already subscribed to all of [${dSelectedCount}] ` +
      `selected ${deliveries(dSelectedCount)}.\n\n` +
      'Perhaps, do you want to unsubscribe?',
  },
  logisticsListDescriptions: {
    subscribeConfirmMassage: (
      dUnsubscribedCount: number,
      dSelectedCount: number,
    ) =>
      `Subscribe to [${dUnsubscribedCount}] unsubscribed ` +
      `${workflows(dUnsubscribedCount)} ` +
      `of [${dSelectedCount}] selected ` +
      `${workflows(dSelectedCount)}?`,
    unsubscribeConfirmMessage: (dSelectedCount: number) =>
      `You have already subscribed to all of [${dSelectedCount}] ` +
      `selected ${workflows(dSelectedCount)}.\n\n` +
      'Perhaps, do you want to unsubscribe?',
    areYouWantToDelete:
      'Are you sure that you want to delete all of these selected Workflows?',
    deletionConfirmMsg: (selectedCount: number) =>
      `You selected - ${selectedCount} ${workflows(selectedCount)}`,
  },
  sitemapsDataIsLoading: 'Sitemaps Data is loading',
  editingConfirmation: 'Editing Confirmation',
  selectDataColumn: 'Select data column',
  addProperty: 'Add property',
  addFieldForEditing: 'Add field for editing',
  deliveryBulkEditorDescriptions: {
    warningMessagePart1:
      'None of the selected deliveries can be  ' +
      'changed according to your account permissions',
    warningMessagePart2: 'Make another selection or contact administrator',
  },
  failedToEdit: 'Failed to edit',
  editedXFieldsOnYDeliveries: (x: number, y: number) =>
    `Edited ${x} Field${ending(x)} on ${y} ${deliveries(y)}`,
  editXDeliveries_question: (x: number) => `Edit ${x} ${deliveries(x)}?`,
  onlyXOfYSelectedDeliveriesCanBeChanged: (x: number, y: number) =>
    `Only [${x}] of [${y}] selected ${deliveries(x)} ` +
    'can be changed, proceed?',
  editXFieldsOnYDeliveries: (x: number, y: number) =>
    `Edit ${x} Field${ending(x)} on ${y} ${deliveries(y)}`,
  updateXDeliveries: (x: number) => `Update ${x} ${deliveries(x)}`,
  failedToUpdate: 'Failed to update',
  signedOff: 'Signed off',
  signedOffFor: (companyName: string) => `Signed off for ${companyName}`,
  statusUpdated: 'Status updated',
  statusUpdatedOnXDeliveries: (x: number) =>
    `Status updated on ${x} ${deliveries(x)}`,
  updateDeliveryStatus: 'Update Delivery Status',
  loadingDeliveriesData: 'Loading deliveries data',
  loadingFormsData: 'Loading Workflows data',
  form: 'Form',
  log: 'Log',
  utilization: 'Utilization',
  controls: 'Controls',
  resourceProperties: 'Resource Properties',
  resourceControls: 'Resource Controls',
  mapProperties: 'Map Properties',
  sessions: 'Sessions',
  properties: 'Properties',
  map: 'Map',
  deleteResourceQuestion: 'Are you sure you want to delete this resource?',

  weekOfEndDate: 'Week Of End Date',
  companySummary: 'Company Summary',
  percentDone: '% Done',
  percentOnTime: '% On time',
  trucks: 'Trucks',
  avgTrucksPerDelivery: 'Avg. trucks / Delivery',
  deliveryGantt: 'Delivery Gantt',
  projectSummary: 'Project Summary',
  completionRatePercent: 'Completion Rate (%)',
  completionRate: 'Completion Rate',
  onTimeRatePercent: 'On-Time Rate (%)',
  onTimeRate: 'On-Time Rate',
  unplanned: 'Unplanned',
  unplannedRate: 'Unplanned Rate',
  onTimeStart: 'On-Time Start',
  lateOnTime: 'Late/On-Time',
  allDay: 'All day',
  weekly: 'Weekly',
  monthly: 'Monthly',
  onTimeLate: 'On-Time/Late',
  earnedDuration: 'Earned Duration',
  show_verb: 'Show',
  showXMore: (x: number) => `Show ${x} more`,
  showActive: 'Show active',
  loadingProjectData: 'Loading Project Data',
  updateToday: 'Update today',
  critical: 'Critical',
  deltaDays: '∆ Days',
  sumSlip: '∑ Slip',
  openIssues: 'Open issues',
  plannedToday: 'Planned today',
  actualToday: 'Actual today',
  deltaManpowerToday: '∆ Man. today',
  deltaManpowerTrend: '∆ Man. trend',
  soon: 'Soon',
  numberOfPicks: 'Number of picks',
  repeatDelivery: 'Repeat delivery',
  every_plural: 'Every',
  endsOn: 'Ends on',
  thisDelivery: 'This delivery',
  thisAndFutureDeliveries: 'This and future deliveries',
  allDeliveries: 'All deliveries',
  everyXDays: (x: number) => `Every ${x} day${ending(x)}`,
  everyXWeeks: (x: number) => `Every ${x} week${ending(x)}`,
  everyXMonths: (x: number) => `Every ${x} month${ending(x)}`,
  xDays: (x: number) => `day${ending(x)}`,
  xWeeks: (x: number) => `week${ending(x)}`,
  xMonths: (x: number) => `month${ending(x)}`,
  xMaterial: (x: number) => `Material #${x}`,
  addMaterial: 'Add material',
  forecast: 'Forecast',
  unknown: 'Unknown',
  multipleEntries: {
    add_x: (x: string) => `Add ${x}`,
    paste: 'You can paste multiple entries',
    typeOrPaste: 'You can type or paste multiple entries',
    repeatedValue: 'Duplicate entry',
  },
  fileType: 'File Type',
  fileExtension: 'File Extension',
  workflowType: 'Workflow Type',
  workflowTypeLabel: 'Workflow Type',
  workflowName: 'Workflow Name',
  workflowCategory: 'Workflow Category',
  workflowId: 'Workflow ID',
  fileCategory: 'File Category',
  uploadedBy: 'Uploaded by',
  uploader: 'Uploader',
  source: 'Source',
  sort: 'Sort',
  sortBy: 'Sort by',
  sortDirection: 'Sort direction',
  setDefaults: 'Set defaults',
  ascending: 'Ascending',
  descending: 'Descending',
  failedToVerifyUploadedFile: 'Failed to verify uploaded file',

  searchCompanyOrCompanyType: 'Search company or company type',

  newActivity: 'New activity',
  monitoringName: 'Monitoring Name',
  deviceId: 'Device ID',
  device: 'Device',
  lastMaturity: 'Last maturity',
  lastReading: 'Last reading',
  elapsedTime: 'Elapsed time (hh:mm)',
  maxElapsedTime: 'Max elapsed time (min)',
  lastTemperature: (temperatureUnit: MaturixTemperatureUnit) =>
    `Last temperature (${EnglishTree.temperatureUnit(temperatureUnit)})`,
  lastStrength: (strengthUnit: MaturixStrengthUnit) =>
    `Last strength (${EnglishTree.strengthUnit(strengthUnit)})`,
  temperatureWithUnit: (temperatureUnit: MaturixTemperatureUnit) =>
    `Temperature (${EnglishTree.temperatureUnit(temperatureUnit)})`,
  strengthWithUnit: (strengthUnit: MaturixStrengthUnit) =>
    `Strength (${EnglishTree.strengthUnit(strengthUnit)})`,
  temperatureUnit: (temperatureUnit: MaturixTemperatureUnit): string => {
    switch (temperatureUnit) {
      case MaturixTemperatureUnit.CELCIUS:
        return 'Celsius'
      case MaturixTemperatureUnit.FAHRENHEIT:
        return 'Fahrenheit'
    }
  },
  strengthUnit: (strengthUnit: MaturixStrengthUnit): string => {
    switch (strengthUnit) {
      case MaturixStrengthUnit.KGPERCMSQ:
        return 'kg/cm²'
      case MaturixStrengthUnit.MPA:
        return 'MPa'
      case MaturixStrengthUnit.NPERMMSQ:
        return 'N/mm²'
      case MaturixStrengthUnit.PSI:
        return 'PSI'
    }
  },

  unconfirmed: 'Unconfirmed',
  confirmed: 'Confirmed',
  delivering: 'Delivering',
  paused: 'Paused',
  onHold: 'On hold',

  ticketed: 'Ticketed',
  recalled: 'Recalled',
  enRoute: 'En-route',
  pouring: 'Pouring',
  delivered: 'Delivered',

  orderSummary: 'Order summary',
  payloadSummary: 'Payload summary',

  orderStatus: 'Order status',
  totalVolume: 'Total volume',
  deliveryLocation: 'Delivery Location',
  mixType: 'Mix type',
  slump: 'Slump',
  spacingOrUnloadTime: 'Spacing or unload time',
  pouringMode: 'Pouring mode',
  elementsToPour: 'Elements to pour',
  extraCharge: 'Extra charge',
  orderedBy: 'Ordered by',

  load: 'Load',
  total: 'Total',
  timeOnSite: 'Time on site',
  ticket: 'Ticket',
  volumeLoaded: 'Volume loaded',
  totalOrderVolume: 'Total order volume',
  additive: 'Additive',
  jobTimes: 'Job times',
  startLoad: 'Start load',
  beginPouring: 'Begin pouring',
  washing: 'Washing',
  totalTimeOnSite: 'Total time on site',
  inXHoursAndXMin: (hours: number, minutes: number) =>
    `In ${hours}h ${minutes}min`,
  xHoursAndXMinLate: (hours: number, minutes: number) =>
    `${hours}h ${minutes}min late`,
  inXMin: (minutes: number) => `In ${minutes} min`,
  xMinLate: (minutes: number) => `${minutes} min late`,
  isAboutToArrive: 'Is about to arrive',

  projected_time: 'Projected',
  recurringDelivery: 'Recurring',
  recurringWorkflow: 'Recurring',
  endingXDate: (date: string) => `Ending ${date}`,
  isQuestionMandatory: 'Is question mandatory?',
  remainingDuration: 'Remaining duration',
  baseUrl: 'Base Url',
  siteId: 'Site ID',
  isActive: 'Is Active',
  syncStartDate: 'Synchronization start date',
  notUniqueActivityCodeMessage: (code: string) =>
    `The activity with the code [${code}] already exists. Try another value`,
  activityWasUpdated: 'Following activity was updated',
  activityUpdated: 'Activity updated',
  editXFieldsOnYActivities: (x: number, y: number) =>
    `Edit ${x} Field${ending(x)} on ${y} ${activities(y)}`,
  editedXFieldsOnYActivities: (x: number, y: number) =>
    `Edited ${x} Field${ending(x)} on ${y} ${activities(y)}`,
  editXActivities_question: (x: number) => `Edit ${x} ${activities(x)}?`,
  onlyXOfYSelectedActivitiesCanBeChanged: (x: number, y: number) =>
    `Only [${x}] of [${y}] selected ${activities(x)} ` +
    'can be changed, proceed?',
  p6Viewer: 'P6 Viewer',
  scanner: 'Scanner',
  scannerApp: 'Scanner App',
  selectScanner: 'Select scanner',
  createNewScanner: 'Create new scanner',
  activitySetName: 'Activity set name',
  editActivitySet: 'Edit an activity set',
  includeXFile: (ext: string) => `Include a .${ext} file`,
  includeDetailedEachForm: 'Include a detailed view of each Workflow',
  usersSubscribedTo: {
    deliveries: (uCount: number, iCount: number) =>
      `Subscribed ${uCount} ${users(uCount)} to ${iCount} ${deliveries(
        iCount,
      )}`,
    activities: (uCount: number, iCount: number) =>
      `Subscribed ${uCount} ${users(uCount)} to ${iCount} ${activities(
        iCount,
      )}`,
    forms: (uCount: number, iCount: number) =>
      `Subscribed ${uCount} ${users(uCount)} to ${iCount} ${workflows(iCount)}`,
  },
  usersUnSubscribedFrom: {
    deliveries: (uCount: number, iCount: number) =>
      `Unsubscribed ${uCount} ${users(uCount)} to ${iCount} ${deliveries(
        iCount,
      )}`,
    activities: (uCount: number, iCount: number) =>
      `Unsubscribed ${uCount} ${users(uCount)} to ${iCount} ${activities(
        iCount,
      )}`,
    forms: (uCount: number, iCount: number) =>
      `Unsubscribed ${uCount} ${users(uCount)} to ${iCount} ${workflows(
        iCount,
      )}`,
  },
  scannerName: 'Scanner Name',
  scanMaster: 'Scan Master',
  scanStationReport: 'Scan Station Report',
  materialTrackingReport: 'Material Tracking Report',
  truckSize: 'Truck size',
  cancelationReason: 'Cancelation Reason',
  responsibleCompanies: 'Responsible Companies',
  noDataAvailable: 'No data available',
  last7Days: 'Last 7 days',
  last30Days: 'Last 30 days',
  last120Days: 'Last 120 days',
  last365Days: 'Last 365 days',
  entireProject: 'Entire project',
  frequencyAndPeriod: 'Frequency and period',
  activityDataLinking: {
    disableLinkingQuestion: (field: string) =>
      `Linking to external '${field}' will be disabled. Do you want to proceed?`,
    enableLinkingQuestion: (fieldValue: string, externalFieldValue: string) =>
      `Do you want to link '${fieldValue}' to external '${externalFieldValue}'? This may affect other activities.`,
    linkingIsDisabled: 'Data linking is disabled',
    linkingIsEnabled: 'Data linking is enabled',
    linkingDisabled: `Data linking disabled`,
    linkingEnabled: `Data linking enabled`,
    editWarning: `Linking changes might impact other activities`,
    bulkEditWarning: `Data linking will be disabled for editable fields`,
  },

  materialsUpload: 'Materials upload',
  plannedQuantity: 'Planned quantity',

  materialsUploading: {
    uploadMaterials: 'Upload materials',
    goToTheMaterialsPage: 'Go to the Materials page',
    uploadYourMaterialsFile:
      'Upload your Materials file in Excel (.xlsx, .xlsm) format.',
    thereAreNoSheetsError:
      'There are no sheets in the provided Excel file or an error occurred while reading the file',
    materialCategoryLvl1: 'Material Category (Level 1)',
    materialLvl2: 'Material (Level 2)',
    materialDescription: 'Material Description',
    materialPlannedQuantity: 'Material Planned Quantity',
    startingTheUpload: 'Starting the upload',
    readingMaterialsFile: 'Reading materials file',
    extractingMaterialsData: 'Extracting materials data',
    removingOldData: 'Removing old data',
    savingMaterialsData: 'Saving materials data',
    finishingUpload: 'Finishing upload',
    uploadOfMaterialsFailedAt: 'Uploading of materials is failed at',
    uploadWasSuccessful: 'Upload was successful',
    pleaseSelectSheetToUpload: (isMultiple: boolean) =>
      `${
        isMultiple ? 'The current file contains multiple sheets. ' : ''
      }Please select a sheet from the file to import.`,
    makeSureLocationNamesUnique:
      'Make sure location names are unique in StruxHub',
  },

  materialsUploadHistory: {
    currentMaterials: 'Current materials',
    projectUsesDefaultMaterials: 'The project uses default materials',
    latestUpload: 'Latest upload',
    materialsRestoringConfirmation: 'Materials Restoring Confirmation',
    totalMaterialCategoriesAndMaterials:
      'Total material categories and materials',
    restoreDefaultMaterials: 'Restore default materials',
    doYouConfirmRestoreToDefaults:
      'Do you confirm restoring to the default materials?',
    restoredSuccessfully: 'Restored successfully',
    failedToRestore: 'Failed to restore',
  },

  materialsUploadConfirm: {
    confirmDataMerge: 'Confirm data merge',
    confirmDescription1:
      'Uploading this file will update all changed values for existing materials and create new records for new materials.',
    confirmDescription2:
      'If you want to delete all existing materials data and replace it with the data in the file upload,',
    confirmDescription3: 'click here',
    replaceDescription:
      'Do you really want to delete all existing materials data and replace it with the data in the file upload?',
  },

  collapseAll: 'Collapse all',
  expandAll: 'Expand all',
  expandMenu: 'Expand menu',
  expandPanel: 'Expand panel',
  collapseMenu: 'Collapse menu',

  myUserProfile: 'My user profile',
  addTeammates: 'Add Teammates',
  addSummaryColumn: 'Add summary column',
  configureColumns: 'Configure columns',
  summaryColumns: 'Summary columns',
  receipt: 'Receipt',
  summary: 'Summary',

  remainingQuantity: 'Remaining quantity',

  materialsInDeliveryFormWarning: {
    onlyXMaterialsForForm: (x: number) =>
      `Currently only ${x} material${ending(x, '', 's')} ${ending(
        x,
        'is',
        'are',
      )} enabled for one Delivery Form. If this is a limitation please contact`,
    andWeCanTalk: 'and we can talk',
  },

  deliverySuccessfullyCreated: 'Delivery successfully created',

  xDates,

  materialDescription: 'Material Description',
  thisGroupAlreadyExists: 'This group already exists',

  upload: 'Upload',
  totalBooked: 'Total booked',
  totalDelivered: 'Total delivered',
  doneDeliveries: 'Done deliveries',

  xVendors: (x: number) => `${x} Vendor${ending(x)}`,
  thisWeek: 'This week',
  thisMonth: 'This month',

  youCannotChangeDelivery:
    'You cannot change a delivery while it is in this status',
  plannedInstallLocation: 'Planned install location',
  plannedDeliveryLocation: 'Planned delivery location',
  currentLocation: 'Current location',
  procurementID: 'Procurement ID',
  id_short: 'ID',
  readyToInstallQuantity: 'Ready to install quantity',
  totalOnSiteQuantity: 'Total on-site quantity',
  toMoveQuantity: 'To move quantity',

  preciseLocation: 'Precise Location',
  editPreciseLocation: 'Edit Precise Location',
  preciseLocationPreviewTooltip:
    'This preview is used in the PDF report. Adjust the zoom level to control how the map appears.',
  zoomOverviewReportTooltip:
    'This zoom level is also used as the Overview image in PDF reports for Workflow Locations.',
  thisLocationIsNotGeolocated: 'This location is not geolocated',
  notGeolocated: 'Not geolocated',
  inheritsFrom: 'Inherits from',
  defaultsTo: 'Defaults to',
  setPreciseLocation: 'Set Precise Location',
  movePinPosition: 'Move Pin Position',
  markupTools: 'Markup tools',
  bold: 'Bold',
  italic: 'Italic',
  underline: 'Underline',
  fill: 'Fill',
  border: 'Border',
  thickness: 'Thickness',
  shortcuts: 'Shortcuts',
  gestures: 'Gestures',
  gesturePointsGroup: 'Points (line / polygon)',
  gestureTextGroup: 'Text',
  gestureMapGroup: 'Map',
  gestureSelectMarkup: 'Select a markup',
  gestureEditPoints: 'Edit points (line / polygon)',
  gestureDeselect: 'Deselect',
  gestureMovePoint: 'Move a point',
  gestureAddPoint: 'Add a point',
  gestureRemovePoint: 'Remove a point',
  gestureClosePolygon: 'Close into a polygon',
  gestureRectangleCircle: 'Rectangle / circle',
  gestureLineArrow: 'Line / arrow',
  gesturePolygonPolyline: 'Polygon / polyline',
  gesturePenHighlighterEraser: 'Pen / highlighter / eraser',
  gestureAddText: 'Add text',
  gestureEditText: 'Edit text',
  gestureMoveResizeRotate: 'Move / resize / rotate',
  gestureRepositionPin: 'Reposition the pin',
  gesturePan: 'Pan',
  gestureZoom: 'Zoom',
  gestureRotateTilt: 'Rotate & tilt',
  gestureTap: 'Tap',
  gestureTapAgain: 'Tap again',
  gestureTapMap: 'Tap the map',
  gestureDrag: 'Drag',
  gestureTapMidpoint: 'Tap a midpoint',
  gestureLongPress: 'Long-press',
  gestureTapFirstPoint: 'Tap the first point',
  gestureTapOrDrag: 'Tap or drag',
  gestureDragOrTapEnds: 'Drag, or tap start then end',
  gestureTapPointsClose: 'Tap points; tap the first to close',
  gestureDoubleTap: 'Double-tap',
  gestureDragBodyCornerPin: 'Drag body / corner / pin',
  gestureTapMoveThenDrag: 'Tap Move, then drag the map',
  gesturePinch: 'Pinch',
  gestureTwoFingerDrag: 'Two-finger drag',
  customColor: 'Custom color',
  strokeWidth: 'Stroke width',
  strokeWidthSlider: 'Stroke width slider',
  borderWidth: 'Border width',
  borderWidthSlider: 'Border width slider',
  backgroundColor: 'Background color',
  textColor: 'Text color',
  textSize: 'Text Size',
  fontSize: 'Font size',
  moveToolbar: 'Move toolbar',
  move: 'Move',
  savePreciseLocationPosition: 'Save Precise Location position?',
  dontSave: "Don't save",
  dragTheMapToChangeLocationPinPosition: 'Drag a map to edit pin position',
  addMarkups: 'Add Markups',
  markUpPhoto: 'Mark up photo',
  photoMarkupLoadFailed: 'Could not load this photo',
  untitledWorkflow: 'Untitled workflow',
  fromDrawing: 'From drawing',
  continueWorkflow: 'Continue workflow…',
  collapse: 'Collapse',
  expand: 'Expand',
  createWorkflow: 'Create Workflow',
  holdToCreateWorkflow: 'Create a Workflow',
  holdToCreateWorkflowHint:
    'Press and hold anywhere on the map to drop a pin and start a Workflow.',
  gotIt: 'Got it',
  enterWorkflowTitle: 'Enter title',
  discard: 'Discard',
  discardWorkflowDraft: 'Discard Workflow draft?',
  discardWorkflowDraftQuestion:
    'You will lose the pin, the form input, and any drawn markups.',
  workflowTitleSubmitted: (name: string) => `${name} submitted`,
  userStamp: 'User stamp',
  userStampTooltip: "Includes the signing user's name with the signature.",
  inventoryTracking: 'Tracking Fields',
  inventoryTrackingTooltip:
    'When enabled, adds quantity and location fields to track material resource inventory.',
  moveToMyLocation: 'Move to my location',
  noCoordinatesAvailable: 'No coordinates available',
  geolocationPermissionDenied:
    'Geolocation permission denied. Please enable location access in your browser settings.',
  geolocationPositionUnavailable:
    'Location information is unavailable. Please check your device location settings.',
  geolocationTimeout: 'Location request timed out. Please try again.',
  geolocationError:
    'An error occurred while getting your location. Please try again.',
  dismiss: 'Dismiss',
  pictograms: {
    names: {
      building: 'Building',
      level: 'Level',
      zone: 'Zone',
      area: 'Area',
      route: 'Route',
      door: 'Door',
      gate: 'Gate',
      shaft: 'Shaft',
      'loading-zone': 'Loading Zone',
      'restrooms-men': "Men's Restroom",
      entrance: 'Entrance',
      exit: 'Exit',
      'access-a': 'Access A',
      'access-b': 'Access B',
      'access-c': 'Access C',
      'access-d': 'Access D',
      'access-e': 'Access E',
      'access-f': 'Access F',
      'no-access-1': 'No Access 1',
      'no-access-2': 'No Access 2',
      'no-access-3': 'No Access 3',
      'pedestrian-walkway': 'Pedestrian Walkway',
      elevator: 'Elevator',
      stairs: 'Stairs',
      parking: 'Parking',
      'bike-parking': 'Bike Parking',
      'buggy-parking': 'Buggy Parking',
      'hi-vis-apparel': 'High-Visibility Safety Apparel',
      'safety-helmet': 'Safety Helmet',
      'safety-gloves': 'Safety Gloves',
      'safety-boots': 'Safety Toe Boots',
      'not-required-all': 'Not Required: All',
      'not-required-apparel': 'Not Required: Apparel',
      'not-required-helmet': 'Not Required: Helmet',
      'not-required-gloves': 'Not Required: Gloves',
      'not-required-boots': 'Not Required: Boots',
      'visitor-check-in': 'Visitor Check-in',
      'admin-office': 'Admin Office',
      'electrical-room': 'Electrical Room',
      restrooms: 'Restrooms',
      'restrooms-women': "Women's Restroom",
      'break-area': 'Break Area',
      'hydration-station': 'Hydration Station',
      'smoking-area': 'Smoking Area',
      canteen: 'Canteen',
      break: 'Break',
      'wash-area': 'Wash Area',
      dumpster: 'Dumpster',
      'brand-oes': 'OES Equipment',
      'brand-sunbelt': 'Sunbelt Rentals',
      'brand-united-rentals': 'United Rentals',
      'brand-equipmentshare': 'EquipmentShare',
      'brand-feco': 'First Equipment Company',
      deliveries: 'Deliveries',
      'construction-vehicles': 'Construction Vehicles',
      trailer: 'Trailer',
      'security-checkpoint': 'Security Checkpoint',
      'temporary-water': 'Temporary Water',
      'equipment-storage': 'Equipment Storage',
      'material-boxes': 'Material Storage: Boxes',
      'material-wood': 'Material Storage: Wood',
      'material-steel-1': 'Material Storage: Steel 1',
      'material-plumbing': 'Material Storage: Plumbing',
      'material-steel-2': 'Material Storage: Steel 2',
      'construction-debris-1': 'Construction Debris 1',
      'construction-debris-2': 'Construction Debris 2',
      'information-area': 'Information Area',
      'site-map': 'Site Map',
      'first-aid': 'First Aid',
      aed: 'Automated External Defibrillator',
      'covid-checkpoint': 'Health Screening',
      'fire-extinguisher': 'Fire Extinguisher',
      'muster-point': 'Muster Point',
      'exit-running': 'Exit (Running Figure)',
      cleanroom: 'Cleanroom / Gowning',
      'icra-zone': 'ICRA Zone',
      'negative-pressure': 'Negative Pressure',
      'esd-zone': 'ESD Zone',
      'no-photography': 'No Photography',
      'no-drones': 'No Drones',
      'fall-hazard': 'Fall Hazard / Open Edge',
      'silica-dust': 'Silica / Dust',
      noise: 'Noise',
      'overhead-load': 'Overhead Load / Crane Swing',
      excavation: 'Excavation',
      'hot-work': 'Hot Work',
      'confined-space': 'Confined Space',
      'arc-flash': 'High Voltage / Arc Flash',
      stop: 'Stop',
      yield: 'Yield',
      'speed-limit': 'Speed Limit',
      'one-way': 'One Way',
      flagger: 'Flagger Ahead',
      'truck-crossing': 'Truck Crossing',
      'fire-lane': 'Fire Lane — Keep Clear',
      'concrete-washout': 'Concrete Washout',
      'storm-drain': 'Storm Drain Inlet Protection',
      'erosion-control': 'Erosion Control',
      'wheel-wash': 'Wheel Wash',
      'spill-kit': 'Spill Kit',
      scaffold: 'Scaffold',
      'swing-stage': 'Swing Stage',
      'mast-climber': 'Mast Climber',
      eyewash: 'Eyewash / Safety Shower',
      standpipe: 'Standpipe / Hose',
      'fire-hydrant': 'Fire Hydrant',
      'alarm-pull-station': 'Alarm Pull Station',
      'crane-zone': 'Crane / Lift Zone',
      'brand-herc-rentals': 'Herc Rentals',
    },
  },
  iconPicker: {
    library: 'Library',
    companyLogos: 'Company logos',
    selectIcon: 'Select Icon',
    xIcons: (x: number) => `${x} Icon${ending(x)}`,
    noResults: 'No icons found',
    libraryIconColor:
      'Color applies to labels, tags and calendars, not to the icon, since this icon has its own colors',
  },

  formMaterialSubFields: {
    materialHasQuantityAndLocation:
      'Material has Quantity and Location fields which you can also add/edit below.',
    clickToEnableQuantity: 'Click here if you want to enable Quantity.',
    clickToEnableLocation: 'Click here if you want to enable Location.',
  },
  newFormType: 'New Workflow type',
  materialTransferId: 'Material transfer ID',
  materialTransferDate: 'Material transfer date',
  newTransfer: 'New transfer',
  doneTransfers: 'Done transfers',
  totalTransferred: 'Total transferred',
  xTransfers: (x: number) => `${x} Transfer${ending(x)}`,

  youCanSelectMultipleOptions: 'You can select multiple options',
  updateStatus: 'Update Status',

  review_verb: 'Review',
  reviewed: 'Reviewed',
  activated: 'Activated',
  submitted: 'Submitted',
  workflowIsDoneOrDenied: (isDenied: boolean) =>
    `The Workflow is ${isDenied ? 'denied' : 'done'}`,
  workflowIsEnded: 'The Workflow is ended',
  request_noun: 'Request',
  review_noun: 'Review',
  recurringInspection: 'Recurring inspection',
  assignAsBicWhenWorkflowMatches:
    'Assign as Ball-in-Court when the Workflow matches',
  thisWorkflowStepIsAssignedByRules:
    'This Workflow Step is assigned using the following rules',
  adminsCanAdvanceWorkflow:
    'Admins can advance the Workflow regardless of assigned Users.',
  completedBy: 'Completed by',
  anyAssignedUser: 'Any assigned User',
  allAssignedUsers: 'All assigned Users',
  allAssignedCompanies: 'All assigned Companies',
  allAssignedUsersAndCompanies: 'All assigned Users/Companies',
  oneUserPerRule: 'One User per rule',
  anyAssignedUserDescription:
    'The first assigned User to complete this step moves the Workflow forward.',
  allAssignedUsersDescription:
    'Every assigned User, and one person from each assigned Company, must complete this step before the Workflow moves forward.',
  oneUserPerRuleDescription:
    'One User from each rule must complete this step before the Workflow moves forward.',
  assignees: 'Assignees',
  optionalInParentheses: '(optional)',
  workflowCompany: 'Workflow Company',
  completeForWhichRule: 'Complete for which rule?',
  countsTowardOneRule:
    'You are assigned through more than one rule on this step. Completing it counts toward one of them.',
  ruleName: 'Rule name',
  allUsersMustCompleteDisabledTooltip:
    'Add a BIC User rule to enable this option.',
  xOfYComplete: (x: number, y: number) => `${x} of ${y} complete`,
  noFieldsSubmitted: 'No fields submitted',
  submittedAt: 'Submitted at',
  xRules: (x: number) => `${x} rule${ending(x)}`,
  step: 'Step',
  stepCompletion: 'Step Completion',
  bicUser: 'BIC user',
  formSection: 'Workflow section',
  actions: 'Actions',
  frequency: 'Frequency',
  deadline: 'Deadline',
  automaticStart: 'Automatic start',
  addStep: 'Add step',

  workflowConfDescr: {
    mustContainApproval:
      'Workflow with a Request step must contain an Approval step',
    cannotBeTheLastStep: (stepName: string) =>
      `${stepName} can't be the last step, please add another one or remove this one.`,
    workflowStartsOnDate: 'Workflow starts automatically on start date.',
    automaticallyStart: 'Automatically start Workflow on start date',
    replaceWithStep: (stepType: string): string =>
      `Replace with ${stepType} step`,
    replaceWithStart: 'Replace with Start step',
    deleteWorkflowStep: 'Delete Workflow step',
    removeStepDialogMsg: 'Do you want to remove this Workflow step?',
    toastUnsavedMsg: 'The changes you made have been reset',
    recurringHasConstr: 'Recurring Inspection step has constraints:',
    toggleDialogMsg: (isManualStart: boolean): string =>
      `Change it to an ${
        isManualStart ? 'Manual Start' : 'Automatic Start'
      } step?`,
    selectStepMsg: 'Select a Workflow step to add/modify fields',
  },
  workflowConfTooltips: {
    bicBlockFirstLine: `Assign one or more Workflow Users as Ball-in-Court for this step. Ball-in-Court users are responsible for completing the step before the Workflow can progress.`,
    bicBlockSecondLine: `Rules can be used to assign users dynamically based on StruxHub Fields, such as Location, Equipment, or Company`,
  },
  xWorkflowsShowing: (x: number) => `${x} ${workflows(x)} showing`,
  workflowTableText: {
    seeDescr: 'See step descriptions',
    stepCategory: 'Workflow step category',
    workflowStep: 'Workflow step',
    constraints: 'Constraints',
    bicText: 'Can specify ball-in-court user?',
    multipleText: 'Can have multiple instances in Workflow?',
    submissionDescr:
      'Initiating Workflow step to submit a form that does not require a subsequent approval.',
    submissionConstr: 'Can only initiate a Workflow.',
    requestDescr: 'Initiating Workflow step to request an approval.',
    requestConstr:
      'Can only initiate a Workflow. Must have a subsequent Approval step.',
    reviewDescr:
      'General advancement step of a Workflow that can be used for pre-approvals.',
    approvalDescr:
      'Approval of a Workflow request that can be assigned to a specific user(s).',
    approvalConstr: 'Must have a preceding Request step.',
    startDescr: 'Start of a Workflow initiated by clicking a «Start» button.',
    autoStartDescr:
      'Start of a Workflow that is automatically initiated on the Workflow start date.',
    onSiteDescr: 'Used to indicate the on-site arrival of a delivery vehicle.',
    readyToInspectDescr:
      'Intermediate Workflow step used to notify inspectors.',
    inspectionDescr: 'General inspection step.',
    recurringDescr: 'Recurring inspections at a specified frequency.',
    recurringConstr:
      'Must be directly preceded by either a Start or Automatic start step.',
    finishDescr: 'Completion of a Workflow marked by clicking a «Done» button.',
  },
  workflowSteps: 'Workflow steps',
  workflowDiagram: 'Workflow diagram',
  workflowStepConfigurator: 'Workflow step configurator',
  configurator: 'Configurator',
  restoreToLastSavedState: 'Restore to last saved state',
  endOfTheDay: 'End of the day',
  everyX_withoutNum: (x: number) => `Every${ending(x, '', '')}`,
  submission: 'Submission',
  initiation: 'Initiation',

  deleteWorkflow: 'Delete the Workflow',
  workflowDeletionDialogDescr: {
    confirmDelete:
      'Deleting this Workflow will remove the Workflow and all associated notes and data from the project',
    confirmDelete2: 'Do this only if the Workflow Request was a mistake.',
  },
  draftWorkflowDeletionDialogDescr: {
    confirmDelete:
      'This will permanently remove the draft Workflow from the project.',
  },
  emailPdfReport: 'Email PDF report',
  unsubscribe: 'Unsubscribe',
  followFormMenuDescr: {
    toReceive: 'To receive Workflow notifications',
    toStop: 'To stop receiving Workflow notifications',
  },
  workflowDeleteMenuDescr: {
    isDone: 'The Workflow is Done/Ended, you cannot delete it.',
    onlyFor:
      'Only available to admins, Workflow masters, requesters, assignees',
  },
  configureTableColumns: 'Configure table columns',
  searchProperties: 'Search properties',
  shownInTable: 'Shown in table',
  hiddenInTable: 'Hidden in table',
  visibleLayers: 'Visible layers',
  hiddenLayers: 'Hidden layers',
  // Map file status copy, shared by the tileset upload and notification cards.
  // Both render the SAME file as it moves from client upload to backend
  // processing, so the wording must stay consistent across that hand-off.
  // `finishingUpload` is the client's hand-off stage and is worded deliberately
  // differently from the backend's `processingTheUpload`, so the two read as
  // distinct steps rather than duplicates.
  finishingUpload: 'Finishing upload',
  processingTheUpload: 'Processing the upload',
  uploadSucceeded: 'Upload succeeded',
  uploadFailed: 'Upload failed',
  replaced: 'Replaced',
  pleaseWaitThisMayTakeAWhile: 'Please wait, this may take a while',
  aNewerVersionOfThisFileWasUploaded:
    'A newer version of this file was uploaded.',
  somethingWentWrongPleaseTryAgain: 'Something went wrong — please try again.',
  freezeColumns: 'Freeze columns above',
  restoreDefaults: 'Restore defaults',
  rememberChoice: 'Remember my choice',
  successfullyDeletedForms: (x: number) =>
    `Successfully deleted ${x} ${workflows(x)}`,
  failedToDelete: 'Failed to delete',
  failedToArchive: 'Failed to archive',
  failedToUnarchive: 'Failed to unarchive',
  failedToLoadUsageData: 'Failed to load usage data. Please try again.',
  retry: 'Retry',
  cannotDeleteDoneForms: 'Cannot delete Done Workflows',
  cannotDeleteSystemFormsTooltip: 'Deletion of default Forms is prohibited',
  getMobileNativeApp: 'Get the app',
  getMobileNativeApp_long: 'Get the StruxHub mobile app',
  getAppOnGoogle: 'Get it on Google Play',
  getAppOnApple: 'Download on the App Store',
  oneTimePopup: {
    title: 'Download our new mobile app!',
    body: 'Receive real-time updates and get more work done in the field with StruxHub.',
    dismiss: 'Ok understood!',
  },

  enterX: (x: string) => `Enter ${x}`,
  selectX: (x: string) => `Select ${x}`,
  aboutX: (x: string) => `About ${x}`,
  selectNestedItems: 'Select nested items',
  deselectNestedItems: 'Deselect nested items',
  createNewX: (x: string) => `Create New ${x}`,
  editX: (x: string) => `Edit ${x}`,
  hoursUnit: 'hours',
  enterProjectCode: 'Enter project code',
  somethingWentWrongTryAgainLater: 'Something went wrong, try again later!',
  unsubscribedSuccessfully: 'Unsubscribed successfully!',
  exitScanner: 'Exit Scanner',
  deleteScanner: 'Delete Scanner',
  editScannerText: 'Edit Scanner',
  shouldDeleteScanner: 'Should delete scanner',
  startScanner: 'Start scanner',
  queue: 'Queue',
  startQueue: 'Start Queue',
  addedToQueue: 'Added To Queue',
  startSession: 'Start Session',
  allowedUsers: 'Allowed Users',
  badgeDescription: 'Badge description',
  makeItTimed:
    "Make this a 'Timed' scanner for users. " +
    'Scan-Start : Scan-Stop = Total Time',
  limitAccess: "Limit access to specified 'Badged' Users, Teams or Companies",
  endRideConfirm: 'Are you sure you want to end the ride?',
  changeModeConfirm: 'Are you sure you want to change the ride mode?',
  startSessionConfirm: 'Are you sure you want to start the session?',
  isScannerTimedByDay: 'Timer automatically ENDS at close of work day',
  endRide: 'End Ride',
  scanTo: 'Scan to',
  activeUsers: 'Active users:',
  scannerActive: 'Scanner active',
  scannerInactive: 'Scanner inactive',
  pauseScanner: 'Pause scanner',
  resumeScanner: 'Resume scanner',
  endSessionForAll: 'End session for all',
  formName: 'Workflow type',
  undo: 'Undo',
  redo: 'Redo',
  icon: 'Icon',
  color: 'Color',
  parent: 'Parent',
  lbsParent: 'LBS Parent',
  lbsParentDescription:
    'LBS (Location Breakdown Structure) defines where a Resource is located within the Project. Select the Parent Location for this Resource.',
  newLocationNeedsParent: 'A new location needs an LBS Parent.',
  locationTypeNotUsableInWorkflows:
    "This location type can't be used in a Workflow yet.",
  uncreatableLocationTypesSkipped:
    'Skipped locations of a type this project cannot create.',
  locationTypesStillLoading:
    'Location types are still loading. Try again in a moment.',
  locationTypesFailedToLoad:
    "Couldn't load location types. Reload the page to try again.",
  uncreatableLocationType: "This project can't create this type of location.",
  showInApp: 'Show in App',
  allowCreation: 'Allow Creation',
  activeNotActive: 'Active/Not Active',
  notActive: 'Not Active',
  allowCreationInfo: 'Turns this workflow type on or off for creation.',
  creationAccess: 'Creation Access',
  creationAccessInfo:
    'Sets who can create this workflow type.\nEveryone: any project member.\nAdmins only: reserved for admins.',
  adminsOnly: 'Admins only',
  everyone: 'Everyone',
  blockRequestsForNonWorkTimes: 'Block requests for non-work times',
  general: 'General',
  steps: 'Steps',
  views: 'Views',
  confirmation: 'Confirmation',
  deletionOfDefaultForms: 'Deletion of default Workflows is prohibited',
  theFollowingEntitiesWereUpdated: 'The following entities were updated',
  globeViews: 'Globe Views',
  whiteboards: 'Whiteboards',
  whiteboard: 'Whiteboard',
  georeferenced: 'Georeferenced',
  nonGeoreferenced: 'Non-georeferenced',
  areYouSureDeleteMapView: 'Are you sure you want to delete this map?',
  areYouSureDeleteTileset: 'Are you sure you want to delete this tileset?',
  areYouSureDeleteRemoteTileset:
    'Are you sure you want to delete this remote tileset?',
  addRemoteTileset: 'Add remote tileset',
  fileProperties: 'File properties',
  shapeProperties: 'Shape properties',
  createLocationWithThisShape: 'Create Location with this Shape',
  latitude: 'Latitude',
  longitude: 'Longitude',
  zoom: 'Zoom',
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out',
  parts: 'Parts',
  geometryType: 'Geometry type',
  noMapViewsConfigured: 'No map views configured for this project yet.',
  noMapViewsMatchingX: (x: string) => `No map views match "${x}".`,
  mapViewsManager: 'Map Views Manager',
  selectMapView: 'Select Map View',
  xMapViews: (x: number) => `${x} Map views`,
  xOfYMapViews: (visible: number, total: number) =>
    `${visible} of ${total} Map views`,
  createdAt: 'Created At',
  byLBS: 'By LBS',
  byLocation: 'By Location',
  byType: 'By Type',
  collapseOneLevel: 'Collapse one level',
  expandOneLevel: 'Expand one level',
  previousMapView: 'Previous map view',
  nextMapView: 'Next map view',
  closeMapViews: 'Close map views',
  mapViewsBrowseAndSelect: 'Browse and select a map view for the project.',
  selectAssociatedResource: 'Select associated Resource',
  selectPermittedResources: 'Select permitted Resources',
  selectResources: 'Select Resources',
  openActionsForX: (x: string) => `Open actions for ${x}`,
  openX: (x: string) => `Open ${x}`,
  openAMap: 'Open a map',
  backToX: (x: string) => `Back to ${x}`,
  selectMapViewX: (x: string) => `Select map view ${x}`,
  available: 'Available',
  useMapLabels: 'Show place labels',
  notifiedUsers: 'Notified users',
  selectNotificationUsers:
    'Select users that will receive a notification after completion of this step',
  wouldYouEnableNotificationsForSteps:
    'Would you like to enable notifications for this field across all Workflow steps?',
  allowNotificationsFor: (fieldName: string) =>
    `Allow Notifications for ${fieldName}`,
  notifyFor: 'Notify for',
  selectWorkflowSteps: 'Select Workflow Step(s)',
  allSteps: 'All steps',
  workflowBookingDeadline: 'Workflow Booking Deadline',
  workflowDeadlineDescr: `Workflows must be requested prior to the Deadline.
  Set how many days in advance below. The toggle can be set to block the late requesting of Workflows.
  The user will receive a warning and will have to pick a day that is farther in advance`,
  workflowsMustBeRequested: 'Workflows must be requested prior to',
  workflowWasntRequestedInAdvance: (interval: string) =>
    `This Workflow was not requested${
      interval ? ` ${interval}` : ''
    } in advance`,
  workflowDateTimeBlocked: 'Workflow date-time is blocked',
  pleaseMoveThisWorkflow: (days: number) =>
    `Please move this Workflow. Requests must be ${days} working ` +
    `day${ending(days)} ` +
    `in Advance from the date and time you are requesting and on a work date-time`,
  requestsMustBeInAdvance: (days: number) =>
    `Requests must be ${days} working ` +
    `day${ending(days)} ` +
    `in Advance from the date and time you are requesting`,
  hide: 'Hide',
  warnUsers: 'Warn Users',
  blockUsers: 'Block Users',
  duplicateCurrentWorkflow: 'Duplicate current Workflow',
  mapNotifications: 'Map Notifications',
  olderWorkflowCantBeDuplicate:
    'The older version of the Workflow cannot be duplicated.',
  invalidNumberFormat: 'Invalid number format',

  restrooms: 'Restrooms',
  canteen: 'Canteen',
  toilet: 'Toilet',
  breakText: 'Break',
  dumpster: 'Dumpster',
  electricalRoom: 'Electrical room',
  access: 'Access',
  washArea: 'Wash area',
  medicalArea: 'Medical area',
  musterPoint: 'Muster point',
  parking: 'Parking',
  smokingArea: 'Smoking area',
  covidCheckpoint: 'Covid checkpoint',
  breakArea: 'Break area',
  walkway: 'Walkway',
  trailer: 'Trailer',
  informationPoints: 'Information Points',
  glyphNames: {
    truck: 'Truck',
    'concrete-mixer-truck': 'Concrete Mixer Truck',
    'equipment-storage': 'Equipment Storage',
    'material-storage-boxes': 'Material Storage: Boxes',
    'material-storage-wood': 'Material Storage: Wood',
    'material-storage-steel': 'Material Storage: Steel',
    'material-storage-pipes': 'Material Storage: Pipes',
    'material-lifting-steel': 'Lifting Steel',
    scaffolding: 'Scaffolding',
    'swing-stage': 'Swing Stage',
    'personnel-lift': 'Personnel Lift',
    info: 'Info',
    'site-map': 'Site Map',
    'log-out': 'Exit',
    'no-entry': 'No Entry',
    stop: 'Stop',
    'no-pedestrians': 'No Pedestrians',
    'no-access': 'No Access',
    'security-checkpoint-window': 'Security Checkpoint',
    'visitor-check': 'Visitor Check-in',
    'high-visibility-vest': 'Hi-Vis Vest',
    'safety-helmet': 'Safety Helmet',
    'safety-gloves': 'Safety Gloves',
    'safety-toe-boots': 'Safety Toe Boots',
    'ppe-not-required': 'PPE Not Required',
    'no-vest': 'No Vest',
    'no-helmet': 'No Helmet',
    'no-gloves': 'No Gloves',
    'no-boots': 'No Boots',
    'cleanroom-gowning': 'Cleanroom / Gowning',
    'icra-zone': 'ICRA Zone',
    'negative-pressure': 'Negative Pressure',
    'esd-zone': 'ESD Zone',
    'no-photography': 'No Photography',
    'no-drones': 'No Drones',
    'fall-hazard': 'Fall Hazard',
    'silica-dust': 'Silica / Dust',
    noise: 'Noise',
    'confined-space': 'Confined Space',
    'energized-equipment': 'Energized Equipment',
    'bike-parking': 'Bike Parking',
    'utility-cart': 'Utility Cart',
    'loading-zone': 'Loading Zone',
    'fire-lane': 'Fire Lane',
    'flagger-ahead': 'Flagger Ahead',
    'one-way': 'One Way',
    'speed-limit': 'Speed Limit',
    trash: 'Trash',
    'admin-office': 'Admin Office',
    'hand-wash-station': 'Hand-Wash Station',
    'spill-kit': 'Spill Kit',
    'erosion-control': 'Erosion Control',
    'storm-drain-inlet-protection': 'Storm Drain Inlet Protection',
    'concrete-washout': 'Concrete Washout',
    'wheel-wash': 'Wheel Wash',
    'restroom-men': "Men's Restroom",
    'restroom-women': "Women's Restroom",
    sofa: 'Lounge',
    hydration: 'Hydration',
    'heart-pulse': 'AED',
    eyewash: 'Eyewash',
    'fire-extinguisher': 'Fire Extinguisher',
    'fire-hydrant': 'Fire Hydrant',
    'fire-alarm-button': 'Fire Alarm Button',
    'standpipe-hose': 'Standpipe / Hose',
    flame: 'Hot Work',
    excavator: 'Excavator',
    'overhead-load': 'Overhead Load',
    'brand-oes': 'OES Equipment',
    'brand-sunbelt': 'Sunbelt Rentals',
    'brand-united-rentals': 'United Rentals',
    'brand-equipmentshare': 'EquipmentShare',
    'brand-feco': 'First Equipment Company',
    'brand-herc-rentals': 'Herc Rentals',
  },
  informationPointTypes: {
    general_information_point: 'General information point',
    access: 'Access',
    exit: 'Exit',
    restricted_area: 'Restricted area',
    security_checkpoint: 'Security checkpoint',
    ppe_zone: 'PPE zone',
    controlled_environment: 'Controlled environment',
    site_rules: 'Site rules',
    hazard_zone: 'Hazard zone',
    confined_space: 'Confined space',
    energized_equipment: 'Energized equipment',
    walkway: 'Walkway',
    parking: 'Parking',
    loading: 'Loading',
    traffic_control: 'Traffic control',
    waste: 'Waste',
    stormwater_erosion_control: 'Stormwater & Erosion Control',
    elevated_access: 'Elevated access',
    site_office: 'Site office',
    trailer: 'Trailer',
    electrical_room: 'Electrical room',
    utilities: 'Utilities',
    restrooms: 'Restrooms',
    break_area: 'Break area',
    canteen: 'Canteen',
    hydration_station: 'Hydration station',
    handwash_station: 'Handwash station',
    smoking_area: 'Smoking area',
    medical_area: 'Medical area',
    fire_protection: 'Fire protection',
    muster_point: 'Muster point',
  },
  informationPointGroups: {
    general: 'General',
    entry_restrictions: 'Entry & Restrictions',
    hazards: 'Hazards',
    site_logistics: 'Site Logistics',
    offices_utilities: 'Offices & Utilities',
    restrooms_breaks: 'Restrooms & Breaks',
    emergency: 'Emergency',
  },
  informationPointPicker: {
    title: 'Place a point',
    byType: 'By Type',
    byIcon: 'By Icon',
    xTypes: (x: number) => `${x} Type${ending(x)}`,
  },
  stairway: 'Stairway',
  elevator: 'Elevator',
  shaft: 'Shaft',
  crane: 'Crane',
  hoist: 'Hoist',
  aerialLift: 'Aerial Lift',
  forkLift: 'Fork Lift',
  gradall: 'Gradall',
  workflowTitle: 'Workflow Title',
  workflowStatus: 'Workflow Status',

  openWorkflow: 'Open Workflow',
  attachmentPhotoAdded: 'Attachment/Photo has been added',
  attachment: 'Attachment',

  ended: 'Ended',

  shareBasedOnSettingsIn: 'Share based on Settings in',
  ifSelected: 'If selected',
  onlyWorkflowsSatisfyingModuleRulesWillBeShared: (module: string) =>
    `, only Workflows satisfying the rules in the "${module}" module will be shared.`,
  onlyDeliveriesSatisfyingModuleRulesWillBeShared: (module: string) =>
    `, only Deliveries satisfying the rules in the "${module}" module will be shared.`,
  from: 'From',
  to: 'To',
  _to: 'to',
  shareAllForms: 'Share All Workflows',
  shareAllDeliveries: 'Share All Deliveries',
  formSharing: 'Workflow sharing',
  deliverySharing: 'Delivery sharing',
  chooseDate: 'Choose date',

  autoFinishWorkflowOnFinishDate:
    'Automatically finish Workflow on finish date',
  executeAutomaticFinish: 'Execute automatic finish',
  atAnyStep: 'At any step',
  onlyAt: 'Only at',
  onlyAtOrAfter: 'Only at or after',
  youHaveUnsavedChanges: 'You have unsaved changes.',
  unsavedChangesDiscarded: 'All unsaved changes will be discarded.',
  stay: 'Stay',
  continue: 'Continue',
  workflowMessageAdded: 'Workflow message added',
  createdBy: 'Created By',
  categoryCreatedByStruxHub: 'Category created by StruxHub',
  linkForProjectCopiedToClipboard: (projectCode: string) =>
    `Link for project ${projectCode} Copied to clipboard!`,
  invitedSuccessfully:
    'Invited successfully! Now this project can import your events!',
  unsubscriberSuccessfully: 'Unsubscribed successfully!',
  provideLink: 'Provide link!',
  youCannotSubscribeToYourOwnProject:
    'You cannot subscribe to your own project!',
  fetchingEventsOnBackground: 'Fetching events in background...',
  importedSuccessfully: 'Imported successfully!',
  thisProjectIsAlreadyInvited: 'This project is already Invited!',
  confirmDeleteSubscriber: (subscriberCode: string) =>
    `Are you sure you want to delete project ${subscriberCode}?`,
  sharedFromWithName: companyName => `Shared from ${companyName}`,
  sharedFrom: `Shared from`,
  enableRestrictions: 'Enable restrictions by invite codes',
  notProvided: 'Not provided',
  shared: 'Shared',
  generateAndCopyLinkForStxProjects:
    'Generate and copy link for StruxHub projects:',
  inviteStruxProjectsByItsCode: 'Invite other StruxHub project by its code',
  invitedProjectCodes: 'Invited Project Codes',
  youDontHaveAnyInviteesYet: 'You do not have any invitees yet.',
  subscribeToOtherStruxHubProjects: 'Subscribe to other StruxHub projects',
  subscribeToExternalProjects: 'Subscribe to external projects',
  pastUrlFromOtherProject: 'Paste url from other project',
  yourSubscriptions: 'Your Subscriptions',
  youDoNotHaveAccessToProject: 'You do not have access to project',
  youDontHaveAnySubscriptionsYet: 'You do not have any subscriptions yet.',
  visibility: 'Visibility',
  inviteLink: 'Invite Link',
  sharedProjectName: 'Shared Project Name',
  dontShare: "Don't share",
  disableAccess: 'Disable access',
  shareAllDeliveriesWithinSpecificDate:
    'Share all Deliveries within specific date',
  shareAllFormsWithinSpecificDate: 'Share all Workflows within specific date',
  areYouSureYouWantToDisableAccess: 'Are you sure you want to disable access?',
  deleteSubscription: 'Delete subscription',
  allExternalProjectsWillLosePermission:
    'All external projects that use this link as a subscription to your project will lose the permission to review your Workflows.',
  areYouSureYouWantToDeleteSubscription:
    'Are you sure you want to delete the subscription to the project?',
  inThisCaseYouWillNotBeAbleToSeeAnyWorkflowsFromThisProject:
    'In this case, you will not be able to see any Workflows from this project',
  sharingSettings: 'Sharing settings',
  shareIcalCalendarLink: 'Share ical calendar link',
  shareToExternalProject: 'Share to external project',
  generateShareableLink: `Generate a shareable link to let external Struxhub project
    owners view your Workflows based on your sharing settings.`,
  generateShareableCalendarLink: `Generate a shareable link to let external calendars
    (Apple Calendar, Google Calendar, etc.) view your Workflows based on your sharing settings.`,
  subscribeShareableLink: `Paste a shareable link to get data from external Struxhub project.`,
  ifTheProjectIsEnabled:
    "If the toggle is Enabled, the external project will be able to view your shared Workflows based on the sharing settings you've configured.",
  ifTheProjectIsDisabled:
    'If the toggle is Disabled, the external project will no longer be able to see any shared Workflows.',

  youSubscribedToNExternalProjects: (n: number) =>
    `You subscribed to ${n} external project${ending(n)}`,
  import: 'Import',
  preparingUpload: 'Preparing upload...',
  uploadEquipmentFile:
    'Upload your Equipment file in Excel (.xlsx, .xlsm) or CSV (.csv) format.',
  importComplete: 'Import Complete',
  created: 'Created:',
  skippedValues: 'Some Properties were left empty',
  skippedValuesDescription:
    "All rows were created, but the Properties below couldn't be matched to existing records and were saved as blank.",
  xPropertiesSkipped: (n: number) => `${xProperties(n)} skipped`,
  mapColumnsToProperties: 'Map columns to properties',
  id: 'ID',
  selectIdColumn: 'Select ID column',
  selectTitleColumn: 'Select title column',
  selectColumn: 'Select column',
  template: 'Template',
  selectTemplate: 'Select template',
  loadingProperties: 'Loading properties...',
  equipmentProperties: (n: number) => `Equipment Properties (${n})`,
  materialProperties: (n: number) => `Material Properties (${n})`,
  locationProperties: (n: number) => `Location Properties (${n})`,
  inviteCodeCantBeEmpty: 'Invite code can`t be empty',
  goToTheProject: 'Go to the project',
  locationsFrom: 'Locations From',
  locationsTo: 'Locations To',
  goToLocation: (locationName: string) => `Go to ${locationName}`,
  callTo: (name: string) => `Call to ${name}`,
  mailTo: (name: string) => `Mail to ${name}`,
  goToTheBuilding: 'Go to the building',
  selectWorkflowType: 'Select Workflow Type',
  selectWorkflowTypeFirst: 'Please select workflow type first',
  createNew: 'Create new',
  defaultDuration: 'Default duration',
  maximumDuration: 'Maximum duration',
  maxDurationError: (maxDuration: string) =>
    `The duration between 'start' and 'end' exceeds the maximum allowed limit of ${maxDuration}`,
  nonWorkDayOrDays: 'Non-work day/s',
  nonWorkHours: 'Non-work hours',
  failedToSave: 'Failed to save',
  failedToSaveChangesTryAgain: 'Failed to save changes. Please try again.',
  failedToDeleteFormsTryAgain: 'Failed to delete forms. Please try again.',

  filterType: 'Filter type',
  workWeek: 'Work week',
  maximumDurationTime: 'Maximum duration time',
  defaultDurationTime: 'Default duration time',
  allowRepeatingWorkflow: 'Allow repeating Workflow',
  maximumRepeatingDuration: 'Maximum repeating duration',
  iCalSharingSettings: 'iCal Sharing Settings',
  nothing: 'nothing',
  if: 'If',
  thenShow: 'then show',
  showAllSettings: 'Show all settings',
  hideAllSettings: 'Hide all settings',
  addDependentField: 'Add dependent field',
  addField: 'Add field',
  alreadyAdded: 'Already added',
  item: 'Item',
  addAssociatedField: 'Add Item Field',
  associatedFields: 'Item Fields',
  xAssociatedFields: (x: number) => `${x} Item Field${ending(x)} added`,
  addTableColumn: 'Add table column',
  tableColumns: 'Table columns',
  xTableColumns: (x: number) => `${x} Table column${ending(x)}`,
  newRule: 'New rule',
  inactive: 'Inactive',
  repeatWorkflow: 'Repeat Workflow',
  thisWorkflow: 'This Workflow',
  showOnSourceMap: 'Show on source map',
  thisWorkflowIsPinnedOnADifferentMapView:
    'This Workflow is pinned on a different map view',
  thisWorkflowIsHiddenOnTheMap: 'This Workflow is hidden on the map',
  switchedToMapViewX: (name: string) => `Switched to map view ${name}`,
  thisWorkflowIsNotShownOnThisMapView:
    'This Workflow is not shown on this map view',
  thisWorkflowHasNoPinOnAnyMapView: 'This Workflow has no pin on any map view',
  thisAndFutureWorkflows: 'This and future Workflows',
  usedInMaterialsTableCalculations: 'Used in Materials table calculations',
  usedToSpecifySourceLocationInMaterialsTable:
    'Used to specify the source location in the Materials table',

  note: 'Note',
  dependentFieldsTooltip: {
    title: 'Dependent fields',
    description: `Create rules to dynamically show other fields
     based on the value selected in this field.`,
  },
  dynamicFiltersTooltip: {
    title: 'Dynamic filters',
    description:
      'Create filters to dynamically show options based on field values and conditions.',
  },
  selection: 'Selection',
  selectionSettings: 'Selection Settings',
  conditionalFilters: 'Conditional Filters',
  xConditionalFilters: (x: number) => `${x} Conditional filter${ending(x)}`,
  addConditionalFilter: 'Add Conditional Filter',
  conditionalFields: 'Conditional Fields',
  xConditionalFields: (x: number) => `${x} Conditional field${ending(x)}`,
  addConditionalField: 'Add Conditional Field',
  selectionTooltip:
    'Set how users add items \u2014 create, select, or both. Define the selection menu\u2019s default sort, grouping, and filters.',
  itemFieldsTooltip:
    'Capture additional details per item. Each item becomes a row; these Fields are its columns.',
  conditionalFiltersTooltip:
    'Filter the list of available items based on values in other fields on this form.',
  conditionalFieldsTooltip:
    'Show additional fields only when a specific item is selected.',
  cardView: 'Card View',
  lastUpdatedBy: 'Last Updated By',
  deleteFormsConfirm: (x: number) =>
    `Are you sure you want to delete ${x} form${ending(x)}?`,
  removeSelected: 'Remove selected',
  addOption: 'Add Option',
  optionName: 'Option name',
  removed: 'removed',
  editProperty: 'Edit Property',
  createNewProperty: 'Create New Property',
  deleteProperty: 'Delete property',
  archiveProperty: 'Archive property',
  unarchiveProperty: 'Unarchive property',
  typeToConfirm: 'Type to confirm',
  propertyUsedByXResources: (x: number): string =>
    `This Property is used by ${x} Resource${ending(x)}.`,
  deletePropertyConfirmation: (count: number): string =>
    count > 0
      ? `Deleting it will permanently remove this Property and all its data from those Resources. Type the number of associated Resources or "Delete" to confirm.`
      : 'This Property is not used by any Resources. It will be permanently deleted.',
  archivePropertyConfirmation: (count: number): string =>
    count > 0
      ? `Archiving will remove this Property from active use on those Resources. This can be undone. Type the number of associated Resources or "Archive" to confirm.`
      : 'This Property is not used by any Resources. It will be archived.',
  propertyDetails: 'Property Details',
  propertyPreview: 'Preview',
  propertyPreviewUnavailable: 'Preview unavailable',
  configurePropertyBeforePreview:
    'Configure the property details before previewing it.',
  managedRelationPreviewHelp:
    'This relation can be managed after the resource is created.',
  calculatedPropertyPreviewHelp:
    "This value is calculated automatically and can't be edited.",
  unsupportedPropertyPreviewHelp: "This property type can't be previewed.",
  backToDetails: 'Back to Details',
  textSettings: 'Text settings',
  enableMultilineInput: 'Enable multiline input',
  internalName: 'Internal Name',
  internalNameCannotBeChanged: "Internal Name can't be changed",
  dropdownOptionsSettings: 'Dropdown Options Settings',
  newOption: 'New Option',
  xOptionsAdded: (x: number) => `${x} option${ending(x)} added`,
  allowUsersToCreateNewOptions: 'Allow user to create new options',
  allowUsersToSelectExistingWorkflows:
    'Allow Users to select existing workflows',
  allowUsersToCreateNewWorkflow: 'Allow Users to create new workflow',
  selectExisting: 'Select Existing',
  noResultsFound: 'No results found',
  allowUsersToSelectExisting: (label: string) =>
    `Allow Users to select existing ${label}`,
  allowUsersToCreateNew: (label: string) =>
    `Allow Users to create new ${label}`,
  allowToSelect: 'Allow to select',
  activityFieldRestrictedTooltip:
    'Activity Field is restricted to Activity Workflow Types only.',
  cantSeeResource: (resource: string) => `Can't see the ${resource}?`,
  openNonWorkflowResources: (resource: string) =>
    `Open Non-Workflow Resource ${resource}`,
  alsoManageFromControls:
    'Additionally, you can accomplish these changes from the',
  resourceControlsPage: (resource: string) => `${resource} Controls page`,
  enablingResourcesHint: (resource: string) =>
    `These ${resource} are not Workflow Resources yet. Saving turns their “WR” toggle on and allows them for this field, and for every field set to “Any ${resource}”.`,
  anyWorkflowTypes: 'Any workflow types',
  allowNoneSelection: "Allow 'None' selection",
  saveChanges: 'Save changes',
  noIconsFoundByYourRequest: 'No icons found by your request',
  generalWorkflow: 'General Workflow',
  ganttChart: 'Gantt Chart',
  activity: 'Activity',
  automation: 'Automation',
  craneBooking: 'Crane Booking',
  deliveryBooking: 'Delivery Booking',
  environmental: 'Environmental',
  airMitigation: 'Air Mitigation',
  eco: 'ECO',
  authorizedVehicleAccess: 'Authorized Vehicle Access',
  fireExtinguisherPermit: 'Fire Extinguisher Permit',
  heavyEquipmentUsageLog: 'Heavy Equipment Usage Log',
  ladderPermit: 'Ladder Permit',
  surfacePenetrationPermit: 'Surface Penetration Permit',
  interiorConcretePenetrationPermit: 'Interior/Concrete Penetration Permit',
  aerialLiftInspection: 'Aerial Lift Inspection',
  forkliftRoomWorkPermit: 'Forklift Room Work Permit',
  materialTransfer: 'Material Transfer',
  procurementTracking: 'Procurement Tracking',
  quality: 'Quality',
  qualityControlInspection: 'Quality Control Inspection',
  report: 'Report',
  timesheet: 'Timesheet',
  safety: 'Safety',
  energizedRoomWorkPermit: 'Energized Room Work Permit',
  hotWorkPermit: 'Hot Work Permit',
  jobHazardAnalysis: 'Job Hazard Analysis',
  siteCommunication: 'Site Communication',
  closureRequest: 'Closure Request',
  siteAnnouncement: 'Site Announcement',
  comingSoon: 'Coming soon',
  incorrectDateOrTime: 'Incorrect date or time',
  invalidSettings: 'Invalid settings',
  imageDimensionsCanNotBeGreaterThan: (
    maxWidth?: number,
    maxHeight?: number,
  ) => {
    if (maxWidth && maxHeight) {
      return `Image dimensions can not be greater than ${maxWidth}x${maxHeight}`
    } else if (maxWidth) {
      return `Image width can not be greater than ${maxWidth}px`
    } else if (maxHeight) {
      return `Image height can not be greater than ${maxHeight}px`
    }
    return 'Invalid image dimensions'
  },
  fileSizeCanNotBeGreaterThan: (maxFileSize: string) =>
    `File size can not be greater than ${maxFileSize}`,
  controlledAccessZone: 'Controlled Access Zone',
  guardrailRemovalPermit: 'Guardrail Removal Permit',
  roofAccessPermit: 'Roof Access Permit',
  smartFormImport: 'Smart Workflow import',
  smartFormImportDescription:
    'Upload a PDF or image file and AI will detect and map fields automatically.',
  dragAndDropFile: 'Drag and drop file',
  or: 'or',
  chooseFile: 'Choose file',
  fileSupportText: 'Supports PDF, JPG, PNG files',
  importFile: 'Import file',
  workingOnIt: '🛠️ We`re working on it.',
  detectingHeaders: '🔎 Detecting headers…',
  findingFields: '📑 Finding fields…',
  scanningCheckboxes: '☑️ Scanning checkboxes…',
  organizingWorkflowSteps: '🗂️ Organizing Workflow steps…',
  aligningWithStruxHubSchema: '🧩 Aligning with StruxHub schema…',
  doubleCheckingFieldTypes: '🛡️ Double-checking field types…',
  tidyingUpYourForm:
    '🧹 Tidying up your form (neater than a jobsite trailer desk).',
  sharpeningDigitalPencils: '✏️ Sharpening digital pencils…',
  sortingThingsIntoTheRightBins: '🏗️ Sorting things into the right bins…',
  moveFieldsIntoDifferentWorkflowStepsAfterImport:
    '💡 You can move fields into different workflow steps after import.',
  addWorkflowStepsForApprovalsInspectionsAndStartTimes:
    '💡 You can add workflow steps for approvals, inspections, and start times.',
  routeWorkflowStepsToBallInCourtUsersWithConditionalLogic:
    '💡 You can route workflow steps to ball-in-court users with conditional logic.',
  notifyWorkflowMembersAfterSpecificSteps:
    '💡 You can notify workflow members after specific steps.',
  triggerResourceClosureAfterSpecificWorkflowStep:
    '💡 You can trigger a resource closure after a specific workflow step.',
  linkWorkflowToAnotherWorkflow:
    '💡 You can link a workflow to another workflow via the ‘Linked Workflow’ field.',
  createTemplatesFromImportedFormsToReuseAcrossProjects:
    '💡 You can create templates from imported forms to reuse across projects.',
  assignResponsibilityForWorkflowStepsToSpecificCompanies:
    '💡 You can assign responsibility for workflow steps to specific companies.',
  rollUpFormDataIntoExportableReports:
    '💡 You can roll up form data into exportable reports.',
  attachDocumentsAndPhotosToAnyWorkflow:
    '💡 You can attach documents and photos to any workflow.',
  safetyFirstNoHardHatRequiredWhileWeProcessYourForm:
    '🦺 Safety first: no hard hat required while we process your form.',
  ourScannersLiftFormsFasterThanALifterLiftsSteel:
    '🚜 Our scanners lift forms faster than a crane lifts steel.',
  everSeenPaperworkMoveThisFastOnAJobsiteNeitherHaveWe:
    '🏃 Ever seen paperwork move this fast on a jobsite? Neither have we.',
  importingFormsNoRFIsRequired: '📨 Importing forms — no RFIs required.',
  dontWorryThisWontTakeAsLongAsAConcretePour:
    '🏗️ Don’t worry, this won’t take as long as a concrete pour.',
  processingFormsFasterThanFindingTheRightWrenchInYourToolbox:
    '🔧 Processing forms faster than finding the right wrench in your toolbox.',
  proTipThisIsTheOnlyTimePaperworkWillDoTheWorkForYou:
    '🤓 Pro tip: this is the only time paperwork will do the work for you.',
  scanningWithoutThePaperCuts: '✋ Scanning… without the paper cuts.',
  ourImportToolDoesntNeedAForemanYellingAtIt:
    '📣 Our import tool doesn’t need a foreman yelling at it.',
  almostThereUnlikeThatElevatorInstallation:
    '🚧 Almost there — unlike that elevator installation.',
  wrappingThingsUp: '🎁 Wrapping things up…',
  puttingFinishingTouchesOnYourForm:
    '✅ Putting the finishing touches on your form…',
  justOneMoreCheck: '📦 Just one more check…',
  workflowWasSuccessfullyImported: (workflowName: string) =>
    `🎉 ${workflowName} was successfully imported!`,
  somethingWentWrongWhileImportingFile:
    'Something went wrong while importing your file',
  importInProgress: 'An import is already in progress',
  viewWorkflow: 'View workflow',
  enableMaximumDuration: 'Enable maximum duration',
  enableDefaultDuration: 'Enable default duration',
  minute_plural: (x: number) => `Minute${ending(x)}`,
  hour_plural: (x: number) => `Hour${ending(x)}`,
  day_plural: (x: number) => `Day${ending(x)}`,
  week_plural: (x: number) => `Week${ending(x)}`,
  month_plural: (x: number) => `Month${ending(x)}`,
  durationCannotBeGreaterThan: `Duration cannot be greater than`,
  saveAsDraft: 'Save as draft',
  draftSavedSuccessfully:
    'Draft saved successfully. All changes have been saved.',
  createdXofYWorkflows: (created: number, total: number) =>
    `Created ${created} of ${total} workflows`,
  skippedWorkflows: 'Skipped',
  unknownLocation: 'Unknown location',
  blockedByClosure: 'blocked by closure at',
  blockedByProjectClosed: 'blocked by project closed interval',
  blockedByNonWorkingDay: 'on non-working days',
  blockedByOperatingHours: 'outside operating hours of',
  reservedForOtherCompanies: 'Reserved for other companies',
  discardedUnsavedDrawing:
    'Your unsaved drawing was discarded because the map closed',
  failedToCreateWorkflow: 'Could not create the Workflow. Please try again.',
  failedToCreateRecurringWorkflows:
    'Failed to create recurring workflows. Please try again or contact support',
  unavailableDraftWorkflowsSelected:
    'Unavailable: One or more selected Workflows are in draft status',
  draft_noun: 'Draft',
  linkedWorkflows: 'Linked Workflows',
  addLinkedWorkflow: 'Add linked workflow',
  dateTime: 'Date Time',
  file: 'File',
  allWorkflow_s: 'All workflow(s)',
  appliedTo: (count: number) => `Applied to (${count})`,
  closuresConflictTitle: 'Closures Conflict',
  closuresConflictMessage:
    'Selected locations have closures conflicting with the workflow dates. The conflicting locations have been automatically removed. Please review your form and adjust if needed.',
  closuresConflictRecurringMessage:
    'Some recurring occurrences fall on closed dates. You can continue creating the recurring workflow or review and adjust your selections.',
  selectWorkflowTypes: 'Select Workflow Types',

  resourcePropertyText: 'Text',
  resourcePropertyNumber: 'Number',
  resourcePropertySelect: 'Dropdown',
  resourcePropertyHierarchy: 'Hierarchy',
  resourcePropertyCheckbox: 'Checkbox',
  resourcePropertyDatetime: 'Datetime',
  resourcePropertyRelation: 'Relation',
  resourcePropertyLocation: 'Location',
  resourcePropertyRollup: 'Rollup Field',
  resourcePropertyComputed: 'Computed Field',
  resourcePropertyUser: 'User',
  resourcePropertyCompany: 'Company',
  resourcePropertyFile: 'File',
  uploadFiles: 'Upload files',
  uploadFile: 'Upload file',
  attachFile: 'Attach file',
  attachFiles: 'Attach files',
  camera: 'Camera',
  maxFilesReached: 'Maximum number of files reached',
  fileTooLarge: (maxMB: number) => `File is too large (max ${maxMB} MB)`,
  sitemapsTooLargeForMobile:
    'Some sitemaps are too large to display in the app. Open StruxHub in your browser to view them.',
  fileTypeNotAllowed: 'File type is not allowed',
  pdfPageImagesCouldNotBeRendered:
    'Some images on this PDF page could not be displayed and are missing from the imported map.',
  associatedProperties: 'Associated Properties',
  appliedToColumn: 'Applied to',
  xResourceObjects: (count: number, resourceTypeId: string) => {
    const noun =
      (
        {
          offloading_equipment: 'Equipment',
          material: 'Material',
          sensor: 'Sensor',
        } as Record<string, string>
      )[resourceTypeId] ?? ''
    return `${count} ${noun} Object${ending(count)}`
  },
  deleteSelected: 'Delete selected',
  selectASingleWorkflowToDuplicate: 'Select a single workflow to duplicate',
  subscribeSelected: 'Subscribe selected',
  assignSelected: 'Assign selected',
  doYouWantToDeleteSelectedWorkflows:
    'Do you want to delete selected workflows?',
  allDataRelatedToSelectedWorkflowsWillBeDeleted:
    'All data related to the selected workflows will be permanently deleted and cannot be recovered.',
  statuses: 'statuses',
  updateSelectedWorkflowsStatusTo: 'Update selected Workflows status to:',
  youAreGoingToUpdate: 'You are going to update',
  _for: 'for',
  updateXWorkflows: (x: number) => `Update ${x} Workflow${ending(x)}`,
  subscribeUsers: 'Subscribe Users',
  subscribed: 'Subscribed',
  noUsersHaveSubscribedYet: 'No users have subscribed yet',
  thisUserHasSubscribedToOnlySomeOfTheWorkflowsYouSelected:
    'This user has subscribed to only some of the workflows you selected.',
  subscribedTo: 'Subscribed to:',
  notSubscribedTo: 'Not subscribed to:',
  signature: 'Signature',
  signaturePreview: 'Signature preview',
  draw: 'Draw',
  typeHere: 'Type here',
  addTextHere: 'Add text here',
  signHere: 'Sign here',
  editStyle: 'Edit style',
  clickToAddSignature: 'Click to add signature',
  newUserSignupWorkflow: 'New User Signup Workflow',
  whiteListDomains: 'White List Domains',
  whiteListDomainsTooltip:
    'When Domain added to White List Domains, it allows all users with verified emails addresses from these domains to join your StruxHub project without being specifically invited.',
  whiteListDomainsTooltipNote:
    'NOTE: Do not White List generic email domains like @gmail.com, @outlook.com, @iCloud.com. Doing this would allow unverified users access to your project.',
  usersCanCreateAccountByLink: 'Users can create an account by this link:',
  orSnappingThisQRCode: 'Or snapping this QR Code:',
  print: 'Print',
  noPermissionToCreateWorkflowType:
    'Sorry! You do not have the necessary permission to create this Workflow Type',
  approvalConfiguration: 'Approval Configuration',
  sendBicApprovalTo: 'Send BIC Approval To',
  bicApprovalTooltip:
    'These users receive a ball-in-court approval notification when this rule applies.',
  allProjectCoordinators: 'All Project Coordinators',
  selectApprovers: 'Select Approvers',
  addFilter: 'Add Filter',
  onlyWhen: 'Only when:',
  selectCompanyType: 'Select company type',
  deleteRule: 'Delete rule',
  deleteView: 'Delete view',
  deleteResource: 'Delete resource',
  deleteTag: 'Delete tag',
  deleteItem: 'Delete item',
  deleteField: 'Delete field',
  deleteFieldQuestion: 'Are you sure you want to delete the field?',
  deleteViewQuestion: (name: string) =>
    `Are you sure you want to delete the ${name} view?`,
  removeUser: 'Remove user',
  xAllowedFormsHaveBeenDeleted: (x: number) =>
    `${x} allowed Form${ending(x)} ${ending(x, 'has', 'have')} been deleted`,
  confirmResourceControlChangesTitle: 'Update Resource Controls?',
  resourceControlChangeAffectsWorkflows: (count: number) =>
    `This Resource is still used by ${count} Workflow Type${ending(
      count,
    )}. This will remove it from each:`,
  cascadeConditionalFieldsRemoved: (count: number) =>
    `${count} conditional field${ending(count)} removed`,
  cascadeConditionalFiltersRemoved: (count: number) =>
    `${count} conditional filter${ending(count)} removed`,
  cascadeNoLongerSelectableInFields: (count: number) =>
    `No longer selectable in ${count} field${ending(count)}`,
  cascadeNoLongerSelectableInConditionalFilters: (count: number) =>
    `No longer selectable in ${count} conditional filter${ending(count)}`,
  cascadeNoLongerSelectableInConditionalFields: (count: number) =>
    `No longer selectable in ${count} conditional field${ending(count)}`,
  cascadeRemovedFromExclusion: (count: number) =>
    `Removed from ${count} exclusion list${ending(count)}`,
  cascadeOtherReferencesRemoved: 'Other references removed',
  couldNotCheckAffectedWorkflows:
    'Could not check which workflows are affected. Please try again.',
  someResourcesCouldNotBeDisabled: (count: number) =>
    `Could not disable ${count} resource${ending(count)}. Please try again.`,
  someResourcesCouldNotBeUpdated: (count: number) =>
    `Could not update ${count} resource${ending(count)}. Please try again.`,
  projectLocationDeletionProhibited:
    'Deletion of Project Location is prohibited',
  projectLocationControlEditProhibited: (controlName: string) =>
    `Editing of ${controlName} for Project Location is prohibited`,
  projectLocationBulkEditProhibited:
    'Bulk editing of Project Location is prohibited',
  selectionHasResourcesYouCannotChange:
    'Resources can only be edited or deleted by Admins and their owners (Responsible User or Responsible Company members)',
  outsideHours: 'Outside hours',
  closedStatus: 'Closed',
  tooLong: 'Too long',
  fullyBooked: 'Fully booked',
  reserved: 'Reserved',
  unavailable: 'Unavailable',
  closedOnWeekdays: (weekday: string) => `Closed on ${weekday}s`,
  closedOnX: (day: string) => `Closed on ${day}`,
  onlyOpenXToY: (opens: string, closes: string) =>
    `Only open ${opens} – ${closes}`,
  opensAtX: (time: string) => `Opens at ${time}`,
  closesAtX: (time: string) => `Closes at ${time}`,
  closesAtXReopensAtY: (closes: string, reopens: string) =>
    `Closes at ${closes}, reopens at ${reopens}`,
  hasBreakDuringThisBooking: 'Has a break during this booking',
  xToYEveryDay: (start: string, end: string) => `${start} – ${end} every day`,
  allDayX: (day: string) => `All day ${day}`,
  xMinutesDuration: (x: number) => `${x} minute${ending(x)}`,
  xHoursDuration: (x: number) => `${x} hour${ending(x)}`,
  xDaysDuration: (x: number) => `${x} day${ending(x)}`,
  xWeeksDuration: (x: number) => `${x} week${ending(x)}`,
  xMonthsDuration: (x: number) => `${x} month${ending(x)}`,
  limitIsX: (limit: string) => `Limit is ${limit}`,
  limitIsOneWorkingDay: 'Limit is one working day',
  limitIsOneWorkWeek: 'Limit is one work week',
  thisBookingIsX: (length: string) => `This booking is ${length}.`,
  listWithXMore: (items: string[], moreCount: number) =>
    moreCount
      ? `${items.join(', ')} and ${moreCount} more`
      : items.join(' and '),
  aBooking: 'a booking',
  takenByX: (bookings: string) => `Taken by ${bookings}`,
  takenByXAtY: (booking: string, time: string) =>
    `Taken by ${booking}, ${time}`,
  bookingWithTime: (booking: string, time: string) => `${booking}: ${time}`,
  noRoomAtThisTime: 'No room at this time',
  takesXBookingsAtATime: (x: number) =>
    `Takes ${x} booking${ending(x)} at a time.`,
  fullyBookedOnAllXRepeats: (total: number) =>
    `Fully booked on all ${total} repeats.`,
  fullyBookedOnXOfYRepeats: (failing: number, total: number, first: string) =>
    `Fully booked on ${failing} of ${total} repeats, first on ${first}.`,
  forX: (companies: string) => `For ${companies}`,
  forOtherCompanies: 'For other companies',
  cantBeBooked: "Can't be booked",
  affectsAllXRepeats: (total: number) => `Affects all ${total} repeats.`,
  affectsXOfYRepeats: (failing: number, total: number, first: string) =>
    `Affects ${failing} of ${total} repeats, first on ${first}.`,
  projectHoursHeading: 'Project hours:',
  usesProjectHoursHeading: 'Uses Project hours:',
  hoursHeading: 'Hours:',
  noneSet: 'none set',
  notRequestedXAhead: (deadline: string) => `Not requested ${deadline} ahead`,
  notRequestedInAdvance: 'Not requested in advance',
  outsideWorkingHours: 'Outside working hours',
  youCanStillApprove:
    'You can still Approve. Make sure the site can support it.',
  youCanStillSubmit:
    'You can still Submit. Your Approver will see these notes.',
  approveAnyway: 'Approve anyway',
  submitAnyway: 'Submit anyway',
  xMore: (x: number) => `${x} more`,
}

export default EnglishTree
